/* eslint-disable */
import { useState, useEffect, useRef, useMemo } from "react";
import { supabase } from "./supabase";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

if (typeof window !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version || "3.11.174"}/build/pdf.worker.min.js`;
}

export const extractSalaryFromPdfFile = async (file) => {
  try {
    const parsePromise = (async () => {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
      let fullText = "";
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const textContent = await page.getTextContent();
        const items = textContent.items.map(i => ({ str: i.str, x: i.transform[4], y: i.transform[5] }));
        items.sort((a, b) => {
          if (Math.abs(a.y - b.y) > 3) return b.y - a.y;
          return a.x - b.x;
        });
        fullText += " " + items.map(i => i.str).join(" ");
      }

      let net = null;
      let brut = null;

      const mNetPaye = fullText.match(/Net\s+pay[eé]\s+en\s+Euros[^\d]*?(\d[\d\s]*[.,]\d{2})/i) ||
                       fullText.match(/Net\s+pay[eé][^\d]*?(\d[\d\s]*[.,]\d{2})/i) ||
                       fullText.match(/NET\s+A\s+PAYER\s+AVANT\s+IMP[OÔ]T[^\d]*?(\d[\d\s]*[.,]\d{2})/i) ||
                       fullText.match(/NET\s+A\s+PAYER[^\d]*?(\d[\d\s]*[.,]\d{2})/i);

      if (mNetPaye) {
        net = parseFloat(mNetPaye[1].replace(/\s/g, "").replace(",", "."));
      }

      const mBrut = fullText.match(/SALAIRE\s+BRUT[^\d]*?(\d[\d\s]*[.,]\d{2})/i) ||
                    fullText.match(/TOTAL\s+BRUT[^\d]*?(\d[\d\s]*[.,]\d{2})/i);
      if (mBrut) {
        brut = parseFloat(mBrut[1].replace(/\s/g, "").replace(",", "."));
      }

      return { net, brut };
    })();

    const timeoutPromise = new Promise(resolve => setTimeout(() => resolve({ net: null, brut: null }), 4000));
    return await Promise.race([parsePromise, timeoutPromise]);
  } catch (err) {
    console.warn("Extraction automatique PDF :", err);
    return { net: null, brut: null };
  }
};



/* ─────────────────────────────────────
   CONSTANTES
───────────────────────────────────── */
// Admins globaux (Birahim = DG, accès total)
// ══════════════════════════════════════════════════════
// BARÈMES OFFICIELS — CODE GÉNÉRAL DES IMPÔTS SÉNÉGAL
// Source : Simulateur DGI / Simulateur MAJHA Accounting
// ══════════════════════════════════════════════════════

// TRIMF — CGI art.181 — BARÈME OFFICIEL extrait du Simulateur MAJHA/DGI
// Appliqué sur le salaire brut MENSUEL (pas annuel !)
// Transitions exactes vérifiées sur la table de 4951 lignes :
//   brut >= 50 000  → 300 FCFA/pers  (transition à 50 000)
//   brut >= 84 000  → 400 FCFA/pers  (transition à 84 000)
//   brut >= 167 000 → 500 FCFA/pers  (transition à 167 000)
//   brut >= 1 000 000 → 3 000 FCFA/pers (transition à 1 000 000)
// NB : les 4 tranches correspondent aux annuels : <1M, 1-2M, 2-12M, >12M /pers
// ══════════════════════════════════════════════════════════════════
// TABLE IR+TRIMF OFFICIELLE DGI SÉNÉGAL — Source: Simulateur MAJHA/DGI
// 4 951 lignes, brut 50 000 → 5 000 000 FCFA par tranche de 1 000
// Format flat: [trimf, p1, p1.5, p2, p2.5, p3, p3.5, p4, p4.5, p5] × 4951
// Lookup: index = (ceil(brut/1000)×1000 − 50000) / 1000
// Méthode: brut arrondi au millier supérieur → lecture directe (conforme DGI)
// ══════════════════════════════════════════════════════════════════
import { IR_TABLE_DGI } from "./IR_TABLE_DGI";

const IR_TABLE_PARTS = { 1: 1, 1.5: 2, 2: 3, 2.5: 4, 3: 5, 3.5: 6, 4: 7, 4.5: 8, 5: 9 };
function lookupIR(brutMensuel, parts) {
  const brutArr = Math.ceil(brutMensuel / 1000) * 1000;
  let idx = (brutArr - 50000) / 1000;
  if (idx < 0) return { trimf: 300, ir: 0 };
  if (idx >= IR_TABLE_DGI.length / 10) idx = IR_TABLE_DGI.length / 10 - 1;
  const base = idx * 10;
  const pIdx = IR_TABLE_PARTS[parts] || 1;
  return { trimf: IR_TABLE_DGI[base], ir: IR_TABLE_DGI[base + pIdx] };
}

// Catégories Convention Collective Commerce Sénégal — salaires minima
const CONVENTION_COMMERCE = {
  "1ère A": { sal: 70706, taux: 407.93, cadre: false },
  "1ère B": { sal: 74870, taux: 431.95, cadre: false },
  "2ème": { sal: 75361, taux: 434.78, cadre: false },
  "3ème": { sal: 77840, taux: 449.09, cadre: false },
  "4ème": { sal: 82272, taux: 474.65, cadre: false },
  "5ème": { sal: 89244, taux: 514.88, cadre: false },
  "6ème": { sal: 93790, taux: 541.11, cadre: false },
  "7ème A": { sal: 105342, taux: 607.75, cadre: false },
  "7ème B": { sal: 113984, taux: 657.61, cadre: false },
  "8ème A": { sal: 115718, taux: 667.60, cadre: false },
  "8ème B": { sal: 123484, taux: 712.42, cadre: false },
  "8ème C": { sal: 124255, taux: 716.87, cadre: false },
  "9ème A": { sal: 125543, taux: 724.30, cadre: true },
  "9ème B": { sal: 132498, taux: 764.43, cadre: true },
  "10ème A": { sal: 141024, taux: 813.62, cadre: true },
  "10ème B": { sal: 157097, taux: 906.35, cadre: true },
  "10ème C": { sal: 174049, taux: 1004.15, cadre: true },
  "11ème": { sal: 195119, taux: 1125.71, cadre: true },
};
const CATEGORIES_COMMERCE = Object.keys(CONVENTION_COMMERCE);
// Admins globaux et par site par défaut
const DEFAULT_ADMINS_GLOBAL = ["bir", "birahim"];
const DEFAULT_ADMINS_DAKAR = ["bet", "bethy"];
const DEFAULT_ADMINS_FRANCE = ["maimouna", "mai"];
const ADMINS = [...DEFAULT_ADMINS_GLOBAL, ...DEFAULT_ADMINS_DAKAR, ...DEFAULT_ADMINS_FRANCE];

export const getEmployeAdminRole = (emp) => {
  if (!emp) return "user";
  const login = (emp.login || "").toLowerCase().trim();
  const note = emp.solde_conges_note || "";
  
  if (note.includes("ADMIN_ROLE:global") || DEFAULT_ADMINS_GLOBAL.includes(login)) return "global";
  if (note.includes("ADMIN_ROLE:dakar") || DEFAULT_ADMINS_DAKAR.includes(login)) return "dakar";
  if (note.includes("ADMIN_ROLE:france") || DEFAULT_ADMINS_FRANCE.includes(login)) return "france";
  return "user";
};

export const isEmployeAdmin = (emp) => {
  return getEmployeAdminRole(emp) !== "user";
};

export const getAdminSiteScope = (emp) => {
  const role = getEmployeAdminRole(emp);
  if (role === "global") return "all";
  if (role === "dakar") return "Dakar";
  if (role === "france") return "France";
  return "all";
};

export const adminSite = (u) => getAdminSiteScope(u);

export const setEmployeAdminRoleInNote = (existingNote, role) => {
  let cleanNote = (existingNote || "").replace(/\|?\s*ADMIN_ROLE:[a-z]+/gi, "").trim();
  if (role && role !== "user") {
    cleanNote = cleanNote ? `${cleanNote} | ADMIN_ROLE:${role}` : `ADMIN_ROLE:${role}`;
  }
  return cleanNote || null;
};

export const generateUniqueLogin = (prenom, nom, existingEmployees, currentId = null) => {
  const p = (prenom || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  const n = (nom || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  if (!p) return "";
  
  const otherLogins = new Set(
    (existingEmployees || [])
      .filter(e => !currentId || e.id !== currentId)
      .map(e => (e.login || "").toLowerCase().trim())
  );
  
  if (!otherLogins.has(p)) return p;
  if (n && !otherLogins.has(p + n[0])) return p + n[0];
  if (n && !otherLogins.has(p + n)) return p + n;
  if (n && !otherLogins.has(`${p}.${n}`)) return `${p}.${n}`;
  
  let i = 2;
  while (otherLogins.has(`${p}${i}`)) i++;
  return `${p}${i}`;
};

const isEmployeFemme = (emp) => {
  if (!emp) return false;
  const rawGenre = String(emp.genre || emp.sexe || "").trim().toLowerCase();
  if (rawGenre === "femme" || rawGenre === "f" || rawGenre === "female") return true;
  if (rawGenre === "homme" || rawGenre === "h" || rawGenre === "male") return false;
  const prenom = String(emp.prenom || "").toLowerCase();
  return ["habibatou", "habiba", "awa", "mame khady", "ngone", "jocelyne", "deguene", "bitylokho", "bethy", "maimouna", "fatou", "mariama", "aminata", "aissatou", "khady", "coumba", "ndeye", "safiatou"].some(fn => prenom.includes(fn));
};

const formatPoste = (poste, emp) => {
  if (!poste) return isEmployeFemme(emp) ? "Collaboratrice" : "Collaborateur";
  if (!isEmployeFemme(emp)) return poste;
  
  let p = String(poste);
  p = p.replace(/\bcollaborateur\b/gi, "Collaboratrice");
  p = p.replace(/\bcollaborateurs\b/gi, "Collaboratrices");
  p = p.replace(/\bauditeur\b/gi, "Auditrice");
  p = p.replace(/\bauditeurs\b/gi, "Auditrices");
  p = p.replace(/\bdirecteur\b/gi, "Directrice");
  p = p.replace(/\bdirecteurs\b/gi, "Directrices");
  p = p.replace(/\bchef\b/gi, "Cheffe");
  p = p.replace(/\badministrateur\b/gi, "Administratrice");
  p = p.replace(/\badministrateurs\b/gi, "Administratrices");
  p = p.replace(/\bconsultant\b/gi, "Consultante");
  p = p.replace(/\bconsultants\b/gi, "Consultantes");
  p = p.replace(/\bassistant\b/gi, "Assistante");
  p = p.replace(/\bassistants\b/gi, "Assistantes");
  return p;
};

const CABINET_INFO = {
  Dakar: {
    nom: "CABINET MAJHA ACCOUNTING, SARL",
    adresse1: "DAKAR-MERMOZ, Rue MZ-70, VILLA N°13",
    adresse2: "DAKAR, SENEGAL",
    ninea: "NINEA : ______ · RC : SN-DKR-2018-______",
    tel: "Tel : +221 — — — —",
  }
};
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DEPTS = ["Direction", "Comptabilité", "Fiscal", "Audit", "RH", "Juridique", "Communication", "À définir"];
const ABS_TYPES = ["Maladie", "Accident du travail", "Absence injustifiée", "Absence autorisée", "Formation", "Télétravail", "Autre"];
const CON_TYPES = ["Congés payés", "RTT", "Congé sans solde", "Congé exceptionnel", "Congé maladie"];

// Jours fériés Sénégal — fixes chaque année (MM-DD)
const FERIES_FIXES = [
  "01-01", // Nouvel An
  "04-04", // Fête de l'Indépendance
  "05-01", // Fête du Travail
  "08-15", // Assomption
  "11-01", // Toussaint
  "12-25", // Noël
];

// Jours fériés variables 2025-2026
const FERIES_VARIABLES = [
  "2025-03-29", // Korité
  "2025-06-06", // Tabaski
  "2025-06-27", // Journée de l'Unité Africaine
  "2025-07-27", // Tamkharit
  "2025-02-05", // Gamou
  "2026-03-20", // Korité
  "2026-05-27", // Tabaski
  "2026-06-27", // Journée de l'Unité Africaine
  "2026-01-14", // Tamkharit
];
const n = v => parseFloat(v) || 0;

const imageCache = {};
const getOptimizedImageData = (url, maxDim = 200) => {
  if (imageCache[url]) return Promise.resolve(imageCache[url]);
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          let width = img.width || 200;
          let height = img.height || 200;
          if (width > height) {
            if (width > maxDim) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            }
          } else {
            if (height > maxDim) {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          canvas.width = Math.max(width, 1);
          canvas.height = Math.max(height, 1);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/png");
          imageCache[url] = dataUrl;
          resolve(dataUrl);
        } catch (err) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    } catch {
      resolve(null);
    }
  });
};

const cleanDocUrl = (url) => {
  if (!url) return "";
  if (url.includes("/storage/v1/object/public/Documents/")) {
    const relativePath = url.split("/storage/v1/object/public/Documents/")[1];
    return `/documents/${relativePath}`;
  }
  return url;
};

const downloadCleanPDF = (url, fileName) => {
  if (!url) return;
  const cleanUrl = cleanDocUrl(url);
  const defaultName = fileName || "Bulletin_de_paie.pdf";
  const a = document.createElement("a");
  a.href = cleanUrl;
  a.download = defaultName;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

function PdfPreviewModal({ preview, onClose }) {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (preview) setLoading(true);
  }, [preview]);

  if (!preview) return null;
  const cleanUrl = cleanDocUrl(preview.url);
  const cacheBustedUrl = cleanUrl + (cleanUrl.includes("?") ? "&" : "?") + "t=" + Date.now();

  return (
    <div className="ov" onClick={onClose} style={{ zIndex: 9999, padding: "12px 10px" }}>
      <div className="modal" style={{ maxWidth: 960, width: "100%", height: "92vh", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden", borderRadius: 16, background: "var(--card-bg, #fff)" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="mh" style={{ background: "linear-gradient(135deg,#071536,#0F2B7A)", borderRadius: "16px 16px 0 0", padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, overflow: "hidden", minWidth: 200, flex: 1 }}>
            <span style={{ fontSize: 22 }}>📄</span>
            <div style={{ overflow: "hidden" }}>
              <div className="mt" style={{ color: "#fff", fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                {preview.title || "Document RH"}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.65)", marginTop: 2 }}>
                Portail sécurisé · majharh.com
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "rgba(255, 255, 255, 0.15)",
                color: "#ffffff",
                border: "1px solid rgba(255, 255, 255, 0.35)",
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                textDecoration: "none",
                cursor: "pointer",
                transition: "background .15s"
              }}
              title="Ouvrir dans un nouvel onglet"
            >
              ↗ Ouvrir
            </a>

            <button
              type="button"
              onClick={() => downloadCleanPDF(preview.url, preview.fileName)}
              style={{
                background: "#0047FF",
                color: "#ffffff",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                transition: "opacity .15s"
              }}
            >
              ⬇ Télécharger
            </button>

            <button className="mx" style={{ color: "rgba(255,255,255,.8)", fontSize: 18, marginLeft: 4 }} onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Content Preview Frame */}
        <div style={{ flex: 1, background: "#2A2D32", position: "relative", display: "flex", flexDirection: "column" }}>
          {loading && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, background: "rgba(30,35,45,0.85)", zIndex: 10, color: "#fff" }}>
              <span style={{ width: 28, height: 28, border: "3px solid rgba(255,255,255,.2)", borderTopColor: "#60A5FA", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: "#E2E8F0" }}>Chargement du document…</span>
            </div>
          )}

          <iframe
            src={cacheBustedUrl}
            title={preview.title || "Aperçu PDF"}
            onLoad={() => setLoading(false)}
            style={{ width: "100%", height: "100%", border: "none", display: "block", flex: 1 }}
          />
        </div>
      </div>
    </div>
  );
}

const calcPayeSN = (f, isXOF) => {
  const standardHrs = isXOF ? 173.33 : 151.67;
  const workedHrs = (() => {
    if (!f.horaire) return standardHrs;
    const clean = String(f.horaire).replace(",", ".");
    const val = parseFloat(clean);
    return isNaN(val) || val <= 0 ? standardHrs : val;
  })();
  const ratio = workedHrs / standardHrs;

  const getBaseSalaryXOF = () => {
    if (workedHrs === standardHrs) {
      return n(f.salaire_base) || (f.categorie && CONVENTION_COMMERCE[f.categorie] ? CONVENTION_COMMERCE[f.categorie].sal : standardHrs * 669);
    } else {
      const rate = f.categorie && CONVENTION_COMMERCE[f.categorie]
        ? CONVENTION_COMMERCE[f.categorie].taux
        : (f.salaire_base ? n(f.salaire_base) / standardHrs : 669);
      return workedHrs * rate;
    }
  };
  const sb = isXOF ? getBaseSalaryXOF() : n(f.salaire_base) * ratio;
  const sur = n(f.sursalaire) * ratio;
  const ic = n(f.indem_conges);
  const pa = n(f.prime_anciennete) * ratio;
  const pp = n(f.prime_performance), ap = n(f.autres_primes);
  const it = n(f.indem_transport), irp = n(f.indem_repas);
  const nParts = n(f.parts_fiscales) || 1;
  const withIPM = f.with_ipm !== "non";
  const withIPRESComp = f.regime_cadre === "oui" || (f.categorie && CONVENTION_COMMERCE[f.categorie]?.cadre);
  const typeContrat = f.type_contrat || "cdi";

  // Retenue pour absence
  const heuresAbs = n(f.heures_absence);
  const tauxH = f.categorie && CONVENTION_COMMERCE[f.categorie]
    ? CONVENTION_COMMERCE[f.categorie].taux
    : (sb ? Math.round(sb / (n(f.horaire) || (isXOF ? 173 : 151.67)) * 100) / 100 : 0);
  const retAbsAbsence = Math.round(heuresAbs * tauxH);

  const brut_normal = sb + sur + ic + pa + pp + ap;
  const brut = brut_normal - retAbsAbsence;

  if (isXOF) {
    const base_imposable = brut - pp;
    if (typeContrat === "stagiaire") {
      const { trimf: trimf_stag } = base_imposable > 25000 ? lookupIR(base_imposable, 1) : { trimf: 0, ir: 0 };
      const fraisMed = n(f.retenue_frais_medicaux || f.frais_medicaux_ipm);
      const net = Math.round(base_imposable - (base_imposable > 25000 ? trimf_stag : 0) + it + irp + pp - fraisMed);
      return {
        sb, sur, ic, pa,
        brut, net, trimf: base_imposable > 25000 ? trimf_stag : 0, ipres_sal: 0, ipres_pat: 0,
        ipres_comp_sal: 0, ipres_comp_pat: 0, prest_fam: 0, acc_trav: 0, cfce: 0,
        ipm_sal: 0, ipm_pat: 0, fraisPro: 0, baseIR: 0, irMensuel: 0,
        retAbs: n(f.retenue_absences),
        retAbsAbsence,
        tauxH,
        retenue_frais_medicaux: fraisMed,
        note: "Stagiaire : pas d'IPRES ni d'IR — TRIMF si gratification > 25 000 FCFA"
      };
    }
    // Plafond IPRES : 432 000 FCFA pour les cadres (nouveau plafond), 360 000 FCFA pour les non-cadres (ancien plafond maintenu dans leur logiciel)
    const PLAF_IPRES = withIPRESComp ? 432000 : 360000;
    const brut_plafIPRES = Math.min(base_imposable, PLAF_IPRES);
    const ipres_sal = Math.round(brut_plafIPRES * 0.056);
    const ipres_pat = Math.round(brut_plafIPRES * 0.084);
    let ipres_comp_sal = 0, ipres_comp_pat = 0;
    let tranche_cadre = 0;
    if (withIPRESComp) {
      tranche_cadre = Math.min(base_imposable, 1296000);
      ipres_comp_sal = Math.round(tranche_cadre * 0.024);
      ipres_comp_pat = Math.round(tranche_cadre * 0.036);
    }
    const PLAF_CSS = 63000;
    const brut_plafCSS = Math.min(base_imposable, PLAF_CSS);
    const prest_fam = Math.round(brut_plafCSS * 0.07);
    const acc_trav = Math.round(brut_plafCSS * 0.01);
    const cfce = Math.round(base_imposable * 0.03);
    const ipm_sal = withIPM ? 8500 : 0;
    const ipm_pat = withIPM ? 8500 : 0;
    const partsKey = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].reduce((best, p) => Math.abs(p - nParts) < Math.abs(best - nParts) ? p : best, 1);
    const { ir: irMensuel, trimf } = lookupIR(base_imposable, partsKey);
    const retAbs = n(f.retenue_absences);
    const avanceSal = n(f.avance_salaire);
    const fraisMed = n(f.retenue_frais_medicaux || f.frais_medicaux_ipm);
    const totalRetSal = ipres_sal + ipres_comp_sal + ipm_sal + irMensuel + trimf + retAbs + avanceSal + fraisMed;
    const net = Math.round(base_imposable - totalRetSal + it + irp + pp);
    return { sb, sur, ic, pa, brut, net, ipres_sal, ipres_pat, ipres_comp_sal, ipres_comp_pat, prest_fam, acc_trav, cfce, ipm_sal, ipm_pat, irMensuel, trimf, retAbs, retAbsAbsence, tauxH, brut_plafIPRES, brut_plafCSS, tranche_cadre, retenue_frais_medicaux: fraisMed };
  }
  const PASS = 3864, SMIC_MENSUEL = 1802;
  const isCadre = f.is_cadre_fr === "oui";
  const withPrevoyance = f.with_prevoyance_fr === "oui";
  if (typeContrat === "alternance") {
    const pctSmic = n(f.pct_smic) || 80;
    const brutAlt_normal = brut || Math.round(SMIC_MENSUEL * pctSmic / 100);
    const brutAlt = brutAlt_normal - retAbsAbsence;
    const brutExo = Math.min(brutAlt, SMIC_MENSUEL * 0.79);
    const brutSurExo = Math.max(0, brutAlt - SMIC_MENSUEL * 0.79);
    const assCsg = brutSurExo * 0.9825;
    const csg_crds = Math.round(assCsg * 0.097);
    const totalRetSal = csg_crds;
    const net = Math.round(brutAlt - totalRetSal + it + irp);
    return { sb, sur, pa, brut: brutAlt, net, csg_crds, retraite_sal: 0, prevoyance_sal: 0, chomage_pat: 0, maladie_pat: 0, retraite_pat: 0, ipres_sal: 0, ipres_pat: 0, prest_fam: 0, acc_trav: 0, cfce: 0, ipm_sal: 0, ipm_pat: 0, fraisPro: 0, baseIR: 0, irMensuel: 0, trimf: 0, retAbs: n(f.retenue_absences), retAbsAbsence, tauxH, pctSmic, brutExo, brutSurExo, note: "Alternance : exonération quasi-totale si ≤ 79% SMIC." };
  }
  if (typeContrat === "stagiaire") {
    const FRANCHISE_FR = 618;
    const imposable = Math.max(0, brut - FRANCHISE_FR);
    const csg_crds_stag = imposable > 0 ? Math.round(imposable * 0.9825 * 0.097) : 0;
    const net = Math.round(brut - csg_crds_stag + it + irp);
    return { sb, sur, pa, brut, net, csg_crds: csg_crds_stag, retraite_sal: 0, prevoyance_sal: 0, chomage_pat: 0, maladie_pat: 0, retraite_pat: 0, ipres_sal: 0, ipres_pat: 0, prest_fam: 0, acc_trav: 0, cfce: 0, ipm_sal: 0, ipm_pat: 0, fraisPro: 0, baseIR: 0, irMensuel: 0, trimf: 0, retAbs: n(f.retenue_absences), retAbsAbsence, tauxH, note: "Stagiaire France : exonération cotisations si gratif. ≤ 618€/mois" };
  }
  const brutT1 = Math.min(brut, PASS), brutT2 = Math.max(0, brut - PASS);
  const assietteCsg = brut * 0.9825;
  const csg_nr = Math.round(assietteCsg * 0.024), csg_ded = Math.round(assietteCsg * 0.068), crds = Math.round(assietteCsg * 0.005);
  const csg_crds = csg_nr + csg_ded + crds;
  const retraite_base_sal = Math.round(brutT1 * 0.069), retraite_base_pat = Math.round(brutT1 * 0.0855) + Math.round(brut * 0.0175);
  const arrco_sal_t1 = Math.round(brutT1 * 0.0315), arrco_pat_t1 = Math.round(brutT1 * 0.0472);
  const arrco_sal_t2 = isCadre ? Math.round(brutT2 * 0.0997) : Math.round(brutT2 * 0.0864);
  const arrco_pat_t2 = isCadre ? Math.round(brutT2 * 0.1495) : Math.round(brutT2 * 0.1295);
  const cet_sal = isCadre ? Math.round(brut * 0.0014) : 0, cet_pat = isCadre ? Math.round(brut * 0.0021) : 0;
  const retraite_sal = retraite_base_sal + arrco_sal_t1 + arrco_sal_t2 + cet_sal;
  const retraite_pat = retraite_base_pat + arrco_pat_t1 + arrco_pat_t2 + cet_pat;
  const maladie_pat = Math.round(brut * 0.07), chomage_pat = Math.round(brut * 0.0405);
  const prevoyance_sal = withPrevoyance ? Math.round(brut * (isCadre ? 0.015 : 0.006)) : isCadre ? Math.round(brut * 0.015) : 0;
  const prevoyance_pat = isCadre ? Math.round(brut * 0.0225) : 0;
  const at_pat = Math.round(brut * 0.0222);
  const af_pat = Math.round(brut * (brut <= SMIC_MENSUEL * 3.5 ? 0.0345 : 0.0525));
  const totalRetSal = csg_crds + retraite_sal + prevoyance_sal + n(f.retenue_absences);
  const net = Math.round(brut - totalRetSal + it + irp);
  return { sb, sur, pa, brut, brut_plafIPRES: brutT1, brut_plafCSS: brutT2, csg_crds, csg_nr, csg_ded, crds, retraite_sal, retraite_pat, maladie_pat, chomage_pat, at_pat, af_pat, prevoyance_sal, prevoyance_pat, cfce: 0, ipm_sal: 0, ipm_pat: 0, ipres_sal: 0, ipres_pat: 0, prest_fam: af_pat, acc_trav: at_pat, fraisPro: 0, baseIR: 0, irMensuel: 0, trimf: 0, retAbs: n(f.retenue_absences), retAbsAbsence, tauxH, net, isXOF: false, isCadre };
};

function SimulateurPaie() {
  const [f, setF] = useState({
    sb: 0, sur: 0, ic: 0, pa: 0, pp: 0, ap: 0, it: 26000, irp: 0, parts: 1, cadre: false, ipm: true, heures_absence: 0
  });

  const res = useMemo(() => {
    if (!f.sb || f.sb <= 0) return null;
    try {
      return calcPayeSN({
        salaire_base: f.sb,
        sursalaire: f.sur,
        indem_conges: f.ic,
        prime_anciennete: f.pa,
        prime_performance: f.pp,
        autres_primes: f.ap,
        indem_transport: f.it,
        indem_repas: f.irp,
        parts_fiscales: f.parts,
        regime_cadre: f.cadre ? "oui" : "non",
        with_ipm: f.ipm ? "oui" : "non",
        type_contrat: "cdi",
        remb_pret: 0,
        avance_salaire: 0,
        retenue_absences: 0,
        horaire: "173,33",
        heures_absence: f.heures_absence
      }, true);
    } catch (e) {
      console.error("Erreur calcPayeSN:", e);
      return null;
    }
  }, [f]);

  const row = (l, v, c) => (
    <div className="calc-row">
      <span style={{ fontSize: 12, color: "var(--text2)" }}>{l}</span>
      <span style={{ fontWeight: 600, color: c || "var(--text)" }}>{fN(v, "XOF")}</span>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: 1000, margin: "0 auto", background: "#fff", border: "1.5px solid var(--border2)", boxShadow: "0 12px 40px rgba(0,0,0,.08)" }}>
      <div className="g2" style={{ gap: 40, display: "grid", gridTemplateColumns: "1fr 1fr" }}>
        <div>
          <div className="sect" style={{ marginBottom: 24, fontSize: 18 }}>Simulateur de Paie SÉNÉGAL</div>
          <div style={{ fontSize: 10, fontWeight: 800, color: "var(--blue)", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Salaire & Primes</div>
          <div className="fgrid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field"><label className="flbl">Salaire de base</label><input type="number" className="finp" value={f.sb || ""} onChange={e => setF({ ...f, sb: n(e.target.value) })} /></div>
            <div className="field"><label className="flbl">Sursalaire</label><input type="number" className="finp" value={f.sur || ""} onChange={e => setF({ ...f, sur: n(e.target.value) })} /></div>
            <div className="field"><label className="flbl">Indemnités de congés</label><input type="number" className="finp" value={f.ic || ""} onChange={e => setF({ ...f, ic: n(e.target.value) })} /></div>
            <div className="field"><label className="flbl">Prime ancienneté</label><input type="number" className="finp" value={f.pa || ""} onChange={e => setF({ ...f, pa: n(e.target.value) })} /></div>
            <div className="field"><label className="flbl">Prime performance</label><input type="number" className="finp" value={f.pp || ""} onChange={e => setF({ ...f, pp: n(e.target.value) })} /></div>
            <div className="field"><label className="flbl">Autres primes</label><input type="number" className="finp" value={f.ap || ""} onChange={e => setF({ ...f, ap: n(e.target.value) })} /></div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 800, color: "var(--blue)", marginTop: 24, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Indemnités</div>
          <div className="fgrid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field"><label className="flbl">Transport</label><input type="number" className="finp" value={f.it || ""} onChange={e => setF({ ...f, it: n(e.target.value) })} /></div>
            <div className="field"><label className="flbl">Repas</label><input type="number" className="finp" value={f.irp || ""} onChange={e => setF({ ...f, irp: n(e.target.value) })} /></div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 800, color: "var(--blue)", marginTop: 24, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Situation</div>
          <div className="field"><label className="flbl">Parts fiscales</label><select className="fsel" value={f.parts} onChange={e => setF({ ...f, parts: parseFloat(e.target.value) })}>
            {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map(p => <option key={p} value={p}>{p} part{p > 1 ? "s" : ""}</option>)}
          </select></div>
          <div style={{ display: "flex", gap: 20, marginTop: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}><input type="checkbox" checked={f.cadre} onChange={e => setF({ ...f, cadre: e.target.checked })} /> Statut Cadre</label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}><input type="checkbox" checked={f.ipm} onChange={e => setF({ ...f, ipm: e.target.checked })} /> Option IPM</label>
          </div>
          <div style={{ fontSize: 10, fontWeight: 800, color: "var(--blue)", marginTop: 24, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Déductions</div>
          <div className="field" style={{ marginBottom: 0 }}><label className="flbl">Heures d'absence</label><input type="number" className="finp" placeholder="0" value={f.heures_absence || ""} onChange={e => setF({ ...f, heures_absence: n(e.target.value) })} /></div>
        </div>

        <div className="calc-box" style={{ background: "var(--blue-ll)", border: "1.5px solid var(--blue-l)", padding: 32, borderRadius: 20 }}>
          {!res ? (
            <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", color: "var(--text3)", opacity: 0.6 }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🧮</div>
              <div style={{ textAlign: "center" }}>Saisissez un salaire de base<br />pour voir les résultats</div>
            </div>
          ) : (
            <>
              <div className="net-hero" style={{ marginBottom: 28, padding: 24, borderRadius: 16, boxShadow: "0 10px 25px rgba(0,71,255,.2)" }}>
                <div className="net-lbl" style={{ opacity: .8 }}>Net à Payer Réel</div>
                <div className="net-val" style={{ fontSize: 38 }}>{fN(res.net, "XOF")}</div>
              </div>
              {row("Brut total (CSS)", res.brut + f.it + f.irp)}
              {row("Brut imposable", res.brut - f.pp)}
              <div style={{ margin: "20px 0", borderTop: "1px dashed var(--border2)" }}></div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text3)", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Retenues Salariales</div>
              {res.retAbsAbsence > 0 && row("Retenue pour absence", res.retAbsAbsence, "var(--red)")}
              {row("IPRES RG 5,6%", res.ipres_sal, "var(--red)")}
              {f.cadre && row("IPRES Cadre 2,4%", res.ipres_comp_sal, "var(--red)")}
              {row("IR (Table DGI)", res.irMensuel, "var(--red)")}
              {row("TRIMF", res.trimf, "var(--red)")}
              {f.ipm && row("IPM Santé (Forfait)", res.ipm_sal, "var(--red)")}
              <div style={{ margin: "20px 0", borderTop: "1px dashed var(--border2)" }}></div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text3)", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Charges Patronales</div>
              {row("IPRES Pat 8,4%", res.ipres_pat)}
              {f.cadre && row("IPRES Cadre Pat 3,6%", res.ipres_comp_pat)}
              {row("Prest. Familiale", res.prest_fam)}
              {row("Accident Travail", res.acc_trav)}
              {row("CFCE 3%", res.cfce)}
              {f.ipm && row("IPM Pat (Forfait)", res.ipm_pat)}
              <div className="calc-row tot" style={{ marginTop: 20, paddingTop: 20, borderTop: "2.5px solid var(--blue-l)", fontSize: 17 }}>
                <span>Coût global entreprise</span>
                <span>{fN(res.brut + res.ipres_pat + res.ipres_comp_pat + res.prest_fam + res.acc_trav + res.cfce + res.ipm_pat + f.it + f.irp, "XOF")}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const fN = (v, d) => !v && v !== 0 ? "—" : d === "XOF"
  ? new Intl.NumberFormat("fr-FR").format(Math.round(v)) + " FCFA"
  : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);
const fD = d => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—";
const fDs = d => d ? new Date(d).toLocaleDateString("fr-FR") : "—";
const TODAY_STR = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const nbJ = (a, b) => !a || !b ? 0 : Math.max(1, Math.ceil((new Date(b) - new Date(a)) / 86400000) + 1);

function countJoursOuvrables(dateDebut, dateFin) {
  if (!dateDebut || !dateFin) return 0;
  let count = 0;
  const current = new Date(dateDebut);
  const end = new Date(dateFin);

  while (current <= end) {
    const day = current.getDay(); // 0=dimanche, 6=samedi
    const mm = String(current.getMonth() + 1).padStart(2, "0");
    const dd = String(current.getDate()).padStart(2, "0");
    const yyyy = current.getFullYear();
    const dateStr = `${yyyy}-${mm}-${dd}`;
    const fixeStr = `${mm}-${dd}`;

    const isDimanche = day === 0; // dimanche uniquement
    const isJourFerie = FERIES_FIXES.includes(fixeStr) || FERIES_VARIABLES.includes(dateStr);

    if (!isDimanche && !isJourFerie) {
      count++;
    }

    current.setDate(current.getDate() + 1);
  }

  return count;
}

function calculateLeaveBalance(emp, approvedConges = []) {
  if (!emp || !emp.date_entree) return { total: 0, acquisition: 0, bonus: 0, pris: 0, solde: 0 };
  const parseDate = (d) => {
    if (!d) return null;
    if (d.includes("/")) {
      const [day, month, year] = d.split("/");
      return new Date(`${year}-${month}-${day}`);
    }
    return new Date(d);
  };

  const start = parseDate(emp.date_entree);
  const now = new Date();

  // Date de référence globale du cabinet
  // France : 1er Juin, Sénégal : 1er Août
  const refMonth = emp.site === "France" ? 5 : 7;
  let globalRef = new Date(now.getFullYear(), refMonth, 1);
  if (now < globalRef) {
    globalRef = new Date(now.getFullYear() - 1, refMonth, 1);
  }

  // La date de début de calcul est :
  // 1. La date de reprise saisie manuellement (priorité)
  // 2. SINON, la date la plus récente entre l'embauche et le 1er août/juin global
  const calcStart = emp.date_reprise_reference
    ? parseDate(emp.date_reprise_reference)
    : (start > globalRef ? start : globalRef);

  const monthsDiff = (now.getFullYear() - calcStart.getFullYear()) * 12 + (now.getMonth() - calcStart.getMonth());
  const seniorityYears = Math.floor(((now - start) / (1000 * 60 * 60 * 24 * 365.25)));

  // 1. Acquisition de base (2.5j/mois pour France, 2j/mois pour Sénégal)
  const rate = emp.site === "France" ? 2.5 : 2;
  const acquisition = Math.max(0, monthsDiff * rate);

  // 2. Bonus Ancienneté (Sénégal) - Basé sur l'ancienneté totale
  let bonusSeniority = 0;
  if (emp.site !== "France") {
    if (seniorityYears >= 25) bonusSeniority = 6;
    else if (seniorityYears >= 20) bonusSeniority = 3;
    else if (seniorityYears >= 15) bonusSeniority = 2;
    else if (seniorityYears >= 10) bonusSeniority = 1;
  }

  // 3. Bonus Mères de famille (+1j par enfant < 14 ans) (Sénégal)
  let bonusMothers = 0;
  if (emp.site !== "France" && emp.genre === "Femme" && emp.nb_enfants > 0) {
    bonusMothers = parseInt(emp.nb_enfants);
  }

  // 4. Ajustement manuel admin
  const adjustment = parseFloat(emp.solde_conges_ajustement || 0);

  // 5. Congés pris (SEULEMENT ceux pris APRÈS la date de référence)
  const pris = approvedConges
    .filter(c => {
      const cDate = new Date(c.date_debut);
      return c.employe_id === emp.id &&
        c.statut === "approuve" &&
        c.type_conge === "Congés payés" &&
        cDate >= calcStart;
    })
    .reduce((sum, c) => sum + (c.nb_jours || 0), 0);

  const cumul_conges = acquisition + bonusSeniority + bonusMothers;
  const acquis_annee = pris - adjustment;
  const total = cumul_conges + adjustment;
  const solde = Math.max(0, cumul_conges - acquis_annee);

  return {
    cumul_conges,
    acquis_annee,
    total,
    acquisition,
    bonus: bonusSeniority + bonusMothers,
    bonusSeniority,
    bonusMothers,
    adjustment,
    pris,
    solde
  };
}

/* ─────────────────────────────────────
   GESTION DES STATUTS DE VALIDATION DES BULLETINS
───────────────────────────────────── */
export const isBulletinBrouillon = (f) => {
  if (!f || !f.uploaded_by) return false;
  return f.uploaded_by.startsWith("[BROUILLON]") || f.uploaded_by.startsWith("brouillon:");
};

export const isBulletinPublie = (f) => {
  return !isBulletinBrouillon(f);
};

export const formatUploadedBy = (uploaded_by) => {
  if (!uploaded_by) return "—";
  return uploaded_by.split("||SNAP:")[0].replace(/^(\[BROUILLON\]|\[PUBLIÉ\]|brouillon:|publie:)\s*/i, "").trim() || "—";
};

export const getBulletinSnapRaw = (uploaded_by) => {
  if (!uploaded_by || !uploaded_by.includes("||SNAP:")) return "";
  return "||SNAP:" + uploaded_by.split("||SNAP:")[1];
};

export const parseBulletinSnap = (fiche) => {
  if (!fiche?.uploaded_by || !fiche.uploaded_by.includes("||SNAP:")) return null;
  try {
    return JSON.parse(fiche.uploaded_by.split("||SNAP:")[1]);
  } catch {
    return null;
  }
};

export const buildBulletinSnap = (emp, calcForm, calc) => {
  const sBase = calcForm?.salaire_base !== undefined ? (parseFloat(calcForm.salaire_base) || 0) : (parseFloat(emp?.salaire_base) || 0);
  const sSur = calcForm?.sursalaire !== undefined ? (parseFloat(calcForm.sursalaire) || 0) : (parseFloat(emp?.sursalaire) || 0);
  const sConges = parseFloat(calcForm?.indem_conges) || 0;
  const iTrans = calcForm?.indem_transport !== undefined ? (parseFloat(calcForm.indem_transport) || 26000) : (parseFloat(emp?.indem_transport) || 26000);
  const iRepas = calcForm?.indem_repas !== undefined ? (parseFloat(calcForm.indem_repas) || 0) : (parseFloat(emp?.indem_repas) || 0);
  const pPerf = calcForm?.prime_performance !== undefined ? (parseFloat(calcForm.prime_performance) || 0) : (parseFloat(emp?.prime_performance) || 0);
  return {
    brut: Math.round(sBase + sSur + sConges),
    indem_conges: Math.round(sConges),
    transport: Math.round(iTrans),
    remuDirecte: Math.round(iRepas + pPerf),
    chargesSociales: Math.round((calc.ipres_pat || 0) + (calc.ipres_comp_pat || 0) + (calc.prest_fam || 0) + (calc.acc_trav || 0) + (calc.cfce || 0) + (calc.ipm_pat || 0)),
    prestFam: Math.round(calc.prest_fam || 0),
    accTrav: Math.round(calc.acc_trav || 0),
    ipres: Math.round((calc.ipres_sal || 0) + (calc.ipres_pat || 0) + (calc.ipres_comp_sal || 0) + (calc.ipres_comp_pat || 0)),
    ipres_sal: Math.round(calc.ipres_sal || 0),
    ipres_pat: Math.round(calc.ipres_pat || 0),
    ipres_comp_sal: Math.round(calc.ipres_comp_sal || 0),
    ipres_comp_pat: Math.round(calc.ipres_comp_pat || 0),
    ipm_sal: Math.round(calc.ipm_sal || 0),
    ipm_pat: Math.round(calc.ipm_pat || 0),
    ir: Math.round(calc.irMensuel || 0),
    cfce: Math.round(calc.cfce || 0),
    trimf: Math.round(calc.trimf || 0),
    parts_fiscales: parseFloat(calcForm?.parts_fiscales || emp?.parts_fiscales || 1),
    poste: emp?.poste || ""
  };
};

/* ─────────────────────────────────────
   DESIGN SYSTEM — CSS
───────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
:root{
  --bg:#F4F6FA;
  --white:#FFFFFF;
  --border:#E4E9F2;
  --border2:#CDD5E3;
  --text:#0D1117;
  --text2:#4A5568;
  --text3:#8896A8;
  --blue:#0047FF;
  --blue-d:#0035CC;
  --blue-l:#EBF0FF;
  --blue-ll:#F4F7FF;
  --green:#059669;
  --green-l:#D1FAE5;
  --red:#DC2626;
  --red-l:#FEF2F2;
  --yellow:#D97706;
  --yellow-l:#FEF3C7;
  --orange:#C2410C;
  --orange-l:#FEF9EE;
  --r8:8px; --r10:10px; --r12:12px; --r16:16px;
  --shadow:0 1px 4px rgba(0,0,0,.06),0 1px 2px rgba(0,0,0,.04);
  --shadow-md:0 4px 16px rgba(0,71,255,.1),0 1px 4px rgba(0,0,0,.06);
}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html,body{height:100%;font-family:'Inter',system-ui,sans-serif;background:var(--bg);color:var(--text);-webkit-font-smoothing:antialiased;font-size:14px}
.sec-mask{-webkit-text-security:disc !important;text-security:disc !important;}
a{color:inherit;text-decoration:none}
input,select,textarea,button{font-family:inherit;font-size:inherit}
button{cursor:pointer}

/* ── LOGIN ── */

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

.lp{min-height:100vh;display:flex;align-items:center;justify-content:center;background:linear-gradient(145deg, #001A8C 0%, #0047FF 60%, #3366FF 100%); padding:20px; position:relative; overflow:hidden;}
.lp::before{content:'';position:absolute;inset:0;backdrop-filter:blur(30px);-webkit-backdrop-filter:blur(30px);background:rgba(255,255,255,0.05);z-index:1;}
.lcard{background:#ffffff;border:1px solid rgba(255,255,255,0.8);border-radius:var(--r16);padding:48px;width:100%;max-width:440px;box-shadow:0 30px 60px rgba(0,0,0,0.4);position:relative;z-index:2;}
.llogo{display:flex;align-items:center;gap:14px;margin-bottom:36px;justify-content:center}
.lmark{width:48px;height:48px;background:linear-gradient(135deg, var(--blue), #60A5FA);border-radius:14px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:22px;font-weight:700; box-shadow: 0 0 20px rgba(0, 71, 255, 0.4); animation: float 4s ease-in-out infinite;}
.lbrand{font-size:24px;font-weight:700;color:#0F172A; letter-spacing:-0.5px;}
.lh{font-size:24px;font-weight:700;color:#0F172A;margin-bottom:8px;text-align:center; letter-spacing:-0.5px;}
.lp2{font-size:15px;color:#475569;margin-bottom:36px;text-align:center;line-height:1.6}
.lerr{background:#FEE2E2;color:#EF4444;font-size:13px;padding:14px;border-radius:var(--r8);margin-bottom:24px;font-weight:600;text-align:center; border: 1px solid rgba(239,68,68,0.3);}
.lfield{margin-bottom:24px}
.llbl{display:block;font-size:13px;font-weight:600;color:#475569;margin-bottom:10px}
.linp{width:100%;padding:14px 18px;border:2px solid #CBD5E1;border-radius:var(--r8);font-size:15px;color:#0F172A;outline:none;transition:all 0.3s;background:#F8FAFC;}
.linp:focus{border-color:var(--blue);box-shadow:0 0 0 4px rgba(0, 71, 255, 0.15); background:#ffffff;}
.lbtn{width:100%;padding:16px;background:linear-gradient(135deg, var(--blue), var(--blue-d));color:#fff;border:none;border-radius:var(--r8);font-size:16px;font-weight:700;transition:all 0.3s;margin-top:12px; box-shadow: 0 10px 20px rgba(0, 71, 255, 0.35); cursor:pointer;}
.lbtn:hover{transform: translateY(-2px); box-shadow: 0 15px 30px rgba(0, 71, 255, 0.45); background:linear-gradient(135deg, #3366FF, var(--blue));}
.lbtn:active{transform: translateY(0);}
.lbtn:disabled{opacity:.5;cursor:not-allowed; transform:none; box-shadow:none;}

/* ── SHELL ── */
.shell{display:flex;height:100vh;overflow:hidden}

/* ── SIDEBAR ── */
.side{width:236px;min-width:236px;background:linear-gradient(160deg, #001A8C 0%, #0047FF 55%, #3366FF 100%);display:flex;flex-direction:column}
.slogo{padding:22px 20px 20px;border-bottom:1px solid rgba(255,255,255,.12);display:flex;align-items:center;gap:12px}
.smark{width:38px;height:38px;background:#fff;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 8px rgba(0,0,0,.12)}
.sbrand{font-size:15px;font-weight:700;color:#fff;letter-spacing:.1px}
.stag{font-size:10px;color:rgba(255,255,255,.55);margin-top:2px}
.snav{flex:1;padding:12px 10px;overflow-y:auto}
.snav::-webkit-scrollbar{display:none}
.nsec{font-size:9.5px;font-weight:600;color:rgba(255,255,255,.4);padding:14px 10px 6px;letter-spacing:1px;text-transform:uppercase}
.nitem{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:var(--r8);cursor:pointer;color:rgba(255,255,255,.65);font-size:13px;font-weight:400;margin-bottom:3px;transition:all .12s;position:relative;user-select:none}
.nitem:hover{background:rgba(255,255,255,.1);color:#fff}
.nitem.on{background:rgba(255,255,255,.18);color:#fff;font-weight:600}
.nic{font-size:15px;flex-shrink:0;width:18px;text-align:center}
.nbdg{margin-left:auto;background:#FF4444;color:#fff;font-size:9px;font-weight:700;padding:2px 7px;border-radius:20px}
.sfoot{padding:16px;border-top:1px solid rgba(255,255,255,.12)}
.surow{display:flex;align-items:center;gap:10px;margin-bottom:10px}
.suname{font-size:12px;font-weight:600;color:#fff}
.surole{font-size:11px;color:rgba(255,255,255,.5);margin-top:2px}
.soutt{font-size:12px;color:rgba(255,255,255,.45);cursor:pointer;transition:color .12s;display:inline-flex;align-items:center;gap:5px}
.soutt:hover{color:#fff}

/* ── MAIN ── */
.main{flex:1;display:flex;flex-direction:column;overflow:hidden}
.topbar{background:var(--white);border-bottom:1px solid var(--border);padding:0 28px;height:62px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0;box-shadow:0 1px 3px rgba(0,0,0,.04)}
.tbtitle{font-size:16px;font-weight:700;color:var(--text);letter-spacing:-.2px}
.tbsub{font-size:11.5px;color:var(--text3);margin-top:2px}
.tbright{display:flex;align-items:center;gap:12px}
.content{flex:1;overflow-y:auto;padding:28px 28px}
.content::-webkit-scrollbar{width:4px}
.content::-webkit-scrollbar-track{background:transparent}
.content::-webkit-scrollbar-thumb{background:var(--border2);border-radius:4px}

/* ── AVATAR ── */
.av{border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:600;color:#fff;flex-shrink:0}

/* ── CARD ── */
.card{background:var(--white);border:1px solid var(--border);border-radius:var(--r12);padding:22px;box-shadow:var(--shadow)}
.chead{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px}
.ctitle{font-size:14px;font-weight:600;color:var(--text)}
.csub{font-size:12px;color:var(--text3)}

/* ── KPI GRID ── */
.kgrid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:26px}
.kcard{background:var(--white);border:1px solid var(--border);border-radius:14px;padding:22px;box-shadow:0 4px 18px rgba(0,0,0,0.03);position:relative;overflow:hidden;transition:transform 0.2s}
.kcard:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,0.06)}
.kic{font-size:22px;margin-bottom:12px;display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;background:var(--bg);border-radius:12px;color:var(--blue)}
.klbl{font-size:11.5px;color:var(--text3);font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px}
.kval{font-size:26px;font-weight:800;color:var(--text);line-height:1;letter-spacing:-.5px;margin-bottom:4px}
.ksub{font-size:11px;color:var(--text3);font-weight:500}

/* ── BADGES ── */
.badge{display:inline-flex;align-items:center;padding:4px 10px;border-radius:20px;font-size:11px;font-weight:500;white-space:nowrap}
.b-blue{background:var(--blue-l);color:var(--blue)}
.b-green{background:var(--green-l);color:var(--green)}
.b-yellow{background:var(--yellow-l);color:var(--yellow)}
.b-red{background:var(--red-l);color:var(--red)}
.b-gray{background:#F1F5F9;color:#64748B}
.b-navy{background:var(--blue-l);color:var(--blue)}
.b-xof{background:var(--orange-l);color:var(--orange)}

/* ── BUTTONS ── */
.btn{padding:9px 18px;border-radius:var(--r8);font-size:13px;font-weight:500;border:1.5px solid transparent;transition:all .12s;display:inline-flex;align-items:center;gap:7px}
.btn:disabled{opacity:.5;cursor:not-allowed}
.btn-sm{padding:6px 13px;font-size:12px}
.btn-primary{background:var(--blue);color:#fff;border-color:var(--blue)}
.btn-primary:hover:not(:disabled){background:var(--blue-d)}
.btn-ghost{background:var(--white);color:var(--text2);border-color:var(--border)}
.btn-ghost:hover:not(:disabled){background:var(--bg)}
.btn-danger{background:var(--red-l);color:var(--red);border-color:#FECACA}
.btn-success{background:var(--green-l);color:var(--green);border-color:#A7F3D0}

/* ── TABLE ── */
.tbl-wrap{background:var(--white);border:1px solid var(--border);border-radius:var(--r12);overflow:hidden;box-shadow:var(--shadow)}
.tbl{width:100%;border-collapse:collapse}
.tbl th{text-align:left;font-size:11px;font-weight:600;color:var(--text3);padding:12px 18px;border-bottom:1px solid var(--border);white-space:nowrap;background:#FAFBFD;text-transform:uppercase;letter-spacing:.5px}
.tbl td{padding:14px 18px;border-bottom:1px solid #F7F8FB;font-size:13px;color:var(--text2);vertical-align:middle}
.tbl tbody tr:last-child td{border-bottom:none}
.tbl tbody tr:hover td{background:var(--blue-ll)}

/* ── MODAL ── */
.ov{position:fixed;inset:0;background:rgba(13,17,23,.45);z-index:100;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(3px)}
.modal{background:var(--white);border-radius:var(--r16);width:100%;max-width:520px;max-height:90vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.18)}
.mhead{padding:22px 24px 18px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between}
.mtitle{font-size:16px;font-weight:700;color:var(--text);letter-spacing:-.2px}
.msub{font-size:12px;color:var(--text3);margin-top:3px}
.mclose{background:none;border:none;font-size:20px;cursor:pointer;color:var(--text3);line-height:1;padding:2px;border-radius:6px;transition:all .12s}
.mclose:hover{color:var(--text);background:var(--bg)}
.mbody{padding:22px 24px}
.mfoot{padding:16px 24px;border-top:1px solid var(--border);display:flex;gap:10px;justify-content:flex-end}

/* ── FORM FIELDS ── */
.fgrid2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.field{margin-bottom:16px}
.flabel{display:block;font-size:11.5px;font-weight:500;color:var(--text2);margin-bottom:6px}
.finput{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:var(--r8);font-size:13.5px;color:var(--text);outline:none;background:#fff;transition:border .15s}
.finput:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(25,91,253,.08)}
.fselect{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:var(--r8);font-size:13.5px;color:var(--text);outline:none;background:#fff}
.ftextarea{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:var(--r8);font-size:13.5px;color:var(--text);outline:none;resize:vertical;min-height:80px;background:#fff}

/* ── FILTER BUTTONS ── */
.fbtn{padding:7px 16px;border-radius:20px;font-size:12px;font-weight:500;border:1.5px solid var(--border);background:var(--white);color:var(--text2);cursor:pointer;transition:all .12s}
.fbtn.on{background:var(--blue);color:#fff;border-color:var(--blue)}
.fbtn:hover:not(.on){background:var(--bg)}

/* ── SECTION HEADER ── */
.sech{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px}
.sect{font-size:15px;font-weight:700;color:var(--text);letter-spacing:-.2px}

/* ── SEARCH ── */
.sbox{display:flex;align-items:center;gap:9px;padding:10px 15px;background:var(--white);border:1.5px solid var(--border);border-radius:var(--r8);margin-bottom:16px;transition:border .12s}
.sbox:focus-within{border-color:var(--blue)}
.sinp{flex:1;border:none;background:none;outline:none;font-size:13px;color:var(--text)}

/* ── NOTIF BELL ── */
.bell-btn{position:relative;padding:8px;border-radius:var(--r8);border:1.5px solid var(--border);background:var(--white);color:var(--text2);font-size:16px;line-height:1;transition:all .12s}
.bell-btn:hover{background:var(--bg)}
.bell-dot{position:absolute;top:5px;right:5px;width:8px;height:8px;background:#EF4444;border-radius:50%;border:2px solid #fff}
.notif-panel{position:absolute;top:calc(100% + 8px);right:0;width:320px;background:var(--white);border:1px solid var(--border);border-radius:var(--r12);box-shadow:var(--shadow-md);z-index:300;overflow:hidden}
.np-head{padding:14px 18px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between}
.np-t{font-size:13px;font-weight:600;color:var(--text)}
.np-item{padding:12px 18px;border-bottom:1px solid #F7F8FA;cursor:pointer;transition:background .1s}
.np-item:hover{background:var(--bg)}
.np-item:last-child{border-bottom:none}
.np-it{font-size:12px;font-weight:500;color:var(--text)}
.np-is{font-size:11px;color:var(--text3);margin-top:2px}
.np-unread{background:var(--blue-ll)}

/* ── TOAST ── */
.toast-wrap{position:fixed;bottom:28px;right:28px;z-index:999;display:flex;flex-direction:column;gap:10px;pointer-events:none}
.toast{background:var(--text);color:#fff;padding:14px 18px;border-radius:var(--r12);display:flex;align-items:flex-start;gap:12px;box-shadow:0 8px 28px rgba(0,0,0,.2);max-width:300px;animation:slideIn .2s ease;pointer-events:all;border-left:3px solid var(--blue)}
.toast-ic{font-size:18px;flex-shrink:0;margin-top:1px}
.toast-t{font-size:13px;font-weight:600}
.toast-s{font-size:11px;opacity:.65;margin-top:3px}
@keyframes slideIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes spin{to{transform:rotate(360deg)}}

/* ── EMPLOYEE SPECIFIC ── */
.net-hero{background:linear-gradient(135deg,var(--blue-d) 0%,var(--blue) 100%);border-radius:var(--r16);padding:26px;color:#fff;margin-bottom:18px;position:relative;overflow:hidden}
.net-hero::after{content:'';position:absolute;right:-30px;top:-30px;width:140px;height:140px;background:rgba(255,255,255,.07);border-radius:50%}
.net-lbl{font-size:11px;opacity:.7;margin-bottom:8px;letter-spacing:.5px;text-transform:uppercase;font-weight:500}
.net-val{font-size:34px;font-weight:700;line-height:1;letter-spacing:-1px}
.net-period{font-size:11px;opacity:.5;margin-top:7px}
.soldes-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:20px}
.solde-box{background:var(--white);border:1px solid var(--border);border-radius:var(--r12);padding:18px;text-align:center;box-shadow:var(--shadow)}
.solde-val{font-size:28px;font-weight:700;color:var(--text);letter-spacing:-.5px}
.solde-lbl{font-size:11px;color:var(--text3);margin-top:5px;font-weight:500}
.profil-banner{background:linear-gradient(135deg,var(--blue-d),var(--blue));border-radius:var(--r16);padding:28px;color:#fff;display:flex;align-items:center;gap:20px;margin-bottom:20px;position:relative;overflow:hidden}
.profil-banner::after{content:'';position:absolute;right:-40px;bottom:-40px;width:180px;height:180px;background:rgba(255,255,255,.06);border-radius:50%}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.info-box{padding:12px 14px;background:var(--bg);border-radius:var(--r10);border:1px solid var(--border)}
.info-lbl{font-size:10.5px;color:var(--text3);margin-bottom:4px;font-weight:500;text-transform:uppercase;letter-spacing:.3px}
.info-val{font-size:13.5px;font-weight:600;color:var(--text)}
.ann-card{background:var(--white);border:1px solid var(--border);border-radius:var(--r12);padding:20px;margin-bottom:12px;box-shadow:var(--shadow)}
.ann-type{display:inline-block;font-size:10.5px;font-weight:600;padding:3px 10px;border-radius:20px;margin-bottom:10px;text-transform:uppercase;letter-spacing:.4px}
.pay-list{display:flex;flex-direction:column;gap:2px}
.pay-row{display:flex;align-items:center;gap:14px;padding:12px 14px;border-radius:var(--r8);cursor:pointer;transition:background .1s}
.pay-row:hover{background:var(--bg)}

/* ── CALC PREVIEW ── */
.calc-box{background:var(--blue-l);border:1px solid #C7D7F8;border-radius:var(--r10);padding:16px;margin-top:14px}
.calc-row{display:flex;justify-content:space-between;padding:5px 0;font-size:13px;color:var(--text2)}
.calc-row.tot{border-top:1px solid #C7D7F8;margin-top:8px;padding-top:12px;font-weight:700;font-size:15px;color:var(--blue)}

/* ── UPLOAD ZONE ── */
.upload-zone{display:flex;align-items:center;gap:12px;padding:13px 16px;border:1.5px dashed var(--border2);border-radius:var(--r10);cursor:pointer;background:var(--bg);transition:all .15s}
.upload-zone:hover{border-color:var(--blue);background:var(--blue-ll)}

/* ── ABSENCE ITEMS ── */
.abs-item{display:flex;align-items:flex-start;gap:14px;padding:16px 18px;background:var(--white);border:1px solid var(--border);border-radius:var(--r12);box-shadow:var(--shadow);margin-bottom:10px}
.abs-alert{background:var(--red-l);border:1px solid #FECACA;border-radius:var(--r10);padding:14px 16px;margin-bottom:16px}
.abs-alert-t{font-size:12px;font-weight:600;color:var(--red);margin-bottom:10px}
.abs-alert-row{display:flex;justify-content:space-between;font-size:12px;color:var(--text2);padding:4px 0}

/* ── EMPTY STATE ── */
.empty{text-align:center;padding:56px 20px;color:var(--text3)}
.empty-ic{font-size:44px;margin-bottom:14px;opacity:.5}
.empty-t{font-size:14px;font-weight:600;color:var(--text2);margin-bottom:5px}
.empty-s{font-size:13px;line-height:1.65;max-width:280px;margin:0 auto}

/* ── MOBILE NAV ── */
.bnav{display:none;position:fixed;bottom:0;left:0;right:0;background:var(--white);border-top:1px solid var(--border);z-index:100;padding-bottom:env(safe-area-inset-bottom);box-shadow:0 -2px 12px rgba(0,0,0,.06)}
.bnav-inner{display:flex;height:60px;align-items:center}
.btab{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;background:none;border:none;padding:6px;position:relative}
.btab-ic{font-size:20px}
.btab-lb{font-size:9.5px;color:var(--text3);font-family:inherit;font-weight:500}
.btab.on .btab-lb{color:var(--blue);font-weight:600}
.btab-bdg{position:absolute;top:3px;right:calc(50% - 18px);background:#EF4444;color:#fff;font-size:9px;padding:1px 5px;border-radius:20px}

/* ── GRID LAYOUTS ── */
.g2{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-bottom:18px}
.g14{display:grid;grid-template-columns:210px 1fr;gap:18px}
.g31{display:grid;grid-template-columns:2fr 1fr;gap:20px}
.qact-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px}
.qbtn{background:var(--white);border:1px solid var(--border);border-radius:12px;padding:14px;text-align:left;cursor:pointer;transition:all .15s}
.qbtn:hover{background:var(--blue-ll);border-color:var(--blue-l);transform:translateY(-1px)}
.qic{font-size:18px;margin-bottom:6px;display:block}
.qlbl{font-size:12px;font-weight:600;color:var(--text);margin-bottom:2px}
.qsub{font-size:10.5px;color:var(--text3)}
.tl-item{display:flex;align-items:flex-start;gap:10px;margin-bottom:14px;position:relative}
.tl-item::before{content:'';position:absolute;left:4px;top:18px;bottom:-14px;width:1.5px;background:var(--border)}
.tl-item:last-child::before{display:none}
.tl-dot{width:10px;height:10px;border-radius:50%;background:var(--blue);margin-top:4px;flex-shrink:0;position:relative;z-index:2;box-shadow:0 0 0 3px var(--white)}
.tl-t{font-size:12px;font-weight:600;color:var(--text);line-height:1.4}
.tl-t span{color:var(--text3);font-weight:400}
.tl-d{font-size:10.5px;color:var(--text3);margin-top:2px}
.chklst{display:flex;flex-direction:column;gap:12px}
.chk-item{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;background:var(--bg);border-radius:10px}
.chk-l{display:flex;align-items:center;gap:10px}
.chk-ic{display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:var(--white);font-size:12px;box-shadow:var(--shadow)}

/* ── RESPONSIVE ── */
@media(max-width:900px){
  .kgrid{grid-template-columns:repeat(2,1fr)}
  .g2,.g14,.g31{grid-template-columns:1fr}
}
@media(max-width:768px){
  .side{display:none}
  .bnav{display:block}
  .content{padding:16px;padding-bottom:80px}
  .topbar{padding:0 16px;height:56px}
  .kgrid{grid-template-columns:1fr 1fr;gap:10px;margin-bottom:18px}
  .tbl th:nth-child(n+4),.tbl td:nth-child(n+4){display:none}
  .toast-wrap{bottom:76px;right:14px;left:14px}
  .toast{max-width:100%}
}
@media(max-width:480px){
  .kgrid{grid-template-columns:1fr 1fr}
  .soldes-grid{grid-template-columns:repeat(3,1fr)}
  .fgrid2{grid-template-columns:1fr}
  .modal{border-radius:var(--r16) var(--r16) 0 0;position:fixed;bottom:0;left:0;right:0;max-height:92vh;max-width:100%}
  .ov{align-items:flex-end;padding:0}
}

/* ── EXTRA: SaaS polish ── */
.hero-card{background:linear-gradient(160deg, #001A8C 0%, #0047FF 55%, #3366FF 100%);border-radius:var(--r16);padding:28px 30px;color:#fff;position:relative;overflow:hidden;margin-bottom:20px}
.hero-card::before{content:'';position:absolute;width:220px;height:220px;border-radius:50%;background:rgba(255,255,255,.06);right:-60px;top:-60px}
.hero-card::after{content:'';position:absolute;width:140px;height:140px;border-radius:50%;background:rgba(255,255,255,.04);left:-30px;bottom:-40px}
.hero-eyebrow{font-size:10px;font-weight:600;color:rgba(255,255,255,.5);letter-spacing:1px;text-transform:uppercase;margin-bottom:8px}
.hero-amount{font-size:36px;font-weight:800;color:#fff;letter-spacing:-1.5px;line-height:1}
.hero-sub{font-size:12px;color:rgba(255,255,255,.5);margin-top:6px}
.hero-chips{display:flex;gap:8px;margin-top:14px;flex-wrap:wrap;position:relative;z-index:1}
.hero-chip{background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.15);color:rgba(255,255,255,.85);font-size:11px;padding:4px 12px;border-radius:20px;font-weight:500}

.stat-row{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:20px}
.stat-box{background:var(--white);border:1px solid var(--border);border-radius:var(--r12);padding:16px 18px;box-shadow:var(--shadow);text-align:center}
.stat-val{font-size:30px;font-weight:800;color:var(--text);letter-spacing:-1px;line-height:1}
.stat-lbl{font-size:11px;color:var(--text3);margin-top:5px;font-weight:500}

.slip-item{display:flex;align-items:center;gap:14px;padding:14px 16px;border-radius:var(--r10);cursor:pointer;transition:all .12s;border:1px solid transparent}
.slip-item:hover{background:var(--blue-ll);border-color:var(--blue-l)}
.slip-month{font-size:13px;font-weight:600;color:var(--text)}
.slip-net{font-size:12px;color:var(--text3);margin-top:2px}
.slip-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}

.req-card{background:var(--white);border:1.5px solid var(--border);border-radius:var(--r12);padding:16px 18px;margin-bottom:10px;transition:all .12s;box-shadow:var(--shadow)}
.req-card:hover{border-color:var(--blue-l);box-shadow:0 2px 12px rgba(25,91,253,.08)}
.req-type{font-size:13px;font-weight:600;color:var(--text)}
.req-dates{font-size:12px;color:var(--text3);margin-top:3px;display:flex;align-items:center;gap:6px}
.req-days{background:var(--bg);color:var(--text2);font-size:11px;font-weight:600;padding:2px 8px;border-radius:20px}
.req-motif{font-size:11px;color:var(--text3);margin-top:6px;font-style:italic}
.req-just{display:inline-flex;align-items:center;gap:5px;font-size:11px;color:var(--blue);margin-top:6px;font-weight:500;padding:3px 10px;background:var(--blue-ll);border-radius:20px}

.profile-card{background:var(--white);border:1px solid var(--border);border-radius:var(--r12);overflow:hidden;box-shadow:var(--shadow);margin-bottom:16px}
.profile-cover{background:linear-gradient(135deg,#0D2680,var(--blue));height:80px;position:relative}
.profile-av-wrap{position:absolute;bottom:-24px;left:24px}
.profile-av-ring{width:60px;height:60px;border-radius:50%;border:3px solid #fff;background:var(--blue);display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#fff;box-shadow:0 2px 8px rgba(0,0,0,.15)}
.profile-info{padding:32px 24px 20px}
.profile-name{font-size:18px;font-weight:700;color:var(--text);letter-spacing:-.3px}
.profile-role{font-size:13px;color:var(--text3);margin-top:3px}

.ann-pill{display:inline-flex;align-items:center;gap:5px;font-size:10px;font-weight:700;padding:3px 10px;border-radius:20px;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px}
.ann-pill.info{background:var(--blue-l);color:var(--blue)}
.ann-pill.important{background:#FEF3C7;color:#92400E}
.ann-pill.urgent{background:var(--red-l);color:var(--red)}

.badge-status{display:inline-flex;align-items:center;gap:5px;padding:4px 12px;border-radius:20px;font-size:11px;font-weight:600;white-space:nowrap}
.bs-green{background:#D1FAE5;color:#065F46}
.bs-yellow{background:#FEF3C7;color:#92400E}
.bs-red{background:#FEE2E2;color:#DC2626}
.bs-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0}

.upload-pretty{display:flex;flex-direction:column;align-items:center;gap:8px;padding:22px;border:2px dashed var(--border2);border-radius:var(--r10);cursor:pointer;text-align:center;transition:all .15s;background:var(--bg)}
.upload-pretty:hover{border-color:var(--blue);background:var(--blue-ll)}
.upload-pretty.has-file{border-style:solid;border-color:var(--green);background:#F0FDF4}

.tab-bar{display:flex;gap:4px;background:var(--bg);padding:4px;border-radius:var(--r10);margin-bottom:20px}
.tab-btn{flex:1;padding:8px 12px;border-radius:8px;font-size:12px;font-weight:500;border:none;background:none;color:var(--text3);cursor:pointer;transition:all .12s;text-align:center}
.tab-btn.on{background:var(--white);color:var(--blue);font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,.08)}

.welcome-bar{background:var(--white);border:1px solid var(--border);border-radius:var(--r12);padding:16px 20px;margin-bottom:20px;display:flex;align-items:center;gap:14px;box-shadow:var(--shadow)}
.welcome-ic{width:42px;height:42px;border-radius:var(--r10);background:var(--blue-ll);display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}

.dash-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px}
.mini-kpi{background:var(--white);border:1px solid var(--border);border-radius:var(--r12);padding:18px;box-shadow:var(--shadow);display:flex;align-items:center;gap:14px}
.mki-icon{width:44px;height:44px;border-radius:var(--r10);display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}
.mki-val{font-size:22px;font-weight:800;color:var(--text);line-height:1;letter-spacing:-.5px}
.mki-lbl{font-size:11px;color:var(--text3);margin-top:3px}

@media(max-width:480px){.stat-row{grid-template-columns:repeat(3,1fr)}.dash-grid{grid-template-columns:1fr 1fr}}

/* ── MESSAGERIE ── */
.msg-layout{display:grid;grid-template-columns:260px 1fr;height:calc(100vh - 130px);gap:0;background:var(--white);border:1px solid var(--border);border-radius:var(--r12);overflow:hidden;box-shadow:var(--shadow)}
.msg-threads{border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden}
.msg-thread-head{padding:14px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between}
.msg-thread-title{font-size:13px;font-weight:700;color:var(--text)}
.msg-thread-item{display:flex;align-items:center;gap:10px;padding:12px 14px;cursor:pointer;border-bottom:1px solid var(--border);transition:background .1s;position:relative}
.msg-thread-item:hover{background:var(--bg)}
.msg-thread-item.active{background:var(--blue-ll);border-left:3px solid var(--blue)}
.msg-thread-name{font-size:13px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.msg-thread-preview{font-size:11px;color:var(--text3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px}
.msg-thread-time{font-size:10px;color:var(--text3);position:absolute;top:10px;right:10px}
.msg-thread-unread{width:8px;height:8px;border-radius:50%;background:var(--blue);flex-shrink:0}
.msg-chat{display:flex;flex-direction:column;overflow:hidden}
.msg-chat-head{padding:12px 18px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:12px;background:var(--white)}
.msg-chat-body{flex:1;overflow-y:auto;padding:16px 20px;display:flex;flex-direction:column;gap:10px;background:var(--bg)}
.msg-bubble-wrap{display:flex;gap:8px;align-items:flex-start}
.msg-bubble-wrap.mine{flex-direction:row-reverse}
.msg-bubble{max-width:100%;padding:10px 14px;border-radius:18px;font-size:14.5px;line-height:1.5;width:fit-content;display:inline-block;overflow-wrap:break-word;word-break:normal;white-space:pre-wrap;box-sizing:border-box;min-width:60px}
.msg-bubble.theirs{background:var(--white);color:var(--text);border:1px solid var(--border);border-radius:4px 18px 18px 18px}
.msg-bubble.mine{background:var(--blue);color:#fff;border-radius:18px 4px 18px 18px}
.msg-bubble-time{font-size:10px;color:var(--text3);margin-top:3px;text-align:right}
.msg-bubble-time.mine{text-align:right;color:rgba(255,255,255,.6)}
.msg-file-attach{display:flex;align-items:center;gap:8px;padding:8px 12px;background:rgba(255,255,255,.15);border-radius:8px;margin-top:6px;font-size:12px;cursor:pointer;text-decoration:none;color:inherit}
.msg-file-attach.theirs{background:var(--blue-ll);color:var(--blue)}
.msg-compose{padding:12px 16px;border-top:1px solid var(--border);display:flex;gap:10px;align-items:flex-end;background:var(--white)}
.msg-compose-input{flex:1;padding:10px 14px;border:1.5px solid var(--border);border-radius:10px;font-size:13px;outline:none;resize:none;min-height:40px;max-height:100px;font-family:inherit;transition:border .15s}
.msg-compose-input:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(25,91,253,.08)}
.msg-new-btn{background:var(--blue);color:#fff;border:none;border-radius:8px;padding:6px 12px;font-size:12px;cursor:pointer;font-weight:600;white-space:nowrap}
.msg-send-btn{background:var(--blue);color:#fff;border:none;border-radius:8px;padding:10px 14px;font-size:15px;cursor:pointer;flex-shrink:0}
.msg-send-btn:disabled{opacity:.4;cursor:default}
.msg-attach-btn{background:var(--bg);border:1.5px solid var(--border);border-radius:8px;padding:8px 10px;font-size:16px;cursor:pointer;flex-shrink:0;transition:all .12s}
.msg-attach-btn:hover{border-color:var(--blue);background:var(--blue-ll)}
.msg-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:12px;color:var(--text3)}
.msg-date-sep{text-align:center;font-size:11px;color:var(--text3);padding:4px 0;position:relative}
.msg-date-sep::before,.msg-date-sep::after{content:'';position:absolute;top:50%;width:35%;height:1px;background:var(--border)}
.msg-date-sep::before{left:0}.msg-date-sep::after{right:0}
@media(max-width:768px){.msg-layout{grid-template-columns:1fr}.msg-threads{display:none}.msg-layout.show-threads .msg-threads{display:flex}.msg-layout.show-threads .msg-chat{display:none}}

/* ── ALIASES (compatibilité composants) ── */
.fsel{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:var(--r8);font-size:13.5px;color:var(--text);outline:none;background:#fff}
.finp{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:var(--r8);font-size:13.5px;color:var(--text);outline:none;background:#fff;transition:border .15s}
.finp:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(25,91,253,.08)}
.flbl{display:block;font-size:11.5px;font-weight:500;color:var(--text2);margin-bottom:6px}
.fta{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:var(--r8);font-size:13.5px;color:var(--text);outline:none;resize:vertical;min-height:72px;background:#fff}
.fta:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(25,91,253,.08)}
.mh{padding:20px 22px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between}
.mb{padding:20px 22px}
.mf{padding:14px 22px;border-top:1px solid var(--border);display:flex;gap:10px;justify-content:flex-end}
.mt{font-size:16px;font-weight:700;color:var(--text);letter-spacing:-.2px}
.mx{background:none;border:none;font-size:20px;cursor:pointer;color:var(--text3);line-height:1;padding:2px;border-radius:6px;transition:all .12s}
.mx:hover{color:var(--text);background:var(--bg)}
.ritem{display:flex;align-items:flex-start;gap:12px;padding:12px 0;border-bottom:1px solid var(--border)}
.ritem:last-child{border-bottom:none}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.btn-xs{padding:4px 10px;font-size:11px;border-radius:6px}
.btn-sm{padding:6px 13px;font-size:12px}

.theme-france {
  --blue:#6366f1;
  --blue-d:#4f46e5;
  --blue-l:#e0e7ff;
  --blue-ll:#eef2ff;
}

`;

/* ─────────────────────────────────────
   PDF — BULLETIN DE PAIE SÉNÉGALAIS
───────────────────────────────────── */
async function genPDF(data) {
  const { employe, mois, annee, devise, horaire, categorie,
    salaire_base, sursalaire, indem_conges, prime_anciennete, prime_performance, autres_primes,
    indem_transport, indem_repas, avantages_nature, remb_pret, avance_salaire,
    retenue_frais_medicaux, frais_medicaux_ipm,
    calc, conges_acquis, conges_pris, conges_restants, matricule,
    type_contrat, pct_smic, nb_epouses, nb_enfants, parts_ir, parts_trimf,
    heures_absence } = data;

  const site = employe.site || data.site || "Dakar";
  const isXOF = devise === "XOF" || site !== "France";
  const cabinet = CABINET_INFO[site] || CABINET_INFO.Dakar;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, H = 297, ML = 10, MR = 10, CW = W - ML - MR;

  // Couleurs
  const BLUE = [0, 71, 255], NAVY = [12, 27, 58], GRAY = [100, 116, 139];
  const LGRAY = [241, 245, 249], LLGRAY = [248, 250, 252], BLACK = [15, 23, 42];
  const WHITE = [255, 255, 255], GREEN = [5, 150, 105], RED = [220, 38, 38];
  const sf = c => doc.setFillColor(...c);
  const sc = c => doc.setTextColor(...c);
  const sd = c => doc.setDrawColor(...c);
  const bf = (s, sz) => { doc.setFont("helvetica", s || "normal"); if (sz) doc.setFontSize(sz); };

  // Formatage sans espaces Unicode
  const fV = v => {
    if (!v && v !== 0) return "";
    const abs = Math.abs(Math.round(Number(v)));
    return (v < 0 ? "-" : "") + String(abs).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };
  const fVp = v => v ? fV(v) : ""; // vide si zéro

  const mNum = parseInt(mois), yr = parseInt(annee);
  const lastDay = new Date(yr, mNum, 0).getDate();
  const period = `01/${String(mNum).padStart(2, "0")}/${yr} AU ${lastDay}/${String(mNum).padStart(2, "0")}/${yr}`;

  const c = calc || {
    brut: n(salaire_base) + n(sursalaire) + n(indem_conges) + n(prime_anciennete),
    net: n(salaire_base) + n(sursalaire) + n(indem_conges) + n(prime_anciennete),
    ipres_sal: 0, irMensuel: 0, trimf: 0, ipres_pat: 0, prest_fam: 0, acc_trav: 0, cfce: 0, retAbs: 0
  };
  const sb = c.sb !== undefined ? c.sb : n(salaire_base);
  const sur = c.sur !== undefined ? c.sur : n(sursalaire);
  const ic = c.ic !== undefined ? c.ic : n(indem_conges);
  const pa = c.pa !== undefined ? c.pa : n(prime_anciennete);
  const pp = n(prime_performance), ap = n(autres_primes), an = n(avantages_nature);
  const it = n(indem_transport), irp = n(indem_repas);
  const remb = n(remb_pret), avance = n(avance_salaire);
  const fraisMed = n(retenue_frais_medicaux || frais_medicaux_ipm || (c && c.retenue_frais_medicaux));

  // La prime de performance n'est pas cotisable au Sénégal
  const base_imposable = isXOF ? (c.brut - n(data.prime_performance || 0)) : c.brut;
  const brut = c.brut || (sb + sur + ic + pa + pp + ap);
  const tauxH = c.tauxH !== undefined ? c.tauxH : (categorie && CONVENTION_COMMERCE[categorie]
    ? CONVENTION_COMMERCE[categorie].taux
    : (horaire && sb ? Math.round(sb / n(horaire) * 100) / 100 : 0)); // taux horaire
  const retAbsAbsence = c.retAbsAbsence || 0;
  const heuresAbs = n(heures_absence);

  // ══════════════════════════════════════
  // HEADER — cadre titrée
  // ══════════════════════════════════════
  // Bordure extérieure
  sd(BLACK); doc.setLineWidth(0.5); doc.rect(ML, 5, CW, H - 10, "S");
  // Ligne titre
  sd(BLACK); doc.setLineWidth(0.3); doc.line(ML, 18, ML + CW, 18);

  // BULLETIN DE PAIE centré
  sc(BLACK); bf("bold", 12);
  doc.text("BULLETIN DE PAIE", W / 2, 13, { align: "center" });
  bf("normal", 8);
  doc.text(`Periode du  :     ${period}`, W / 2 - 10, 17, { align: "center" });
  bf("bold", 8); doc.text(`Date  :  ${lastDay}/${String(mNum).padStart(2, "0")}/${yr}`, W - ML - 5, 17, { align: "right" });

  // ══ BLOC GAUCHE : infos cabinet ══
  let y = 20;
  sf(LGRAY); doc.rect(ML, y, CW * 0.42, 28, "F");
  sc(BLACK); bf("bold", 8.5);
  doc.text(cabinet.nom, ML + 6, y + 5);
  bf("normal", 7.5);
  doc.text(cabinet.adresse1, ML + 6, y + 10);
  doc.text(cabinet.adresse2, ML + 6, y + 15);
  bf("normal", 6.5); sc(GRAY);

  const logoData = await getOptimizedImageData('/logo-pdf.png', 180);

  if (logoData) {
    doc.addImage(logoData, 'PNG', ML + 6, y + 15, 13, 13, undefined, 'FAST');
  } else {
    sf(BLUE); doc.roundedRect(ML + 6, y + 22, 14, 8, 1, 1, "F");
    sc(WHITE); bf("bold", 7); doc.text("MAJHA", ML + 13, y + 27, { align: "center" });
  }

  // ══ BLOC MILIEU : infos employé ══
  const mx = ML + CW * 0.44;
  sd(BLACK); doc.setLineWidth(0.2); doc.line(mx, y, mx, y + 28);
  sc(BLACK); bf("bold", 8);
  doc.text("Prenoms et Nom", mx + 3, y + 5);
  bf("bold", 9);
  doc.text(employe.prenom + " " + employe.nom.toUpperCase(), mx + 3, y + 10);
  const addr = employe.adresse || "";
  if(addr) {
    bf("normal", 6.5); sc(GRAY);
    doc.text(addr, mx + 3, y + 14);
  }
  bf("normal", 7.5); sc(GRAY);
  // Sexe / Parts IR / Épouses / Parts TRIMF
  const infoL = mx + 3, infoR = mx + 28;
  const startY = addr ? y + 19 : y + 16;
  const rawGenre = String(employe.genre || employe.sexe || data.genre || "").trim().toLowerCase();
  const isFemale = rawGenre === "femme" || rawGenre === "f" || rawGenre === "female" || 
    (employe.prenom && ["habibatou", "habiba", "awa", "mame khady", "ngone", "jocelyne", "deguene", "bitylokho", "bethy", "maimouna"].some(fn => String(employe.prenom).toLowerCase().includes(fn)));
  const sexeAffiche = isFemale ? "F" : "H";
  
  const headerGrid = [
    ["Sexe", sexeAffiche],
    ["Parts IR", String(parts_ir || n(data.parts_fiscales) || 1)],
    [isFemale ? "Statut" : "Nb epouses", isFemale ? (employe.situation_matrimoniale ? (employe.situation_matrimoniale.includes("Mari") ? "Mariee" : employe.situation_matrimoniale) : "Mariee") : String(nb_epouses || 0)],
    ["Parts TRIMF", String(parts_trimf || 1)]
  ];

  headerGrid.forEach(([l, v], i) => {
    const iy = startY + Math.floor(i / 2) * 6;
    const ix = i % 2 === 0 ? infoL : infoR;
    sc(GRAY); bf("normal", 6.5); doc.text(l + " :", ix, iy);
    sc(BLACK); bf("bold", 7); doc.text(v, ix + doc.getTextWidth(l + " :") + 1, iy);
  });
  // C.S.S. / IPRES badges
  const badX = mx + CW * 0.26;
  sf(LGRAY); doc.rect(badX, y + 1, 16, 6, "F"); sd(GRAY); doc.setLineWidth(0.15); doc.rect(badX, y + 1, 16, 6, "S");
  sc(BLACK); bf("bold", 6.5); doc.text("C.S.S.", badX + 8, y + 5, { align: "center" });
  sf(LGRAY); doc.rect(badX + 17, y + 1, 16, 6, "F"); sd(GRAY); doc.rect(badX + 17, y + 1, 16, 6, "S");
  doc.text("IPRES", badX + 25, y + 5, { align: "center" });
  // Convention
  sc(GRAY); bf("normal", 7); doc.text("Commerce", badX, y + 13);

  y += 30;
  sd(BLACK); doc.setLineWidth(0.3); doc.line(ML, y, ML + CW, y);

  // ══ LIGNE : emploi / date / enfants / ancienneté / horaire ══
  y += 1;
  const cols = [ML + 2, ML + 40, ML + 82, ML + 112, ML + 140, ML + 168];
  sc(GRAY); bf("normal", 6.5);
  ["EMPLOI OCCUPE", "DATE EMB.", "Nb. ENFANTS", "ANCIENNETE", "HORAIRE"].forEach((h, i) => {
    doc.text(h, cols[i + 1], y + 3);
  });
  y += 5;
  sc(BLACK); bf("bold", 7.5);
  const convLabel = isXOF ? "Commerce" : "Syntec";
  doc.text("Convention collective :", ML + 2, y + 2); bf("normal", 7.5); doc.text(convLabel, ML + 34, y + 2);
  y += 4;
  bf("bold", 8.5); doc.text(formatPoste(employe.poste, employe), cols[1], y + 2);
  bf("normal", 7.5);
  doc.text(employe.date_entree ? new Date(employe.date_entree).toLocaleDateString("fr-FR") : "—", cols[2], y + 2);
  doc.text(String(nb_enfants || 0), cols[3] + 5, y + 2);
  doc.text("", cols[4] + 5, y + 2); // ancienneté calculée
  doc.text(String(horaire || "173,33"), cols[5], y + 2);
  y += 5;
  // Date de naissance + lieu
  sc(GRAY); bf("normal", 6.5);
  doc.text("Date de naissance : " + String(employe.date_naissance || "—") + "     Lieu de naissance : " + String(employe.ville_naissance || "DAKAR"), ML + 2, y + 2);
  // Catégorie + Site
  bf("bold", 7.5); sc(BLACK);
  doc.text("Cat\u00e9gorie : " + String(categorie || "8 A") + "        Site : " + String(site).toUpperCase(), cols[3], y + 2);
  y += 6;

  sd(BLACK); doc.setLineWidth(0.3); doc.line(ML, y, ML + CW, y);

  // ══════════════════════════════════════
  // TABLEAU DE PAIE
  // ══════════════════════════════════════
  // Colonnes : N° | DESIGNATION | NOMBRE | BASE | SAL TAUX | SAL GAIN | SAL RET | PAT TAUX | PAT GAIN | PAT RET
  // Table dimensions with internal padding to avoid touching the outer border
  const tL = ML + 1.5; 
  const tW = CW - 3;
  const C = {
    n: tL, nW: 8,
    d: tL + 9, dW: 50,
    nb: tL + 60, nbW: 14,
    ba: tL + 75, baW: 18,
    st: tL + 94, stW: 12,
    sg: tL + 107, sgW: 20,
    sr: tL + 128, srW: 20,
    pt: tL + 149, ptW: 12,
    pr: tL + 162, prW: tW - 162 - 2,
  };
  const TH = 7;

  // Header tableau
  sf(NAVY); doc.rect(tL, y, tW, TH, "F");
  sc(WHITE); bf("bold", 6);
  doc.text("N", C.n + 1, y + 4.5);
  doc.text("DESIGNATION", C.d + 1, y + 4.5);
  doc.text("NOMBRE", C.nb + C.nbW, y + 4.5, { align: "right" });
  doc.text("BASE", C.ba + C.baW, y + 4.5, { align: "right" });

  sc([180, 200, 255]); bf("normal", 5);
  doc.text("SALARIAL (Retenues)", C.st + 16, y + 2.5, { align: "center" });
  doc.text("PATRONAL (Retenues)", C.pt + 10, y + 2.5, { align: "center" });

  sc(WHITE); bf("bold", 5.5);
  doc.text("TAUX", C.st + C.stW, y + 6, { align: "right" });
  doc.text("GAINS", C.sg + C.sgW, y + 6, { align: "right" });
  doc.text("RETENUES", C.sr + C.srW, y + 6, { align: "right" });

  doc.text("TAUX", C.pt + C.ptW, y + 6, { align: "right" });
  doc.text("RETENUES", C.pr + C.prW, y + 6, { align: "right" });

  y += TH;
  let ri = 0;
  const RH = 5.5;
  const tableTop = y;

  const row = (ref, label, nombre, base, tSal, gSal, rSal, tPat, gPat, rPat, bold, sub) => {
    const ry = y + ri * RH;
    if (bold) sf([220, 230, 255]);
    else if (sub) sf([250, 252, 255]);
    else sf(ri % 2 === 0 ? LLGRAY : WHITE);
    doc.rect(tL, ry, tW, RH, "F");
    sd([200, 215, 235]); doc.setLineWidth(0.07); doc.line(tL, ry + RH, tL + tW, ry + RH);
    const ty = ry + RH - 1.3;
    bf(bold ? "bold" : "normal", bold ? 6.5 : 6);
    sc(bold ? BLUE : GRAY); if (ref) doc.text(String(ref), C.n + 1, ty);
    sc(bold ? BLUE : BLACK); doc.text(String(label), C.d + 1, ty);
    sc(GRAY); bf("normal", 6);
    if (nombre) doc.text(String(nombre), C.nb + C.nbW, ty, { align: "right" });
    if (base) doc.text(String(base), C.ba + C.baW, ty, { align: "right" });
    if (tSal) doc.text(String(tSal), C.st + C.stW, ty, { align: "right" });
    if (gSal) { sc(GREEN); bf("bold", 6); doc.text(String(gSal), C.sg + C.sgW, ty, { align: "right" }); }
    sc(RED); if (rSal) { bf("bold", 6); doc.text(String(rSal), C.sr + C.srW, ty, { align: "right" }); }
    sc(GRAY); bf("normal", 6);
    if (tPat) doc.text(String(tPat), C.pt + C.ptW, ty, { align: "right" });
    if (rPat) { sc(RED); bf("bold", 6); doc.text(String(rPat), C.pr + C.prW, ty, { align: "right" }); }
    ri++;
  };

  // ── 150 Salaire de base : NOMBRE=heures, BASE=taux horaire, GAIN=montant ──
  row("150", "Salaire de base",
    horaire ? fV(n(horaire)) : "",
    tauxH ? fV(tauxH) : "",
    "", fVp(sb), "", "", "", "");
  // ── 155 Sursalaire : GAIN salarial ──
  if (sur) row("155", "Sursalaire", "", "", "", fVp(sur), "", "", "", "");
  // ── 156 Indemnités de congés : GAIN salarial imposable (juste après Sursalaire, avant Total Brut) ──
  if (ic) row("156", "Indemnites de conges", "", "", "", fVp(ic), "", "", "", "");
  // ── Retenue pour absence ──
  if (heuresAbs) row("157", "Retenue pour absence", fV(heuresAbs), fV(tauxH), "", "", fVp(retAbsAbsence), "", "", "");
  // ── 160 Prime d'ancienneté : GAIN salarial ──
  if (pa) row("160", "Prime Anciennete", "", "", "", fVp(pa), "", "", "", "");
  // ── TOTAL BRUT : BASE=brut (ligne totalisatrice) ──
  const total_brut_affiche = sb + sur + ic + pa + ap - retAbsAbsence;
  row("", "TOTAL BRUT", "", fVp(total_brut_affiche), "", "", "", "", "", "", true);
  // ── 175 Autres primes : GAIN salarial ──
  if (ap) row("175", "Autres Primes", "", "", "", fVp(ap), "", "", "", "");
  // ── 180 Avantages en nature : GAIN salarial ──
  if (an) row("180", "Avantages en nature", "", "", "", fVp(an), "", "", "", "");

  if (isXOF) {
    // ── 185 IPRES RG — base = brut imposable (sans prime perf) ──
    row("185", "Retenue Ipres Regime General",
      "", fVp(c.brut_plafIPRES || base_imposable),
      "5,60%", "", fVp(c.ipres_sal),
      "8,40%", "", fVp(c.ipres_pat));
    if (c.ipres_comp_sal)
      row("186", "IPRES Complementaire cadres",
        "", fVp(c.tranche_cadre), "2,40%", "", fVp(c.ipres_comp_sal), "3,60%", "", fVp(c.ipres_comp_pat || 0));
    // ── 195 Prestation familiale ──
    row("195", "Prestation familliale",
      "", fVp(c.brut_plafCSS || 63000), "0", "", "", "7%", "", fVp(c.prest_fam));
    // ── 200 AT ──
    row("200", "Accident de travail",
      "", fVp(c.brut_plafCSS || 63000), "0", "", "", "1%", "", fVp(c.acc_trav));
    // ── 205 CFCE ──
    row("205", "CFCE",
      "", fVp(base_imposable), "0", "", "", "3%", "", fVp(c.cfce));
    // ── 210 IR (Impôt sur le Revenu) ──
    if (c.irMensuel)
      row("210", "Retenue IR", "", fVp(base_imposable), "", "", fVp(c.irMensuel), "", "", "");
    // ── 215 TRIMF ──
    if (c.trimf)
      row("215", "TRIMF", "", "", "", "", fVp(c.trimf), "", "", "");
    // ── 220 IPM ──
    if (c.ipm_sal || c.ipm_pat)
      row("220", "IPM / Assurance Maladie", "", "", "", "", fVp(c.ipm_sal), "Forfait", "", fVp(c.ipm_pat));
    // ── 221 Retenue Frais Médicaux (Ticket modérateur 20% IPM) ──
    if (fraisMed)
      row("221", "Retenue Frais Medicaux (IPM)", "", "", "", "", fVp(fraisMed), "", "", "");
    // ── Retenue absences ──
    if (c.retAbs)
      row("225", "Retenue absences injust.", "", "", "", "", fVp(c.retAbs), "", "", "");
    // ── 226 Avance / Salaire ──
    if (avance)
      row("226", "Avance / Salaire", "", "", "", "", fVp(avance), "", "", "");
    // ── 240 Transport : GAIN non imposable ──
    if (it) row("240", "Indemnite de transport", "", "", "", fVp(it), "", "", "", "");
    // ── 245 Repas : GAIN non imposable ──
    if (irp) row("245", "Indemnite repas", "", "", "", fVp(irp), "", "", "", "");
    // ── 250 Prime de Performance : GAIN non imposable/non cotisable ──
    if (pp) row("250", "Prime de Performance", "", "", "", fVp(pp), "", "", "", "");
    // ── Appoint ──
    row("", "Appoint du mois encours", "", "", "", "0", "", "", "", "");
  } else {
    // France
    if (c.csg_crds) {
      row("310", "CSG non-deductible (2,4%)",
        "", fVp(Math.round(brut * 0.9825)), "2,40%", "", fVp(Math.round(brut * 0.9825 * 0.024)), "", "", "");
      row("315", "CSG deductible + CRDS (7,3%)",
        "", fVp(Math.round(brut * 0.9825)), "7,30%", "", fVp(Math.round(brut * 0.9825 * 0.073)), "", "", "");
    }
    if (c.retraite_sal)
      row("320", "Retraite base CNAV",
        "", fVp(Math.min(brut, 3864)), "6,90%", "", fVp(Math.round(Math.min(brut, 3864) * 0.069)),
        "8,55%", "", fVp(Math.round(Math.min(brut, 3864) * 0.0855)));
    if (c.retraite_sal)
      row("325", "Retraite compl. ARRCO/AGIRC",
        "", fVp(Math.min(brut, 3864)), "3,15%", "", fVp(Math.round(Math.min(brut, 3864) * 0.0315)),
        "4,72%", "", fVp(Math.round(Math.min(brut, 3864) * 0.0472)));
    row("330", "Assurance maladie", "", "", "0%", "", "", "7,00%", "", fVp(Math.round(brut * 0.07)));
    row("335", "Assurance chomage", "", "", "0%", "", "", "4,05%", "", fVp(Math.round(brut * 0.0405)));
    if (c.prevoyance_sal)
      row("340", "Prevoyance",
        "", fVp(brut), "1,50%", "", fVp(c.prevoyance_sal), "2,25%", "", fVp(c.prevoyance_pat || 0));
    if (c.retAbs)
      row("210", "Retenue absences", "", "", "", "", fVp(c.retAbs), "", "", "");
    if (it) row("240", "Indemnite transport", "", "", "", fVp(it), "", "", "", "");
    if (irp) row("245", "Indemnite repas", "", "", "", fVp(irp), "", "", "", "");
    if (pp) row("250", "Prime de performance", "", "", "", fVp(pp), "", "", "", "");
  }

  // Bordure tableau + lignes verticales
  const tableH = ri * RH;
  sd(NAVY); doc.setLineWidth(0.35); doc.rect(tL, tableTop - TH, tW, TH + tableH, "S");
  [C.d - 1, C.nb - 1, C.ba - 1, C.st - 1, C.sg - 1, C.sr - 1, C.pt - 1, C.pr - 1].forEach(x => {
    sd([200, 215, 235]); doc.setLineWidth(0.08); doc.line(x, tableTop - TH, x, tableTop + tableH);
  });

  y += tableH + 3;
  sd(BLACK); doc.setLineWidth(0.3); doc.line(ML, y, ML + CW, y);
  y += 2;

  // ══════════════════════════════════════
  // PIED : MONTANTS | NET | CONGÉS
  // ══════════════════════════════════════
  const PH = 38;
  // NET À PAYER (centré)
  const netStr = fV(c.net || n(data.salaire_net) || 0);
  const netW = 56, netX = W / 2 - netW / 2;
  sf(BLUE); doc.roundedRect(netX, y, netW, PH, 2, 2, "F");
  sc(WHITE); bf("bold", 7.5); doc.text("NET A PAYER", netX + netW / 2, y + 8, { align: "center" });
  bf("bold", 13);
  let nfsz = 13;
  bf("bold", nfsz);
  while (doc.getTextWidth(netStr) > netW - 6 && nfsz > 8) { nfsz -= 0.5; bf("bold", nfsz); }
  doc.text(netStr, netX + netW / 2, y + 20, { align: "center" });
  sc([200, 220, 255]); bf("normal", 6);
  doc.text(isXOF ? "FCFA" : "EUR", netX + netW / 2, y + 27, { align: "center" });
  sc([180, 210, 255]); bf("normal", 5.5); doc.text("Reglement :", netX + netW / 2, y + 33, { align: "center" });

  // MONTANTS DE LA PERIODE (gauche)
  const moX = ML + 1, moW = netX - ML - 3;
  sc(BLACK); bf("bold", 6.5); doc.text("MONTANTS DE LA PERIODE", moX, y + 4);
  bf("normal", 5.5); sc(GRAY); doc.text("DE L'ANNEE", moX + moW - 2, y + 4, { align: "right" });
  const moRows = [
    ["Heures travaillees", fV(n(horaire)), fV(n(horaire))],
    ["Salaire brut imposable", fV(isXOF ? base_imposable : brut), ""],
    ["Charges salariales", fV((c.ipres_sal || 0) + (c.ipres_comp_sal || 0) + (c.irMensuel || 0) + (c.trimf || 0) + (c.ipm_sal || 0) + (fraisMed || 0) + (c.csg_crds || 0) + (c.retraite_sal || 0) + (c.prevoyance_sal || 0)), ""],
    ["Charges patronales", fV((c.ipres_pat || 0) + (c.ipres_comp_pat || 0) + (c.prest_fam || 0) + (c.acc_trav || 0) + (c.cfce || 0) + (c.ipm_pat || 0) + (c.maladie_pat || 0) + (c.chomage_pat || 0)), ""],
    ["Total salaire", fV((brut || 0) + (it || 0)), ""],
    ["Avantages en nature", fV(an), "0"],
  ];
  moRows.forEach(([l, v, ann], i) => {
    const ry = y + 8 + i * 5;
    sc(GRAY); bf("normal", 5.5); doc.text(l, moX, ry);
    sc(BLACK); bf("bold", 6); doc.text(String(v), moX + moW * 0.55, ry, { align: "right" });
    if (ann !== undefined) { sc(GRAY); bf("normal", 5.5); doc.text(String(ann), moX + moW - 2, ry, { align: "right" }); }
  });

  // CONGÉS (droite)
  const cgX = netX + netW + 3, cgW = W - MR - cgX;
  sc(BLACK); bf("bold", 6.5); doc.text("CONGES", cgX, y + 4);
  [["Cumul congés", String(conges_acquis || 0)],
  ["Acquis année", String(conges_pris || 0)],
  ["Reste à prendre", String(conges_restants || 0)],
  ["Du", ""], ["Au", ""], ["CP pris sur le mois", ""]].forEach(([l, v], i) => {
    const ry = y + 8 + i * 5;
    sc(GRAY); bf("normal", 5.5); doc.text(l + " :", cgX, ry);
    sc(BLACK); bf("bold", 6); doc.text(v, cgX + cgW - 2, ry, { align: "right" });
  });
  sc(GRAY); bf("normal", 5.5); doc.text("Cumul Heures sup. :", cgX, y + 8 + 6 * 5);

  y += PH + 3;
  
  // To avoid leaving a huge blank space that looks weird, 
  // we push the footer to the absolute bottom of the page.
  y = (doc.internal.pageSize.getHeight ? doc.internal.pageSize.getHeight() : doc.internal.pageSize.height) - 15;

  // ══ FOOTER ══
  sd(NAVY); doc.setLineWidth(0.3); doc.line(ML, y, ML + CW, y);
  
  try {
    const stampBase64 = await getOptimizedImageData('/cachet-pdf.png', 300);
    if (stampBase64) {
      const stampSize = 55; 
      const stampX = W - stampSize - 15;
      const stampY = y - stampSize + 15; // overlap slightly with footer
      doc.addImage(stampBase64, 'PNG', stampX, stampY, stampSize, stampSize, undefined, 'FAST');
    }
  } catch (e) {
    // silently fail if cachet.png is missing
  }

  sc(GRAY); bf("normal", 5.5);
  doc.text("Ce bulletin de salaire doit etre conserve sans limitation de duree  |  Document confidentiel  |  " + cabinet.nom + "  |  DAKAR-MERMOZ, Rue MZ-70, VILLA N 13", W / 2, y + 4, { align: "center" });

  const nom = ("Bulletin_" + employe.prenom + "_" + employe.nom + "_" + yr + "_" + String(mNum).padStart(2, "0") + ".pdf")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9._-]/g, "_");

  // Redraw outer border to ensure it cleanly cuts off any inner backgrounds
  sd(BLACK); doc.setLineWidth(0.5); doc.rect(ML, 5, CW, H - 10, "S");

  return { doc, nom };
}
const Av = ({ initials, color, size = 34, fs }) => (
  <div className="av" style={{ width: size, height: size, background: color || "#0047FF", fontSize: fs || Math.round(size * 0.33) }}>{initials || "?"}</div>
);

/* ─────────────────────────────────────
   STATUS BADGE
───────────────────────────────────── */
const StatBdg = ({ s }) => (
  <span className={"badge " + (s === "approuve" ? "b-green" : s === "refuse" ? "b-red" : "b-yellow")}>
    {s === "approuve" ? "✓ Validé" : s === "refuse" ? "✗ Refusé" : "⏳ En attente"}
  </span>
);

/* ═══════════════════════════════════════════════════════
   ESPACE EMPLOYÉ
═══════════════════════════════════════════════════════ */

function EmployeApp({ user, onLogout }) {
  const [localUser, setLocalUser] = useState(user);
  const [tab, setTab] = useState("accueil");
  const [demandesTab, setDemandesTab] = useState("con");
  const [fiches, setFiches] = useState([]);
  const [docs, setDocs] = useState([]);
  const [conges, setConges] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef(null);
  const tabRef = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  useEffect(() => {
    const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);
  const [employes, setEmployes] = useState([]); // pour la messagerie
  const [msgUnread, setMsgUnread] = useState(0); // badge messagerie
  const [loading, setLoading] = useState(true);
  const [unreadBulletins, setUnreadBulletins] = useState(0);
  const [unreadConges, setUnreadConges] = useState(0);
  const [unreadAbsences, setUnreadAbsences] = useState(0);
  const [unreadAnnonces, setUnreadAnnonces] = useState(0);
  const [myThreads, setMyThreads] = useState([]); // Pour filtrer les notifs messages
  const [showConge, setShowConge] = useState(false);
  const [showAbs, setShowAbs] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [form, setForm] = useState({ type_conge: "Congés payés", type_absence: "Maladie" });
  const [saving, setSaving] = useState(false);
  const absFileRef = useRef(null);

  const openConge = () => { setTab("demandes"); setDemandesTab("con"); setForm(f => ({ ...f, type_conge: "Congés payés" })); setShowConge(true); };
  const openAbs = () => { setTab("demandes"); setDemandesTab("abs"); setForm(f => ({ ...f, type_absence: "Maladie" })); setShowAbs(true); };

  useEffect(() => {
    Promise.all([
      supabase.from("fiches_paie").select("*").eq("employe_id", user.id).order("annee", { ascending: false }).order("mois", { ascending: false }),
      supabase.from("documents_rh").select("*").eq("employe_id", user.id).order("uploaded_at", { ascending: false }),
      supabase.from("conges").select("*").eq("employe_id", user.id).order("created_at", { ascending: false }),
      supabase.from("absences").select("*").eq("employe_id", user.id).order("created_at", { ascending: false }),
      supabase.from("annonces").select("*").order("created_at", { ascending: false }),
      supabase.from("employes").select("id,prenom,nom,poste,site,avatar_initials,avatar_color,actif").eq("actif", true).order("prenom"),
    ]).then(([f, d, c, a, ann, emp]) => {
      setFiches((f.data || []).filter(isBulletinPublie)); setDocs(d.data || []); setConges(c.data || []);
      setAbsences(a.data || []); setAnnonces(ann.data || []);
      setEmployes(emp.data || []);
      supabase.from("threads").select("id").contains("participants", [user.id]).then(res => {
        if (res.data) setMyThreads(res.data.map(t => t.id));
      });
      supabase.from("notifications").select("*").eq("employe_id", user.id).order("created_at", { ascending: false }).then(res => {
        if (res.data) setNotifs(res.data);
      });
      setLoading(false);
    });
  }, [user.id]);

  // Au chargement : calculer les messages non lus depuis la dernière visite messagerie
  useEffect(() => {
    const lastSeen = localStorage.getItem("majha_msg_last_seen_" + user.id);
    if (!lastSeen) return; // première utilisation, pas de référence
    supabase.from("threads").select("id").contains("participants", [user.id]).then(res => {
      if (!res.data || res.data.length === 0) return;
      const ids = res.data.map(t => t.id);
      supabase.from("messages").select("id").in("thread_id", ids).neq("sender_id", user.id).gt("created_at", lastSeen).then(r => {
        if (r.data && r.data.length > 0) setMsgUnread(r.data.length);
      });
    });
  }, [user.id]);

  // Logique de calcul des badges (localStorage)
  useEffect(() => {
    const seenId = localStorage.getItem("majha_seen_bulletin_" + user.id) || "";
    const newBulletins = fiches.filter(f => f.id !== seenId).length > 0 && fiches[0]?.id !== seenId ? 1 : 0;
    setUnreadBulletins(newBulletins);
  }, [fiches, user.id]);

  useEffect(() => {
    const seen = JSON.parse(localStorage.getItem("majha_seen_conges_" + user.id) || "[]");
    const changed = conges.filter(c => (c.statut === "approuve" || c.statut === "refuse") && !seen.includes(c.id)).length;
    setUnreadConges(changed);
  }, [conges, user.id]);

  useEffect(() => {
    const seen = JSON.parse(localStorage.getItem("majha_seen_absences_" + user.id) || "[]");
    const changed = absences.filter(a => (a.statut === "approuve" || a.statut === "refuse") && !seen.includes(a.id)).length;
    setUnreadAbsences(changed);
  }, [absences, user.id]);

  useEffect(() => {
    const seenCount = parseInt(localStorage.getItem("majha_seen_annonces_" + user.id) || "0");
    setUnreadAnnonces(Math.max(0, annonces.length - seenCount));
  }, [annonces, user.id]);

  const handleTabClick = (tabId) => {
    setTab(tabId);
    if (tabId === "fiches") {
      setUnreadBulletins(0);
      if (fiches[0]) localStorage.setItem("majha_seen_bulletin_" + user.id, fiches[0].id);
    }
    if (tabId === "demandes") {
      setUnreadConges(0);
      setUnreadAbsences(0);
      const seenC = conges.filter(c => c.statut === "approuve" || c.statut === "refuse").map(c => c.id);
      const seenA = absences.filter(a => a.statut === "approuve" || a.statut === "refuse").map(a => a.id);
      localStorage.setItem("majha_seen_conges_" + user.id, JSON.stringify(seenC));
      localStorage.setItem("majha_seen_absences_" + user.id, JSON.stringify(seenA));
    }
    if (tabId === "annonces") {
      setUnreadAnnonces(0);
      localStorage.setItem("majha_seen_annonces_" + user.id, String(annonces.length));
    }
    if (tabId === "messagerie") {
      setMsgUnread(0);
      // Sauvegarder le timestamp de la visite pour persister entre rechargements
      localStorage.setItem("majha_msg_last_seen_" + user.id, new Date().toISOString());
    }
  };

  useEffect(() => {
    const ch1 = supabase.channel("emp-c-" + user.id).on("postgres_changes", { event: "UPDATE", schema: "public", table: "conges", filter: `employe_id=eq.${user.id}` }, (p) => {
      setConges(prev => prev.map(c => c.id === p.new.id ? { ...c, ...p.new } : c));
      if (p.new.statut === "approuve" || p.new.statut === "refuse") setUnreadConges(v => v + 1);
    }).subscribe();
    const ch2 = supabase.channel("emp-a-" + user.id).on("postgres_changes", { event: "UPDATE", schema: "public", table: "absences", filter: `employe_id=eq.${user.id}` }, (p) => {
      setAbsences(prev => prev.map(a => a.id === p.new.id ? { ...a, ...p.new } : a));
      if (p.new.statut === "approuve" || p.new.statut === "refuse") setUnreadAbsences(v => v + 1);
    }).subscribe();
    const ch3 = supabase.channel("msg-badge-emp-" + user.id)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (p) => {
        if (p.new.sender_id === user.id) return;
        if (tabRef.current !== "messagerie") setMsgUnread(prev => prev + 1);
      }).subscribe();
    const chF = supabase.channel("emp-bulletin-" + user.id).on("postgres_changes", { event: "*", schema: "public", table: "fiches_paie", filter: `employe_id=eq.${user.id}` }, (p) => {
      if (p.eventType === "INSERT") {
        if (isBulletinPublie(p.new)) {
          setFiches(prev => [p.new, ...prev.filter(x => x.id !== p.new.id)]);
          setUnreadBulletins(v => v + 1);
        }
      } else if (p.eventType === "UPDATE") {
        if (isBulletinPublie(p.new)) {
          setFiches(prev => {
            const exists = prev.some(x => x.id === p.new.id);
            return exists ? prev.map(x => x.id === p.new.id ? p.new : x) : [p.new, ...prev];
          });
          setUnreadBulletins(v => v + 1);
        } else {
          setFiches(prev => prev.filter(x => x.id !== p.new.id));
        }
      } else if (p.eventType === "DELETE") {
        setFiches(prev => prev.filter(x => x.id !== p.old.id));
      }
    }).subscribe();
    const chAnn = supabase.channel("emp-annonces-global").on("postgres_changes", { event: "INSERT", schema: "public", table: "annonces" }, (p) => {
      setAnnonces(prev => [p.new, ...prev]);
      setUnreadAnnonces(v => v + 1);
    }).subscribe();
    const chN = supabase.channel("emp-notifs-" + user.id).on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `employe_id=eq.${user.id}` }, (p) => {
      setNotifs(prev => [p.new, ...prev]);
    }).on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `employe_id=eq.${user.id}` }, (p) => {
      setNotifs(prev => prev.map(n => n.id === p.new.id ? { ...n, ...p.new } : n));
    }).subscribe();
    // Listener pour le profil (pour les ajustements de solde congés en temps réel)
    const chProfile = supabase.channel("emp-profile-" + user.id).on("postgres_changes", { event: "UPDATE", schema: "public", table: "employes", filter: `id=eq.${user.id}` }, (p) => {
      setLocalUser(p.new);
    }).subscribe();
    return () => { supabase.removeChannel(ch1); supabase.removeChannel(ch2); supabase.removeChannel(ch3); supabase.removeChannel(chF); supabase.removeChannel(chAnn); supabase.removeChannel(chN); supabase.removeChannel(chProfile); };
  }, [user.id]);

  // Polling pour le badge messagerie — utilise localStorage comme point de départ
  useEffect(() => {
    // Point de départ = dernière visite messagerie (ou il y a 24h par défaut)
    const stored = localStorage.getItem("majha_msg_last_seen_" + user.id);
    const lastCheckRef = { current: stored || new Date(Date.now() - 86400000).toISOString() };
    const poll = setInterval(async () => {
      if (tabRef.current === "messagerie") {
        // Si on est sur messagerie, mettre à jour le timestamp
        lastCheckRef.current = new Date().toISOString();
        localStorage.setItem("majha_msg_last_seen_" + user.id, lastCheckRef.current);
        return;
      }
      const { data: threads } = await supabase.from("threads").select("id").contains("participants", [user.id]);
      if (!threads || threads.length === 0) return;
      const ids = threads.map(t => t.id);
      const { data: msgs } = await supabase.from("messages").select("id").in("thread_id", ids).neq("sender_id", user.id).gt("created_at", lastCheckRef.current);
      if (msgs && msgs.length > 0) setMsgUnread(msgs.length); // absolu, pas relatif
      lastCheckRef.current = new Date().toISOString();
    }, 8000);
    return () => clearInterval(poll);
  }, [user.id]);

  const derniere = fiches[0];
  const leaveRes = calculateLeaveBalance(localUser, conges);
  const cAcquis = leaveRes.total;
  const cPris = leaveRes.pris;
  const cSolde = leaveRes.solde;
  const pendingConges = conges.filter(c => c.statut === "en_attente").length;
  const pendingAbs = absences.filter(a => a.statut === "en_attente").length;
  const totalAbsJours = absences.filter(a => a.statut === "approuve").reduce((s, a) => s + (a.nb_jours || 0), 0);
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";

  const StatusBadge = ({ s }) => {
    const cfg = { approuve: { cls: "bs-green", dot: "#059669", label: "Approuvé" }, refuse: { cls: "bs-red", dot: "#DC2626", label: "Refusé" }, en_attente: { cls: "bs-yellow", dot: "#D97706", label: "En attente" } };
    const c = cfg[s] || cfg.en_attente;
    return <span className={"badge-status " + c.cls}><span className="bs-dot" style={{ background: c.dot }} />{c.label}</span>;
  };

  const saveConge = async () => {
    if (!form.date_debut || !form.date_fin || !form.type_conge) { return; }
    setSaving(true);
    const { data } = await supabase.from("conges").insert({
      employe_id: user.id,
      type_conge: form.type_conge,
      date_debut: form.date_debut,
      date_fin: form.date_fin,
      nb_jours: countJoursOuvrables(form.date_debut, form.date_fin),
      motif: form.motif || null,
      statut: "en_attente"
    }).select().single();
    if (data) setConges(p => [data, ...p]);
    setSaving(false); setShowConge(false);
    setForm({ type_conge: "Congés payés", type_absence: "Maladie" }); // FIX reset propre
  };

  const saveAbs = async () => {
    if (!form.date_debut || !form.date_fin || !form.type_absence) { return; }
    setSaving(true);
    let justificatif_url = null;
    const file = absFileRef.current?.files?.[0];
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      const path = `absences/${user.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("Documents").upload(path, file, { upsert: true });
      if (!upErr) { const { data: u } = supabase.storage.from("Documents").getPublicUrl(path); justificatif_url = u.publicUrl; }
    }
    const { data } = await supabase.from("absences").insert({
      employe_id: user.id,
      type_absence: form.type_absence,
      date_debut: form.date_debut,
      date_fin: form.date_fin,
      nb_jours: countJoursOuvrables(form.date_debut, form.date_fin),
      motif: form.motif || null,
      statut: "en_attente",
      justificatif_url
    }).select().single();
    if (data) setAbsences(p => [data, ...p]);
    try { if (absFileRef.current) absFileRef.current.value = ""; } catch { }
    setSaving(false); setShowAbs(false);
    setForm({ type_conge: "Congés payés", type_absence: "Maladie" }); // FIX reset propre
  };

  // ── GESTION MOT DE PASSE (PROFIL) ──
  const [pwdForm, setPwdForm] = useState({ current: "", newPwd: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null); // { type: "success" | "error", text: string }
  const [showPwdSection, setShowPwdSection] = useState(false);

  const changerMotDePasse = async (e) => {
    if (e) e.preventDefault();
    setPwdMsg(null);

    if (!pwdForm.current || !pwdForm.newPwd || !pwdForm.confirm) {
      setPwdMsg({ type: "error", text: "Veuillez remplir tous les champs du mot de passe." });
      return;
    }

    if (pwdForm.newPwd.length < 4) {
      setPwdMsg({ type: "error", text: "Le nouveau mot de passe doit comporter au moins 4 caractères." });
      return;
    }

    if (pwdForm.newPwd !== pwdForm.confirm) {
      setPwdMsg({ type: "error", text: "Les deux nouveaux mots de passe ne correspondent pas." });
      return;
    }

    setPwdSaving(true);
    try {
      // Vérification du mot de passe actuel
      const { data: empData, error: fetchErr } = await supabase
        .from("employes")
        .select("password")
        .eq("id", user.id)
        .single();

      if (fetchErr || !empData) {
        setPwdMsg({ type: "error", text: "Impossible de vérifier vos informations actuelles." });
        setPwdSaving(false);
        return;
      }

      if (empData.password && empData.password !== pwdForm.current) {
        setPwdMsg({ type: "error", text: "Votre mot de passe actuel est incorrect." });
        setPwdSaving(false);
        return;
      }

      // Mise à jour du nouveau mot de passe
      const { error: updErr } = await supabase
        .from("employes")
        .update({ password: pwdForm.newPwd })
        .eq("id", user.id);

      if (updErr) {
        setPwdMsg({ type: "error", text: "Erreur lors de la mise à jour: " + updErr.message });
      } else {
        setPwdMsg({ type: "success", text: "✓ Votre mot de passe a été modifié avec succès !" });
        setPwdForm({ current: "", newPwd: "", confirm: "" });
        setTimeout(() => setShowPwdSection(false), 3000);
      }
    } catch (err) {
      setPwdMsg({ type: "error", text: "Une erreur est survenue: " + err.message });
    } finally {
      setPwdSaving(false);
    }
  };

  const ENAV = [
    { id: "accueil", label: "Accueil", ic: "🏠" },
    { id: "fiches", label: "Bulletins", ic: "💰", badge: unreadBulletins },
    { id: "demandes", label: "Demandes", ic: "📋", badge: unreadConges + unreadAbsences },
    { id: "messagerie", label: "Messages", ic: "💬", badge: msgUnread },
    { id: "profil", label: "Mon profil", ic: "👤" },
    { id: "annonces", label: "Annonces", ic: "📢", badge: unreadAnnonces },
  ];
  const ETITLES = { accueil: "Mon espace", fiches: "Mes bulletins de paie", demandes: "Congés & Absences", frais: "Mes notes de frais", messagerie: "Messagerie", profil: "Mon profil", annonces: "Annonces RH" };

  const SPIN_CSS = "@keyframes spin{to{transform:rotate(360deg)}}";
  if (loading) return (<><style>{CSS + SPIN_CSS}</style><div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "var(--text3)" }}><div style={{ width: 36, height: 36, border: "3px solid var(--blue-l)", borderTopColor: "var(--blue)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /><div>Chargement…</div></div></>);

  return (<><style>{CSS}</style>
    <div className={`shell ${user?.site === "France" ? "theme-france" : ""}`}>
      {/* SIDEBAR */}
      <div className="side">
        <div className="slogo">
          <img src="/logo-majha.png" alt="MAJHA" style={{ width: 42, height: 42, objectFit: "contain", mixBlendMode: "lighten", flexShrink: 0, borderRadius: 8 }} />
          <div><div className="sbrand">MAJHA</div><div className="stag">Espace collaborateur</div></div>
        </div>
        <nav className="snav">
          <div className="nsec">Navigation</div>
          {ENAV.map(n => (
            <div key={n.id} className={"nitem" + (tab === n.id ? " on" : "")} onClick={() => handleTabClick(n.id)}>
              <span className="nic">{n.ic}</span>{n.label}
              {n.badge > 0 && (
                <span style={{
                  marginLeft: "auto",
                  background: "#EF4444",
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 20,
                  minWidth: 16,
                  textAlign: "center"
                }}>
                  {n.badge > 9 ? "9+" : n.badge}
                </span>
              )}
            </div>
          ))}
        </nav>
        <div className="sfoot">
          <div className="surow">
            <Av initials={user.avatar_initials} color={user.avatar_color} size={36} />
            <div><div className="suname">{user.prenom} {user.nom}</div><div className="surole">{formatPoste(user.poste, user)}</div></div>
          </div>
          <span className="soutt" onClick={onLogout}>↩ Déconnexion</span>
        </div>
      </div>

      {/* MAIN */}
      <div className="main">
        <div className="topbar">
          <div>
            <div className="tbtitle">{ETITLES[tab]}</div>
            <div className="tbsub">{TODAY_STR}</div>
          </div>
          <div className="tbright">
            {(pendingConges + pendingAbs) > 0 && <span className="badge b-yellow" style={{ fontSize: 11 }}>{pendingConges + pendingAbs} en attente</span>}
            <div style={{ position: "relative" }} ref={notifRef}>
              <div className="bell" onClick={() => setShowNotifs(!showNotifs)}>
                🔔{notifs.filter(n => !n.lu).length > 0 && <span className="bell-dot" />}
              </div>
              {showNotifs && (
                <div className="notif-panel">
                  <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--bg)" }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>Notifications</span>
                    {notifs.filter(n => !n.lu).length > 0 && <button style={{ fontSize: 11, color: "var(--blue)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }} onClick={async () => {
                      await supabase.from("notifications").update({ lu: true }).eq("employe_id", user.id).eq("lu", false);
                      setNotifs(p => p.map(n => ({ ...n, lu: true })));
                    }}>Tout marquer lu</button>}
                  </div>
                  {notifs.length === 0 && <div style={{ padding: "16px", fontSize: 12, color: "var(--text3)", textAlign: "center" }}>Aucune notification</div>}
                  {notifs.slice(0, 10).map((n, i) => (
                    <div key={i} className={"np-item" + (!n.lu ? " np-unread" : "")} onClick={async () => {
                      if (!n.lu) {
                        await supabase.from("notifications").update({ lu: true }).eq("id", n.id);
                        setNotifs(p => p.map((x, j) => j === i ? { ...x, lu: true } : x));
                      }
                      if (n.lien) { setTab(n.lien); setShowNotifs(false); }
                    }}>
                      <div className="np-it">{n.titre}</div>
                      <div className="np-is">{n.message}</div>
                      {!n.lu && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--blue)", flexShrink: 0, marginLeft: "auto" }} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ position: "relative" }}>
              <Av initials={user.avatar_initials} color={user.avatar_color} size={36} fs={12} />
            </div>
          </div>
        </div>
        <div className="content">

          {/* ── ACCUEIL ── */}
          {tab === "accueil" && (
            <div>
              {/* Hero salaire */}
              <div className="hero-card">
                <div style={{ position: "relative", zIndex: 1 }}>
                  <div className="hero-eyebrow">{greeting}, {user.prenom} 👋</div>
                  {derniere ? (
                    <>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)", marginBottom: 4 }}>Dernier salaire net reçu</div>
                      <div className="hero-amount">{fN(derniere.salaire_net, derniere.devise)}</div>
                      <div className="hero-sub">{MONTHS[parseInt(derniere.mois) - 1]} {derniere.annee} · {derniere.devise === "XOF" ? "Régime sénégalais" : "Régime France"}</div>
                    </>
                  ) : (
                    <>
                      <div className="hero-amount" style={{ fontSize: 20, opacity: .7 }}>Aucun bulletin disponible</div>
                      <div className="hero-sub">Vos fiches de paie apparaîtront ici</div>
                    </>
                  )}
                  <div className="hero-chips">
                    <span className="hero-chip">📍 {user.site || "Dakar"}</span>
                    <span className="hero-chip">🏢 {user.departement || "—"}</span>
                    <span className="hero-chip">📅 Solde : {cSolde}j</span>
                  </div>
                </div>
              </div>

              {/* Stats rapides */}
              <div className="dash-grid" style={{ marginBottom: 20 }}>
                <div className="mini-kpi">
                  <div className="mki-icon" style={{ background: "var(--blue-ll)" }}><span>💰</span></div>
                  <div><div className="mki-val">{fiches.length}</div><div className="mki-lbl">Bulletins reçus</div></div>
                </div>
                <div className="mini-kpi" style={{ position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div className="mki-val" style={{ color: "var(--green)" }}>{cSolde}</div>
                    <div className="mki-lbl">Jours restants</div>
                  </div>
                  <svg viewBox="0 0 36 36" style={{ position: "absolute", right: 10, top: -2, width: 56, height: 56, transform: "rotate(-90deg)", opacity: 0.6 }}>
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="var(--green-l)" strokeWidth="3" />
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="var(--green)" strokeDasharray={`${cAcquis && parseFloat(cAcquis) > 0 ? Math.min(100, Math.max(0, (cSolde / parseFloat(cAcquis)) * 100)) : 0}, 100`} strokeWidth="3" />
                  </svg>
                </div>
                <div className="mini-kpi">
                  <div className="mki-icon" style={{ background: "#FEF3C7" }}><span>⏳</span></div>
                  <div><div className="mki-val" style={{ color: "var(--yellow)" }}>{pendingConges + pendingAbs}</div><div className="mki-lbl">En attente</div></div>
                </div>
                <div className="mini-kpi">
                  <div className="mki-icon" style={{ background: "var(--orange-l)" }}><span>📅</span></div>
                  <div><div className="mki-val" style={{ color: "var(--orange)" }}>{totalAbsJours}</div><div className="mki-lbl">Jours d'absence</div></div>
                </div>
              </div>

              {/* Actions rapides */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="chead"><span className="ctitle">Actions rapides</span></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[
                    { ic: "🌴", label: "Demander des congés", action: () => openConge() },
                    { ic: "📅", label: "Déclarer une absence", action: () => openAbs() },
                    { ic: "💰", label: "Voir mes bulletins", action: () => setTab("fiches") },
                    { ic: "👤", label: "Mon profil", action: () => setTab("profil") },
                  ].map((a, i) => (
                    <button key={i} onClick={a.action} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "var(--bg)", border: "1.5px solid var(--border)", borderRadius: 10, cursor: "pointer", textAlign: "left", transition: "all .12s", fontSize: 13, fontWeight: 500, color: "var(--text2)" }} onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--blue)"; e.currentTarget.style.background = "var(--blue-ll)"; }} onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg)"; }}>
                      <span style={{ fontSize: 20 }}>{a.ic}</span>{a.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dernières annonces */}
              {annonces.slice(0, 2).length > 0 && (
                <div className="card">
                  <div className="chead"><span className="ctitle">Annonces récentes</span><button className="btn btn-ghost btn-sm" onClick={() => setTab("annonces")}>Voir tout</button></div>
                  {annonces.slice(0, 2).map((a, i) => (
                    <div key={i} style={{ padding: "10px 0", borderBottom: i === 0 && annonces.slice(0, 2).length > 1 ? "1px solid var(--border)" : "none" }}>
                      <span className={"ann-pill " + (a.type_annonce === "urgent" ? "urgent" : a.type_annonce === "important" ? "important" : "info")}>{a.type_annonce}</span>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{a.titre}</div>
                      <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 3, lineHeight: 1.5 }}>{a.contenu?.substring(0, 80)}{a.contenu?.length > 80 ? "…" : ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── FICHES DE PAIE ── */}
          {tab === "fiches" && (
            <div>
              {derniere && (
                <div className="hero-card" style={{ marginBottom: 20 }}>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div className="hero-eyebrow">Dernier bulletin</div>
                    <div className="hero-amount">{derniere.salaire_net ? fN(derniere.salaire_net, derniere.devise) : "Disponible"}</div>
                    <div className="hero-sub">
                      {MONTHS[parseInt(derniere.mois) - 1]} {derniere.annee}
                      {user?.site !== "France" && ((parseFloat(user.salaire_base) || 0) > 0) ? ` · Brut : ${fN(parseBulletinSnap(derniere)?.brut ?? ((parseFloat(user.salaire_base) || 0) + (parseFloat(user.sursalaire) || 0) + (parseFloat(user.prime_anciennete) || 0)), derniere.devise)}` : ""}
                    </div>
                    {derniere.url && (
                      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                          style={{ background: "rgba(255,255,255,.22)", border: "1.5px solid rgba(255,255,255,.4)", color: "#fff", padding: "8px 18px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                          onClick={() => setPdfPreview({
                            url: derniere.url,
                            title: `Bulletin ${MONTHS[parseInt(derniere.mois) - 1]} ${derniere.annee} — ${user.prenom} ${user.nom}`,
                            fileName: derniere.nom_fichier || `Bulletin_${user.prenom}_${user.nom}_${derniere.annee}_${derniere.mois}.pdf`
                          })}
                        >
                          👁️ Voir le bulletin
                        </button>
                        <button
                          style={{ background: "rgba(255,255,255,.12)", border: "1.5px solid rgba(255,255,255,.25)", color: "#fff", padding: "8px 18px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                          onClick={() => downloadCleanPDF(derniere.url, derniere.nom_fichier || `Bulletin_${user.prenom}_${user.nom}_${derniere.annee}_${derniere.mois}.pdf`)}
                        >
                          ⬇ Télécharger PDF
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="card">
                <div className="chead"><span className="ctitle">Tous mes bulletins</span><span className="csub">{fiches.length} bulletin{fiches.length !== 1 ? "s" : ""}</span></div>
                {fiches.length === 0 ? (
                  <div className="empty"><div className="empty-ic">💰</div><div className="empty-t">Aucun bulletin disponible</div><div className="empty-s">Vos fiches apparaîtront ici dès qu'elles seront déposées par les RH.</div></div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {fiches.map((f, i) => (
                      <div key={i} className="slip-item">
                        <div className="slip-dot" style={{ background: i === 0 ? "var(--blue)" : "var(--border2)" }} />
                        <div style={{ flex: 1 }}>
                          <div className="slip-month">{MONTHS[parseInt(f.mois) - 1]} {f.annee}</div>
                          <div className="slip-net">
                            {f.salaire_net ? `Net : ${fN(f.salaire_net, f.devise)}` : (f.nom_fichier || "Bulletin PDF")}
                            {user?.site !== "France" && ((parseFloat(user.salaire_base) || 0) > 0) ? ` · Brut : ${fN(parseBulletinSnap(f)?.brut ?? ((parseFloat(user.salaire_base) || 0) + (parseFloat(user.sursalaire) || 0) + (parseFloat(user.prime_anciennete) || 0)), f.devise)}` : ""}
                          </div>
                        </div>
                        {i === 0 && <span className="badge b-blue" style={{ fontSize: 10 }}>Dernier</span>}
                        {f.url && (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => setPdfPreview({
                                url: f.url,
                                title: `Bulletin ${MONTHS[parseInt(f.mois) - 1]} ${f.annee} — ${user.prenom} ${user.nom}`,
                                fileName: f.nom_fichier || `Bulletin_${user.prenom}_${user.nom}_${f.annee}_${f.mois}.pdf`
                              })}
                            >
                              👁️ Voir
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Télécharger"
                              onClick={() => downloadCleanPDF(f.url, f.nom_fichier || `Bulletin_${user.prenom}_${user.nom}_${f.annee}_${f.mois}.pdf`)}
                            >
                              ⬇
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── DEMANDES (CONGÉS + ABSENCES) ── */}
          {tab === "demandes" && (
            <div>
              {/* Soldes congés */}
              <div className="stat-row">
                {[["Acquis", cAcquis, "var(--text)"], ["Pris", cPris, "var(--red)"], ["Restants", cSolde, "var(--green)"]].map(([l, v, c]) => (
                  <div key={l} className="stat-box"><div className="stat-val" style={{ color: c }}>{v}</div><div className="stat-lbl">{l}</div></div>
                ))}
              </div>

              {/* Tabs interne */}
              <div className="tab-bar">
                <button className={"tab-btn" + (demandesTab === "con" ? " on" : "")} onClick={() => setDemandesTab("con")}>🌴 Congés ({conges.length})</button>
                <button className={"tab-btn" + (demandesTab === "abs" ? " on" : "")} onClick={() => setDemandesTab("abs")}>📅 Absences ({absences.length})</button>
              </div>

              {/* CONGÉS */}
              {demandesTab === "con" && (
                <div>
                  <div className="sech">
                    <span className="sect">Mes demandes de congés</span>
                    <button className="btn btn-primary btn-sm" onClick={() => { setForm(f => ({ ...f, type_conge: "Congés payés" })); setShowConge(true); }}>+ Nouvelle demande</button>
                  </div>
                  {conges.length === 0 ? (
                    <div className="empty"><div className="empty-ic">🌴</div><div className="empty-t">Aucune demande de congés</div><div className="empty-s">Soumettez votre première demande ci-dessus.</div></div>
                  ) : conges.map((c, i) => (
                    <div key={i} className="req-card">
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--green-l)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🌴</div>
                          <div>
                            <div className="req-type">{c.type_conge}</div>
                            <div className="req-dates">
                              {fDs(c.date_debut)} → {fDs(c.date_fin)}
                              <span className="req-days">{c.nb_jours} jour{c.nb_jours !== 1 ? "s" : ""}</span>
                            </div>
                            {c.motif && <div className="req-motif">"{c.motif}"</div>}
                            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>Déposé le {fDs(c.created_at)}</div>
                          </div>
                        </div>
                        <StatusBadge s={c.statut} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ABSENCES */}
              {demandesTab === "abs" && (
                <div>
                  <div className="sech">
                    <span className="sect">Mes absences</span>
                    <button className="btn btn-primary btn-sm" onClick={() => { setForm(f => ({ ...f, type_absence: "Maladie" })); setShowAbs(true); }}>+ Déclarer</button>
                  </div>
                  {absences.length === 0 ? (
                    <div className="empty"><div className="empty-ic">📅</div><div className="empty-t">Aucune absence déclarée</div><div className="empty-s">Déclarez vos absences pour un suivi par les RH.</div></div>
                  ) : absences.map((a, i) => (
                    <div key={i} className="req-card">
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: a.type_absence === "Absence injustifiée" ? "var(--red-l)" : "var(--orange-l)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                            {a.type_absence === "Maladie" ? "🤒" : a.type_absence === "Accident du travail" ? "🚑" : a.type_absence === "Absence injustifiée" ? "⚠️" : "📅"}
                          </div>
                          <div>
                            <div className="req-type">{a.type_absence}</div>
                            <div className="req-dates">
                              {fDs(a.date_debut)} → {fDs(a.date_fin)}
                              <span className="req-days">{a.nb_jours} jour{a.nb_jours !== 1 ? "s" : ""}</span>
                            </div>
                            {a.motif && <div className="req-motif">"{a.motif}"</div>}
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                              <div style={{ fontSize: 11, color: "var(--text3)" }}>Déclaré le {fDs(a.created_at)}</div>
                              {a.justificatif_url && (
                                <a href={a.justificatif_url} target="_blank" rel="noreferrer">
                                  <span className="req-just">📎 Justificatif joint</span>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                        <StatusBadge s={a.statut} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── NOTES DE FRAIS ── */}
          {tab === "frais" && (
            <div>
              <div className="sech">
                <span className="sect">Mes Notes de Frais</span>
                <button className="btn btn-primary btn-sm" onClick={() => alert("Création de note de frais en cours de développement")}>+ Nouvelle note</button>
              </div>
              <div className="empty"><div className="empty-ic">💸</div><div className="empty-t">Module de notes de frais en construction</div><div className="empty-s">Vous pourrez bientôt déclarer vos dépenses professionnelles ici et suivre leurs remboursements.</div></div>
            </div>
          )}

          {/* ── PROFIL ── */}
          {tab === "profil" && (
            <div>
              <div className="profile-card">
                <div className="profile-cover" />
                <div className="profile-av-wrap">
                  <div className="profile-av-ring" style={{ background: user.avatar_color || "var(--blue)" }}>{user.avatar_initials || "?"}</div>
                </div>
                <div className="profile-info">
                  <div className="profile-name">{user.prenom} {user.nom.toUpperCase()}</div>
                  <div className="profile-role">{formatPoste(user.poste, user)} · {user.departement}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    {[user.email, user.site || "Dakar", "Entrée : " + fD(user.date_entree)].filter(Boolean).map((v, i) => (
                      <span key={i} style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text2)", fontSize: 11, padding: "4px 12px", borderRadius: 20, fontWeight: 500 }}>{v}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="chead"><span className="ctitle">Informations professionnelles</span></div>
                <div className="info-grid">
                  {[
                    ["Poste", formatPoste(user.poste, user) || "—"],
                    ["Département", user.departement || "—"],
                    ["Date d'embauche", fD(user.date_entree)],
                    ["Identifiant", user.login],
                    ["Régime", user.devise === "XOF" ? "Sénégalais (IPRES/IR/TRIMF)" : "France (Sécurité sociale)"],
                    ["Site", user.site || "Dakar"],
                    ...(user.categorie ? [["Catégorie", user.categorie]] : []),
                    ...(user.parts_fiscales ? [["Parts fiscales", String(user.parts_fiscales)]] : []),
                  ].map(([l, v]) => (
                    <div key={l} className="info-box"><div className="info-lbl">{l}</div><div className="info-val">{v}</div></div>
                  ))}
                </div>
              </div>

              {/* État civil si données renseignées */}
              {(user.date_naissance || user.situation_matrimoniale || user.num_cni) && (
                <div className="card" style={{ marginBottom: 14 }}>
                  <div className="chead"><span className="ctitle">État civil</span></div>
                  <div className="info-grid">
                    {[
                      user.date_naissance && ["Date de naissance", fD(user.date_naissance)],
                      user.lieu_naissance && ["Lieu de naissance", user.lieu_naissance],
                      user.nationalite && ["Nationalité", user.nationalite],
                      user.situation_matrimoniale && ["Situation", user.situation_matrimoniale],
                      user.nb_enfants && parseInt(user.nb_enfants) > 0 && ["Enfants à charge", String(user.nb_enfants)],
                      user.num_cni && ["N° CNI / Passeport", user.num_cni],
                      user.num_ipres && ["N° IPRES", user.num_ipres],
                      user.adresse && ["Adresse", user.adresse],
                      user.telephone && ["Téléphone", user.telephone],
                    ].filter(Boolean).map(([l, v]) => (
                      <div key={l} className="info-box"><div className="info-lbl">{l}</div><div className="info-val">{v}</div></div>
                    ))}
                  </div>
                </div>
              )}
              {/* Sécurité & Mot de passe */}
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="chead" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <span className="ctitle">🔒 Sécurité du compte</span>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>Modifier votre mot de passe de connexion</div>
                  </div>
                  {!showPwdSection && (
                    <button className="btn btn-ghost btn-sm" onClick={() => { setShowPwdSection(true); setPwdMsg(null); }}>
                      🔑 Modifier mon mot de passe
                    </button>
                  )}
                </div>

                {showPwdSection && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                    {pwdMsg && (
                      <div style={{
                        padding: "10px 14px",
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 500,
                        marginBottom: 14,
                        background: pwdMsg.type === "success" ? "#ECFDF5" : "#FEF2F2",
                        color: pwdMsg.type === "success" ? "#065F46" : "#991B1B",
                        border: `1px solid ${pwdMsg.type === "success" ? "#A7F3D0" : "#FECACA"}`
                      }}>
                        {pwdMsg.text}
                      </div>
                    )}

                    <div className="fgrid2" style={{ gap: 12 }}>
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label className="flbl">Mot de passe actuel *</label>
                        <input
                          type="text"
                          className="finp sec-mask"
                          placeholder="••••••••"
                          value={pwdForm.current}
                          onChange={e => setPwdForm(f => ({ ...f, current: e.target.value }))}
                          onKeyDown={e => { if (e.key === "Enter") changerMotDePasse(); }}
                          autoComplete="off"
                          data-lpignore="true"
                          data-form-type="other"
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label className="flbl">Nouveau mot de passe *</label>
                        <input
                          type="text"
                          className="finp sec-mask"
                          placeholder="Au moins 4 caractères"
                          value={pwdForm.newPwd}
                          onChange={e => setPwdForm(f => ({ ...f, newPwd: e.target.value }))}
                          onKeyDown={e => { if (e.key === "Enter") changerMotDePasse(); }}
                          autoComplete="off"
                          data-lpignore="true"
                          data-form-type="other"
                        />
                      </div>
                    </div>

                    <div className="field" style={{ marginTop: 12, marginBottom: 14 }}>
                      <label className="flbl">Confirmer le nouveau mot de passe *</label>
                      <input
                        type="text"
                        className="finp sec-mask"
                        placeholder="Répétez le nouveau mot de passe"
                        value={pwdForm.confirm}
                        onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))}
                        onKeyDown={e => { if (e.key === "Enter") changerMotDePasse(); }}
                        autoComplete="off"
                        data-lpignore="true"
                        data-form-type="other"
                      />
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => { setShowPwdSection(false); setPwdMsg(null); setPwdForm({ current: "", newPwd: "", confirm: "" }); }}
                        disabled={pwdSaving}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={changerMotDePasse}
                        disabled={pwdSaving || !pwdForm.current || !pwdForm.newPwd || !pwdForm.confirm}
                      >
                        {pwdSaving ? "Enregistrement…" : "✓ Enregistrer le nouveau mot de passe"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {docs.length > 0 && (
                <div className="card">
                  <div className="chead"><span className="ctitle">Documents RH</span><span className="csub">{docs.length} document{docs.length !== 1 ? "s" : ""}</span></div>
                  {docs.map((d, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < docs.length - 1 ? "1px solid var(--border)" : "none" }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--blue-ll)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>📋</div>
                      <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{d.nom}</div><div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{d.type_doc} · {fD(d.uploaded_at)}</div></div>
                      {d.url && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setPdfPreview({
                            url: d.url,
                            title: d.nom || "Document RH",
                            fileName: d.nom || "Document_RH.pdf"
                          })}
                        >
                          👁️ Voir
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ANNONCES ── */}
          {tab === "annonces" && (
            <div>
              {annonces.length === 0 ? (<div className="empty"><div className="empty-ic">📢</div><div className="empty-t">Aucune annonce</div><div className="empty-s">Les annonces RH apparaîtront ici.</div></div>)
                : annonces.map((a, i) => (
                  <div key={i} className="ann-card">
                    <span className={"ann-pill " + (a.type_annonce === "urgent" ? "urgent" : a.type_annonce === "important" ? "important" : "info")}>
                      {a.type_annonce === "urgent" ? "🚨" : a.type_annonce === "important" ? "⚠️" : "ℹ️"} {a.type_annonce}
                    </span>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 7 }}>{a.titre}</div>
                    <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.7 }}>{a.contenu}</div>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 6 }}>
                      <Av initials={(a.publie_par || "RH")[0]} color="var(--blue)" size={18} fs={8} />
                      Publié par {a.publie_par} · {fD(a.created_at)}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* ── MESSAGERIE ── toujours monté pour garder les listeners actifs */}
          <div style={{ display: tab === "messagerie" ? "block" : "none" }}>
            <MessagerieTab user={user} employes={employes} empActifs={employes.filter(e => e.actif !== false)} mySite={user.site || "Dakar"} onUnreadChange={setMsgUnread} isVisible={tab === "messagerie"} />
          </div>

        </div>
      </div>

      {/* BOTTOM NAV */}
      <div className="bnav"><div className="bnav-inner">
        {ENAV.map(n => (
          <button key={n.id} className={"btab" + (tab === n.id ? " on" : "")} onClick={() => setTab(n.id)} style={{ position: "relative" }}>
            {n.id === "demandes" && (pendingConges + pendingAbs) > 0 && <span className="btab-bdg">{pendingConges + pendingAbs}</span>}
            {n.badge > 0 && <span className="btab-bdg">{n.badge}</span>}
            <span className="btab-ic">{n.ic}</span><span className="btab-lb">{n.label}</span>
          </button>
        ))}
      </div></div>
    </div>

    {/* ── MODAL DEMANDE DE CONGÉS ── */}
    {showConge && (
      <div className="ov" onClick={() => { setShowConge(false); setForm(f => ({ ...f, date_debut: "", date_fin: "", motif: "" })); }}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="mh" style={{ background: "linear-gradient(135deg,#0D2680,var(--blue))", borderRadius: "16px 16px 0 0" }}>
            <div>
              <div className="mt" style={{ color: "#fff" }}>🌴 Demande de congés</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)", marginTop: 3 }}>Solde disponible : {cSolde} jour{cSolde !== 1 ? "s" : ""}</div>
            </div>
            <button className="mx" style={{ color: "rgba(255,255,255,.7)" }} onClick={() => { setShowConge(false); setForm(f => ({ ...f, date_debut: "", date_fin: "", motif: "" })); }}>✕</button>
          </div>
          <div className="mb" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="field">
              <label className="flbl">Type de congé</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
                {CON_TYPES.map(t => (
                  <button key={t} onClick={() => setForm(f => ({ ...f, type_conge: t }))} style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid", borderColor: form.type_conge === t ? "var(--blue)" : "var(--border)", background: form.type_conge === t ? "var(--blue-ll)" : "var(--white)", color: form.type_conge === t ? "var(--blue)" : "var(--text2)", fontSize: 12, fontWeight: form.type_conge === t ? 600 : 400, cursor: "pointer", textAlign: "left", transition: "all .12s" }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="fgrid2">
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Date de début</label>
                <input type="date" className="finp" value={form.date_debut || ""} min={new Date().toISOString().split("T")[0]} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Date de fin</label>
                <input type="date" className="finp" value={form.date_fin || ""} min={form.date_debut || new Date().toISOString().split("T")[0]} onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))} />
              </div>
            </div>
            {form.date_debut && form.date_fin && (
              <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 20 }}>📅</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--blue)" }}>{countJoursOuvrables(form.date_debut, form.date_fin)} jour{countJoursOuvrables(form.date_debut, form.date_fin) !== 1 ? "s" : ""}</div>
                  <div style={{ fontSize: 11, color: "var(--text3)" }}>du {fDs(form.date_debut)} au {fDs(form.date_fin)}</div>
                </div>
                {countJoursOuvrables(form.date_debut, form.date_fin) > cSolde && <div style={{ marginLeft: "auto", background: "var(--red-l)", color: "var(--red)", fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>⚠ Solde insuffisant</div>}
              </div>
            )}
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Motif <span style={{ color: "var(--text3)", fontWeight: 400, textTransform: "none" }}>(optionnel)</span></label>
              <textarea className="fta" rows={3} placeholder="Précisez si nécessaire…" value={form.motif || ""} onChange={e => setForm(f => ({ ...f, motif: e.target.value }))} /></div>
          </div>
          <div className="mf">
            <button className="btn btn-ghost" onClick={() => { setShowConge(false); setForm(f => ({ ...f, date_debut: "", date_fin: "", motif: "" })); }}>Annuler</button>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center" }} disabled={saving || !form.date_debut || !form.date_fin || !form.type_conge} onClick={saveConge}>{saving ? "Envoi en cours…" : "✓ Envoyer la demande"}</button>
          </div>
        </div>
      </div>
    )}

    {/* ── MODAL ABSENCE ── */}
    {showAbs && (
      <div className="ov" onClick={() => { setShowAbs(false); setForm(f => ({ ...f, date_debut: "", date_fin: "", motif: "", _fileName: "" })); try { if (absFileRef.current) absFileRef.current.value = ""; } catch { } }}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="mh" style={{ background: "linear-gradient(135deg,#7C2D12,#C2410C)", borderRadius: "16px 16px 0 0" }}>
            <div>
              <div className="mt" style={{ color: "#fff" }}>📅 Déclarer une absence</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)", marginTop: 3 }}>Joignez un justificatif si disponible</div>
            </div>
            <button className="mx" style={{ color: "rgba(255,255,255,.7)" }} onClick={() => { setShowAbs(false); setForm(f => ({ ...f, date_debut: "", date_fin: "", motif: "", _fileName: "" })); try { if (absFileRef.current) absFileRef.current.value = ""; } catch { } }}>✕</button>
          </div>
          <div className="mb" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="field">
              <label className="flbl">Type d'absence</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
                {ABS_TYPES.map(t => (
                  <button key={t} onClick={() => setForm(f => ({ ...f, type_absence: t }))} style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid", borderColor: form.type_absence === t ? "var(--orange)" : "var(--border)", background: form.type_absence === t ? "var(--orange-l)" : "var(--white)", color: form.type_absence === t ? "var(--orange)" : "var(--text2)", fontSize: 12, fontWeight: form.type_absence === t ? 600 : 400, cursor: "pointer", textAlign: "left", transition: "all .12s" }}>
                    {t === "Maladie" ? "🤒 " : t === "Accident du travail" ? "🚑 " : t === "Formation" ? "📚 " : t === "Télétravail" ? "🏠 " : ""}{t}
                  </button>
                ))}
              </div>
            </div>
            <div className="fgrid2">
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Date de début</label>
                <input type="date" className="finp" value={form.date_debut || ""} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Date de fin</label>
                <input type="date" className="finp" value={form.date_fin || ""} min={form.date_debut || undefined} onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))} />
              </div>
            </div>
            {form.date_debut && form.date_fin && (
              <div style={{ background: "var(--orange-l)", border: "1px solid #FDE68A", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>📅</span>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--orange)" }}>{countJoursOuvrables(form.date_debut, form.date_fin)} jour{countJoursOuvrables(form.date_debut, form.date_fin) !== 1 ? "s" : ""} · {fDs(form.date_debut)} → {fDs(form.date_fin)}</div>
              </div>
            )}
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Motif <span style={{ color: "var(--text3)", fontWeight: 400, textTransform: "none" }}>(optionnel)</span></label>
              <textarea className="fta" rows={2} placeholder="Décrivez brièvement…" value={form.motif || ""} onChange={e => setForm(f => ({ ...f, motif: e.target.value }))} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Justificatif <span style={{ color: "var(--text3)", fontWeight: 400, textTransform: "none" }}>(PDF, PNG ou JPG)</span></label>
              {/* FIX: upload zone avec bouton supprimer séparé (pas imbriqué dans label → fix mobile Safari) */}
              <div style={{ position: "relative" }}>
                <label className={"upload-pretty" + (form._fileName ? " has-file" : "")} style={{ cursor: "pointer" }}>
                  <span style={{ fontSize: 28 }}>{form._fileName ? "✅" : "📎"}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: form._fileName ? "var(--green)" : "var(--text2)" }}>
                      {form._fileName || "Appuyer pour joindre un fichier"}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                      {form._fileName ? "Fichier sélectionné" : "Ordonnance, arrêt de travail, convocation…"}
                    </div>
                  </div>
                  <input ref={absFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: "none" }} onChange={e => {
                    const file = e.target.files?.[0];
                    setForm(f => ({ ...f, _fileName: file?.name || "" }));
                  }} />
                </label>
                {form._fileName && (
                  <button type="button" style={{ position: "absolute", top: 8, right: 8, background: "var(--red-l)", border: "1px solid var(--red)", color: "var(--red)", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}
                    onClick={() => { setForm(f => ({ ...f, _fileName: "" })); try { if (absFileRef.current) absFileRef.current.value = ""; } catch { } }}>
                    ✕ Supprimer
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="mf">
            <button className="btn btn-ghost" onClick={() => { setShowAbs(false); setForm(f => ({ ...f, date_debut: "", date_fin: "", motif: "", _fileName: "" })); try { if (absFileRef.current) absFileRef.current.value = ""; } catch { } }}>Annuler</button>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center", background: "var(--orange)", borderColor: "var(--orange)" }}
              disabled={saving || !form.date_debut || !form.date_fin || !form.type_absence}
              onClick={saveAbs}>
              {saving ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                  Envoi…
                </span>
              ) : "✓ Déclarer l'absence"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* MODAL PRÉVISUALISATION PDF */}
    <PdfPreviewModal preview={pdfPreview} onClose={() => setPdfPreview(null)} />
  </>);
}


/* ═══════════════════════════════════════════════════════
   MESSAGERIE INTERNE
   Tables Supabase nécessaires:
   - messages: id, thread_id, sender_id, content, file_url, file_name, created_at
   - threads: id, participants (array), title, created_at, updated_at
═══════════════════════════════════════════════════════ */
function MessagerieTab({ user, employes, empActifs, mySite, onUnreadChange, setConfirmModal, isVisible }) {
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [threadTitle, setThreadTitle] = useState("");
  const [fileToSend, setFileToSend] = useState(null);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // ─── useRef pour accès à activeThread sans stale closure dans le realtime ───
  const activeThreadRef = useRef(null);
  const employesRef = useRef(employes); // FIX: accès à employes sans stale closure
  useEffect(() => { activeThreadRef.current = activeThread; }, [activeThread]);
  useEffect(() => { employesRef.current = employes; }, [employes]);
  // Ref pour savoir si l'onglet messagerie est visible (pour le badge)
  const isVisibleRef = useRef(isVisible);
  useEffect(() => { isVisibleRef.current = isVisible; }, [isVisible]);

  // Charger les threads de l'utilisateur
  useEffect(() => {
    loadThreads();
    const ch = supabase.channel("msg-" + user.id)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, async (p) => {
        const msg = p.new;
        const currentThread = activeThreadRef.current;
        const allEmps = employesRef.current;
        // FIX: chercher l'expéditeur dans la liste complète employesRef
        let sender = allEmps.find(e => e.id === msg.sender_id);
        if (!sender && msg.sender_id !== user.id) {
          // Fallback: fetch depuis Supabase si pas trouvé localement
          const { data } = await supabase.from("employes").select("id,prenom,nom,avatar_initials,avatar_color").eq("id", msg.sender_id).single();
          sender = data;
          // Ajouter à la ref pour les prochains messages
          if (data) employesRef.current = [...employesRef.current, data];
        }
        // Si c'est notre propre message via realtime (doublon avec optimistic), ignorer
        if (msg.sender_id === user.id) return;
        // Ajouter le message si la conversation est active
        if (currentThread && msg.thread_id === currentThread.id) {
          setMessages(prev => [...prev, { ...msg, employes: sender || { prenom: "?", nom: "", avatar_initials: "?", avatar_color: "#888" } }]);
        }
        // Mettre à jour la liste des threads
        setThreads(prev => {
          const exists = prev.find(t => t.id === msg.thread_id);
          // isActiveThread = vrai SEULEMENT si l'onglet messagerie est ouvert ET le thread est actif
          const isActiveThread = isVisibleRef.current && currentThread && msg.thread_id === currentThread.id;
          const updated = prev.map(t => t.id === msg.thread_id ? {
            ...t,
            last_msg: msg.content || "📎 Fichier",
            updated_at: msg.created_at,
            unread: isActiveThread ? 0 : (t.unread || 0) + 1
          } : t);
          if (!exists) { loadThreads(); return prev; }
          // Remonter le thread en haut de la liste
          const thread = updated.find(t => t.id === msg.thread_id);
          return thread ? [thread, ...updated.filter(t => t.id !== msg.thread_id)] : updated;
        });
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "threads" }, async (p) => {
        const thread = p.new;
        if ((thread.participants || []).includes(user.id)) {
          // Nouvelle conversation créée par quelqu'un d'autre
          loadThreads();
        }
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [user.id]);

  // Scroll auto vers le bas
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages]);

  const loadThreads = async () => {
    const { data } = await supabase.from("threads")
      .select("*")
      .contains("participants", [user.id])
      .order("updated_at", { ascending: false });
    if (!data) { setLoading(false); return; }

    // Fetch last messages to fix the "Démarrer une nouvelle conversation" issue
    const threadIds = data.map(t => t.id);
    let msgData = [];
    if (threadIds.length > 0) {
      const { data: msgs } = await supabase.from("messages").select("thread_id, content, created_at").in("thread_id", threadIds).order("created_at", { ascending: false });
      msgData = msgs || [];
    }

    setThreads(prev => {
      const prevMap = Object.fromEntries(prev.map(t => [t.id, t]));
      return (data || []).map(t => {
        const lastM = msgData.find(m => m.thread_id === t.id);
        const lastMsgText = lastM ? (lastM.content || "📎 Fichier") : "";
        return { ...t, unread: prevMap[t.id]?.unread || 0, last_msg: lastMsgText };
      });
    });
    setLoading(false);
  };

  const loadMessages = async (thread) => {
    setActiveThread(thread);
    activeThreadRef.current = thread;
    // Marquer comme lu
    setThreads(prev => prev.map(t => t.id === thread.id ? { ...t, unread: 0 } : t));
    const { data } = await supabase.from("messages")
      .select("*,employes(prenom,nom,avatar_initials,avatar_color)")
      .eq("thread_id", thread.id)
      .order("created_at", { ascending: true });
    setMessages(data || []);
    // Focus sur l'input après chargement
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const sendMsg = async () => {
    if ((!newMsg.trim() && !fileToSend) || !activeThread) return;
    setSending(true);
    // ── Optimistic update immédiat ──
    const msgContent = newMsg.trim() || null;
    const optimisticId = "opt-" + Date.now();
    // FIX: utiliser user directement pour l'expéditeur (pas via employes)
    const senderObj = {
      id: user.id,
      prenom: user.prenom,
      nom: user.nom,
      avatar_initials: user.avatar_initials || ((user.prenom?.[0] || "") + (user.nom?.[0] || "")).toUpperCase(),
      avatar_color: user.avatar_color || "var(--blue)"
    };
    const optimisticMsg = { id: optimisticId, thread_id: activeThread.id, sender_id: user.id, content: msgContent, file_url: null, file_name: null, created_at: new Date().toISOString(), employes: senderObj, _sending: true };
    setMessages(prev => [...prev, optimisticMsg]);
    setNewMsg(""); setFileToSend(null); setFileName("");
    try { if (fileRef.current) fileRef.current.value = ""; } catch { }
    setTimeout(() => inputRef.current?.focus(), 50);
    // ── Upload fichier si présent ──
    let file_url = null, file_name = null;
    if (fileToSend) {
      const ext = fileToSend.name.split(".").pop().toLowerCase();
      const path = `messages/${activeThread.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("Documents").upload(path, fileToSend, { upsert: true });
      if (!upErr) { const { data: u } = supabase.storage.from("Documents").getPublicUrl(path); file_url = u.publicUrl; file_name = fileToSend.name; }
    }
    // ── Insertion DB ──
    const { data: inserted } = await supabase.from("messages").insert({
      thread_id: activeThread.id, sender_id: user.id,
      content: msgContent, file_url, file_name,
      created_at: new Date().toISOString()
    }).select("*").single();
    if (inserted) {
      setMessages(prev => prev.map(m => m.id === optimisticId ? { ...inserted, employes: senderObj } : m));
      setThreads(prev => {
        const t = prev.find(x => x.id === activeThread.id);
        if (!t) return prev;
        return [{ ...t, last_msg: msgContent || "📎 Fichier", updated_at: inserted.created_at }, ...prev.filter(x => x.id !== activeThread.id)];
      });
    } else {
      setMessages(prev => prev.map(m => m.id === optimisticId ? { ...m, _error: true, _sending: false } : m));
    }
    setSending(false);
  };

  const createThread = async () => {
    if (selectedUsers.length === 0) return;
    setSending(true);
    const participants = [user.id, ...selectedUsers];
    const names = selectedUsers.map(id => {
      const e = employesRef.current.find(x => x.id === id) || employes.find(x => x.id === id);
      return e ? e.prenom : "?";
    }).join(", ");
    const title = threadTitle || names;
    const { data: thread, error } = await supabase.from("threads").insert({
      participants, title,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }).select().single();
    if (thread && !error) {
      // FIX: ajouter immédiatement + ouvrir + charger (pas besoin d'actualiser)
      const newThread = { ...thread, unread: 0 };
      setThreads(prev => [newThread, ...prev]);
      setActiveThread(newThread);
      activeThreadRef.current = newThread;
      setMessages([]);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
    setShowNew(false); setSelectedUsers([]); setThreadTitle(""); setSending(false);
  };

  // FIX: toujours afficher le nom de l'autre personne (pas le titre créé)
  const getOtherParticipants = (thread) => (thread.participants || []).filter(id => id !== user.id).map(id => employesRef.current.find(e => e.id === id) || employes.find(e => e.id === id)).filter(Boolean);
  const getThreadName = (thread) => {
    const others = getOtherParticipants(thread);
    if (others.length === 0) return thread.title || "Conversation";
    const name = others.map(e => e.prenom + " " + e.nom).join(", ");
    return name.length > 30 ? name.substring(0, 30) + "..." : name;
  };
  const getThreadAvatar = (thread) => { const others = getOtherParticipants(thread); return others[0] || { avatar_initials: "?", avatar_color: "#888" }; };
  const fTime = (d) => { if (!d) return ""; const dt = new Date(d); const now = new Date(); const diff = now - dt; if (diff < 60000) return "À l'instant"; if (diff < 3600000) return Math.floor(diff / 60000) + "min"; if (diff < 86400000) return dt.getHours() + "h" + String(dt.getMinutes()).padStart(2, "0"); return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }); };
  const groupByDate = (msgs) => { const groups = []; let lastDate = ""; msgs.forEach(m => { const d = new Date(m.created_at).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }); if (d !== lastDate) { groups.push({ type: "date", label: d }); lastDate = d; } groups.push({ type: "msg", ...m }); }); return groups; };

  const deleteThread = async (threadId, e) => {
    e.stopPropagation();
    if (setConfirmModal) {
      setConfirmModal({
        title: "Supprimer la conversation",
        message: "Supprimer cette conversation ? Les messages seront perdus.",
        danger: true,
        confirmLabel: "Supprimer",
        onConfirm: async () => {
          await supabase.from("messages").delete().eq("thread_id", threadId);
          await supabase.from("threads").delete().eq("id", threadId);
          setThreads(prev => prev.filter(t => t.id !== threadId));
          if (activeThread?.id === threadId) { setActiveThread(null); setMessages([]); }
        }
      });
    } else {
      if (!window.confirm("Supprimer cette conversation ? Les messages seront perdus.")) return;
      await supabase.from("messages").delete().eq("thread_id", threadId);
      await supabase.from("threads").delete().eq("id", threadId);
      setThreads(prev => prev.filter(t => t.id !== threadId));
      if (activeThread?.id === threadId) { setActiveThread(null); setMessages([]); }
    }
  };

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  // Notifier le parent du nombre de messages non-lus (pour le badge nav)
  useEffect(() => { if (onUnreadChange) onUnreadChange(totalUnread); }, [totalUnread]);

  // Quand l'onglet devient visible, tout marquer comme lu
  useEffect(() => {
    if (isVisible && threads.length > 0) {
      setThreads(prev => prev.map(t => ({ ...t, unread: 0 })));
      if (onUnreadChange) onUnreadChange(0);
    }
  }, [isVisible]);

  return (
    <div>
      {/* Header */}
      <div className="sech" style={{ marginBottom: 14 }}>
        <span className="sect">Messagerie interne {totalUnread > 0 && <span style={{ background: "var(--blue)", color: "#fff", fontSize: 11, padding: "1px 8px", borderRadius: 20, marginLeft: 6 }}>{totalUnread}</span>}</span>
        <button className="msg-new-btn" onClick={() => setShowNew(true)}>+ Nouvelle conversation</button>
      </div>

      {/* Layout principal */}
      <div className="msg-layout">
        {/* Liste threads */}
        <div className="msg-threads">
          <div className="msg-thread-head">
            <span className="msg-thread-title">Conversations ({threads.length})</span>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading && <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--text3)" }}>Chargement…</div>}
            {!loading && threads.length === 0 && (
              <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--text3)" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
                Aucune conversation.<br />Cliquez sur "+ Nouvelle" pour commencer.
              </div>
            )}
            {threads.map(t => {
              const av = getThreadAvatar(t);
              const isActive = activeThread?.id === t.id;
              return (
                <div key={t.id} className={"msg-thread-item" + (isActive ? " active" : "")} onClick={() => loadMessages(t)}
                  style={{ position: "relative" }} onMouseEnter={e => e.currentTarget.querySelector(".del-btn")?.style && (e.currentTarget.querySelector(".del-btn").style.opacity = "1")}
                  onMouseLeave={e => e.currentTarget.querySelector(".del-btn")?.style && (e.currentTarget.querySelector(".del-btn").style.opacity = "0")}>
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <Av initials={av.avatar_initials} color={av.avatar_color} size={36} fs={13} />
                    {(t.unread || 0) > 0 && <span style={{ position: "absolute", top: -3, right: -3, background: "var(--blue)", color: "#fff", fontSize: 9, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{t.unread}</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="msg-thread-name">{getThreadName(t)}</div>
                    <div className="msg-thread-preview">{t.last_msg || "Démarrer la conversation"}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <div className="msg-thread-time">{fTime(t.updated_at)}</div>
                    <button className="del-btn" onClick={(e) => deleteThread(t.id, e)} title="Supprimer"
                      style={{ opacity: 0, transition: "opacity .15s", background: "none", border: "none", cursor: "pointer", color: "var(--red)", fontSize: 13, padding: "2px 4px", borderRadius: 4, lineHeight: 1 }}>🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Zone chat */}
        <div className="msg-chat">
          {!activeThread ? (
            <div className="msg-empty">
              <span style={{ fontSize: 48 }}>💬</span>
              <div style={{ fontWeight: 600, color: "var(--text2)" }}>Sélectionnez une conversation</div>
              <div style={{ fontSize: 12 }}>ou créez-en une nouvelle</div>
            </div>
          ) : (
            <>
              {/* Header chat */}
              <div className="msg-chat-head">
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <Av initials={getThreadAvatar(activeThread).avatar_initials} color={getThreadAvatar(activeThread).avatar_color} size={38} fs={13} />
                  <span style={{ position: "absolute", bottom: 1, right: 1, width: 9, height: 9, borderRadius: "50%", background: "#22c55e", border: "2px solid #fff" }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{getThreadName(activeThread)}</div>
                  <div style={{ fontSize: 11, color: "var(--text3)" }}>
                    {getOtherParticipants(activeThread).map(e => e.prenom + " " + e.nom).join(", ")}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="msg-chat-body" ref={bodyRef}>
                {messages.length === 0 && <div className="msg-empty" style={{ flex: 1 }}><span style={{ fontSize: 32 }}>✉️</span><div>Aucun message pour l'instant</div></div>}
                {groupByDate(messages).map((item, i) => {
                  if (item.type === "date") return <div key={"d" + i} className="msg-date-sep">{item.label}</div>;
                  const isMine = item.sender_id === user.id;
                  const sender = item.employes || { prenom: "?", nom: "", avatar_initials: "?", avatar_color: "#888" };
                  return (
                    <div key={item.id} className={"msg-bubble-wrap" + (isMine ? " mine" : "")} style={{ opacity: item._sending ? 0.6 : 1, transition: "opacity .2s" }}>
                      {!isMine && <Av initials={sender.avatar_initials} color={sender.avatar_color} size={28} fs={10} />}
                      <div style={{ maxWidth: "75%", width: "fit-content", flexShrink: 0 }}>
                        {!isMine && <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 3 }}>{sender.prenom} {sender.nom}</div>}
                        <div className={"msg-bubble" + (isMine ? " mine" : " theirs")} style={{ ...item._error ? { background: "#FEE2E2", color: "var(--red)" } : {}, width: "100%" }}>
                          {item.content}
                          {item.file_url && (
                            <a href={item.file_url} target="_blank" rel="noreferrer" className={"msg-file-attach" + (isMine ? "" : " theirs")}>
                              <span style={{ fontSize: 18 }}>📎</span>
                              <span style={{ fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>{item.file_name || "Fichier joint"}</span>
                              <span style={{ fontSize: 11, opacity: .7, marginLeft: "auto" }}>⬇</span>
                            </a>
                          )}
                          <div className={"msg-bubble-time" + (isMine ? " mine" : "")}>
                            {item._sending ? "Envoi…" : item._error ? "Échec — réessayer" : new Date(item.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pièce jointe en attente */}
              {fileName && (
                <div style={{ padding: "6px 16px", background: "var(--blue-ll)", borderTop: "1px solid var(--blue-l)", display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <span>📎</span><span style={{ flex: 1, color: "var(--blue)", fontWeight: 500 }}>{fileName}</span>
                  <button onClick={() => { setFileToSend(null); setFileName(""); try { if (fileRef.current) fileRef.current.value = ""; } catch { } }} style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontWeight: 700, fontSize: 14 }}>✕</button>
                </div>
              )}

              {/* Composeur */}
              <div className="msg-compose">
                <input type="file" ref={fileRef} style={{ display: "none" }} accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx,.xls,.txt" onChange={e => { const f = e.target.files?.[0]; if (f) { setFileToSend(f); setFileName(f.name); } }} />
                <button className="msg-attach-btn" title="Joindre un fichier" onClick={() => fileRef.current?.click()}>📎</button>
                <textarea ref={inputRef} className="msg-compose-input" placeholder="Écrivez votre message…" value={newMsg} rows={1}
                  onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMsg(); } }} />
                <button className="msg-send-btn" disabled={sending || (!newMsg.trim() && !fileToSend)} onClick={sendMsg}>{sending ? "⏳" : "➤"}</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* MODAL NOUVELLE CONVERSATION */}
      {showNew && (
        <div className="ov" onClick={() => setShowNew(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="mh" style={{ background: "linear-gradient(135deg,#0D2680,var(--blue))", borderRadius: "16px 16px 0 0" }}>
              <div className="mt" style={{ color: "#fff" }}>💬 Nouvelle conversation</div>
              <button className="mx" style={{ color: "rgba(255,255,255,.7)" }} onClick={() => setShowNew(false)}>✕</button>
            </div>
            <div className="mb" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Titre de la conversation (optionnel)</label>
                <input className="finp" placeholder="Ex: Équipe comptabilité, Projet X…" value={threadTitle} onChange={e => setThreadTitle(e.target.value)} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Participants *</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 240, overflowY: "auto", border: "1.5px solid var(--border)", borderRadius: 8, padding: "6px 0" }}>
                  {employes.filter(e => e.actif && e.id !== user.id).map(e => {
                    const sel = selectedUsers.includes(e.id);
                    return (
                      <label key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", cursor: "pointer", background: sel ? "var(--blue-ll)" : "transparent", transition: "background .1s" }}>
                        <input type="checkbox" checked={sel} onChange={() => setSelectedUsers(p => sel ? p.filter(x => x !== e.id) : [...p, e.id])} style={{ width: 15, height: 15, accentColor: "var(--blue)" }} />
                        <Av initials={e.avatar_initials} color={e.avatar_color} size={28} fs={10} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{e.prenom} {e.nom}</div>
                          <div style={{ fontSize: 11, color: "var(--text3)" }}>{e.poste} · {e.site}</div>
                        </div>
                        {sel && <span style={{ marginLeft: "auto", color: "var(--blue)", fontWeight: 700, fontSize: 12 }}>✓</span>}
                      </label>
                    );
                  })}
                </div>
                {selectedUsers.length > 0 && <div style={{ fontSize: 11, color: "var(--blue)", marginTop: 5, fontWeight: 500 }}>{selectedUsers.length} participant{selectedUsers.length > 1 ? "s" : ""} sélectionné{selectedUsers.length > 1 ? "s" : ""}</div>}
              </div>
            </div>
            <div className="mf">
              <button className="btn btn-ghost" onClick={() => setShowNew(false)}>Annuler</button>
              <button className="btn btn-primary" disabled={selectedUsers.length === 0 || sending} onClick={createThread} style={{ flex: 1, justifyContent: "center" }}>
                {sending ? "Création…" : "✦ Démarrer la conversation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ESPACE ADMIN
═══════════════════════════════════════════════════════ */

function EmpForm({ form, setForm, modal, empTab, setEmpTab, conges, employes = [] }) {
  const isSN = (form.site || "Dakar") !== "France";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Sélecteur de site en haut du formulaire */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--bg)", padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border)", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>{isSN ? "🇸🇳" : "🇫🇷"}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Site de rattachement</div>
            <div style={{ fontSize: 11, color: "var(--text3)" }}>
              {isSN ? "Sénégal (Dakar) — Paie intégrée" : "France (Paris) — Profil léger & upload des bulletins"}
            </div>
          </div>
        </div>
        <select
          className="fsel"
          style={{ width: "auto", minWidth: 150, padding: "6px 10px", fontSize: 12, fontWeight: 600 }}
          value={form.site || "Dakar"}
          onChange={e => {
            const newSite = e.target.value;
            setForm(f => ({ ...f, site: newSite, devise: newSite === "France" ? "EUR" : "XOF" }));
            if (newSite === "France" && (empTab === "remun" || empTab === "civil")) {
              setEmpTab("pro");
            }
          }}
        >
          <option value="Dakar">🇸🇳 Dakar (Sénégal)</option>
          <option value="France">🇫🇷 France (Paris)</option>
        </select>
      </div>

      {/* Onglets conditionnels */}
      <div className="tab-bar" style={{ margin: "0 0 16px 0" }}>
        <button className={"tab-btn" + (empTab === "pro" ? " on" : "")} onClick={() => setEmpTab("pro")}>
          {isSN ? "💼 Informations pro" : "💼 Informations & Accès"}
        </button>
        {isSN && (
          <button className={"tab-btn" + (empTab === "remun" ? " on" : "")} onClick={() => setEmpTab("remun")}>
            💰 Rémunération
          </button>
        )}
        {isSN && (
          <button className={"tab-btn" + (empTab === "civil" ? " on" : "")} onClick={() => setEmpTab("civil")}>
            👤 État civil <span style={{ background: "var(--blue)", color: "#fff", fontSize: 9, padding: "1px 6px", borderRadius: 10, marginLeft: 4 }}>SN</span>
          </button>
        )}
        <button className={"tab-btn" + (empTab === "conges" ? " on" : "")} onClick={() => setEmpTab("conges")}>
          🌴 Congés
        </button>
      </div>

      {/* ONGLET PRO */}
      {empTab === "pro" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {!isSN && (
            <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 8, padding: "10px 12px", fontSize: 11.5, color: "var(--blue)", display: "flex", alignItems: "center", gap: 8 }}>
              <span>ℹ️</span>
              <div>
                Collaborateur basé à Paris. La paie étant gérée en externe, seules ses informations d'accès et d'équipe sont requises.
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Prénom *</label>
              <input
                className="finp"
                placeholder="Ex: Jean"
                value={form.prenom || ""}
                onChange={e => {
                  const p = e.target.value;
                  setForm(f => {
                    const autoLogin = modal !== "modifier" && !f._customLogin ? generateUniqueLogin(p, f.nom, employes, f.id) : f.login;
                    const autoPass = modal !== "modifier" && !f._customPass && autoLogin ? `${autoLogin}20-25` : f.password;
                    return { ...f, prenom: p, login: autoLogin, password: autoPass };
                  });
                }}
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Nom *</label>
              <input
                className="finp"
                placeholder="Ex: DUPONT"
                value={form.nom || ""}
                onChange={e => {
                  const n = e.target.value;
                  setForm(f => {
                    const autoLogin = modal !== "modifier" && !f._customLogin ? generateUniqueLogin(f.prenom, n, employes, f.id) : f.login;
                    const autoPass = modal !== "modifier" && !f._customPass && autoLogin ? `${autoLogin}20-25` : f.password;
                    return { ...f, nom: n, login: autoLogin, password: autoPass };
                  });
                }}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Poste / Fonction</label>
              <input
                className="finp"
                placeholder={form.genre === "Femme" ? "Ex: Collaboratrice Comptable" : "Ex: Collaborateur Comptable"}
                value={form.poste || ""}
                onChange={e => setForm(f => ({ ...f, poste: e.target.value }))}
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Département</label>
              <select className="fsel" value={form.departement || "Comptabilité"} onChange={e => setForm(f => ({ ...f, departement: e.target.value }))}>
                {DEPTS.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Date d'embauche</label>
              <input type="date" className="finp" value={form.date_entree || ""} onChange={e => setForm(f => ({ ...f, date_entree: e.target.value }))} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Téléphone</label>
              <input
                className="finp"
                placeholder={isSN ? "+221 77 000 00 00" : "+33 6 00 00 00 00"}
                value={form.telephone || ""}
                onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
              />
            </div>
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label className="flbl">Email professionnel</label>
            <input
              className="finp"
              placeholder="prenom@majha-accounting.com"
              value={form.email || ""}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            />
          </div>

          {/* Accès & Connexion */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, background: "var(--bg)", padding: "12px 14px", borderRadius: 10, border: "1px solid var(--border)" }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Identifiant (Login) *</label>
              <input
                className="finp"
                placeholder="prenom"
                value={form.login || ""}
                onChange={e => setForm(f => ({ ...f, login: e.target.value.toLowerCase().trim(), _customLogin: true }))}
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">{modal === "modifier" ? "Nouveau mot de passe" : "Mot de passe initial *"}</label>
              <input
                className="finp"
                placeholder={modal === "modifier" ? "Laisser vide = inchangé" : "prenom20-25"}
                value={form.password || ""}
                onChange={e => setForm(f => ({ ...f, password: e.target.value, _customPass: true }))}
              />
            </div>
          </div>

          {/* Rôle & Droits d'accès Admin */}
          <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: "var(--blue-d)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <span>🛡️</span> Droits d'accès à la plateforme
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <select
                className="fsel"
                value={form._adminRole || getEmployeAdminRole(form)}
                onChange={e => setForm(f => ({ ...f, _adminRole: e.target.value }))}
              >
                <option value="user">👤 Collaborateur standard (Espace Salarié)</option>
                <option value="dakar">🛡️ Administrateur RH Dakar (Gestion Sénégal)</option>
                <option value="france">🛡️ Administrateur RH France (Gestion Paris)</option>
                <option value="global">👑 Administrateur Global (Tous les sites)</option>
              </select>
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
                {(form._adminRole || getEmployeAdminRole(form)) === "user"
                  ? "Accès à son propre espace personnel (bulletins, congés, absences, messagerie)."
                  : (form._adminRole || getEmployeAdminRole(form)) === "global"
                  ? "Accès complet aux tableaux de bord RH, paie et validation pour Dakar et France."
                  : `Accès administrateur RH pour le site de ${(form._adminRole || getEmployeAdminRole(form)) === "dakar" ? "Dakar (Sénégal)" : "Paris (France)"}.`}
              </div>
            </div>
          </div>

          {/* Catégorie convention (Dakar seulement) */}
          {isSN && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Catégorie Convention Commerce</label>
                <select className="fsel" value={form.categorie || ""} onChange={e => {
                  const cat = e.target.value;
                  const info = CONVENTION_COMMERCE[cat];
                  setForm(f => ({
                    ...f,
                    categorie: cat,
                    ...(info ? {
                      salaire_base_ref: info.sal,
                      taux_horaire_ref: info.taux,
                      regime_cadre: info.cadre ? "oui" : "non",
                      salaire_base: String(info.sal)
                    } : {})
                  }));
                }}>
                  <option value="">Choisir une catégorie…</option>
                  {CATEGORIES_COMMERCE.map(c => <option key={c} value={c}>{c} — {CONVENTION_COMMERCE[c].sal.toLocaleString("fr-FR")} FCFA</option>)}
                </select>
              </div>
              {form.categorie && (
                <div style={{ padding: "10px 14px", background: "var(--blue-ll)", borderRadius: 8, border: "1px solid var(--blue-l)" }}>
                  <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>Référence convention</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--blue)" }}>{CONVENTION_COMMERCE[form.categorie]?.sal.toLocaleString("fr-FR")} FCFA/mois</div>
                  <div style={{ fontSize: 11, color: "var(--text3)" }}>{CONVENTION_COMMERCE[form.categorie]?.taux.toFixed(2)} FCFA/h · {CONVENTION_COMMERCE[form.categorie]?.cadre ? "Cadre" : "Non-cadre"}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ONGLET REMUNÉRATION */}
      {empTab === "remun" && form.site !== "France" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Info catégorie convention */}
          {form.categorie && (
            <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>Référence Convention Commerce — {form.categorie}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--blue)" }}>{(CONVENTION_COMMERCE[form.categorie]?.sal || 0).toLocaleString("fr-FR")} FCFA/mois</div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>Le salaire de base est généralement aligné sur ce barème</div>
            </div>
          )}
          <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 8, padding: "10px 14px", fontSize: 11.5, color: "var(--blue)" }}>
            ℹ️ Ces éléments pré-rempliront automatiquement la fiche de paie lors de la génération. Saisissez toujours le <strong>Salaire Brut</strong>.
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px" }}>Éléments de base</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Salaire Brut de Base ({form.devise || "EUR"}) *</label>
              <input type="number" className="finp" value={form.salaire_base || ""} onChange={e => setForm(f => ({ ...f, salaire_base: e.target.value }))} placeholder="Calculé via la catégorie" />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Sursalaire ({form.devise || "EUR"})</label>
              <input type="number" className="finp" placeholder="0" value={form.sursalaire || ""} onChange={e => setForm(f => ({ ...f, sursalaire: e.target.value }))} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Prime d'ancienneté</label>
              <input type="number" className="finp" placeholder="0" value={form.prime_anciennete || ""} onChange={e => setForm(f => ({ ...f, prime_anciennete: e.target.value }))} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Avantages en nature</label>
              <input type="number" className="finp" placeholder="0" value={form.avantages_nature || ""} onChange={e => setForm(f => ({ ...f, avantages_nature: e.target.value }))} />
            </div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginTop: 4 }}>Primes variables</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Prime de performance</label>
              <input type="number" className="finp" placeholder="0" value={form.prime_performance || ""} onChange={e => setForm(f => ({ ...f, prime_performance: e.target.value }))} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Autres primes</label>
              <input type="number" className="finp" placeholder="0" value={form.autres_primes || ""} onChange={e => setForm(f => ({ ...f, autres_primes: e.target.value }))} />
            </div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginTop: 4 }}>Indemnités</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Indemnité de transport</label>
              <input type="number" className="finp" placeholder="0" value={form.indem_transport || ""} onChange={e => setForm(f => ({ ...f, indem_transport: e.target.value }))} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Indemnité de repas</label>
              <input type="number" className="finp" placeholder="0" value={form.indem_repas || ""} onChange={e => setForm(f => ({ ...f, indem_repas: e.target.value }))} />
            </div>
          </div>
        </div>
      )}

      {/* ONGLET ÉTAT CIVIL */}
      {empTab === "civil" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 8, padding: "10px 14px", fontSize: 11, color: "var(--blue)" }}>
            ℹ️ Ces données sont utilisées pour le calcul de l'IR, du TRIMF, et pour la conformité des bulletins de paie.
          </div>

          {/* Identité */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px" }}>Identité</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Genre</label>
              <select className="fsel" value={form.genre || ""} onChange={e => {
                const g = e.target.value;
                const isFemme = g === "Femme";
                setForm(f => ({
                  ...f,
                  genre: g,
                  poste: isFemme && f.poste ? formatPoste(f.poste, { genre: "Femme" }) : f.poste,
                  nb_epouses: isFemme ? "0" : (f.nb_epouses || "0")
                }));
              }}>
                <option value="">Choisir…</option>
                <option value="Homme">Homme</option>
                <option value="Femme">Femme</option>
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0 }}><label className="flbl">Date de naissance</label><input type="date" className="finp" value={form.date_naissance || ""} onChange={e => setForm(f => ({ ...f, date_naissance: e.target.value }))} /></div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}><label className="flbl">Lieu de naissance</label><input className="finp" placeholder="DAKAR" value={form.lieu_naissance || ""} onChange={e => setForm(f => ({ ...f, lieu_naissance: e.target.value.toUpperCase() }))} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}><label className="flbl">Nationalité</label><input className="finp" placeholder="Sénégalaise" value={form.nationalite || ""} onChange={e => setForm(f => ({ ...f, nationalite: e.target.value }))} /></div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">N° CNI ou Passeport</label>
              <input className="finp" placeholder="1234567890123" value={form.num_cni || ""} onChange={e => setForm(f => ({ ...f, num_cni: e.target.value }))} />
            </div>
          </div>
          {isSN && (
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">N° IPRES</label>
              <input className="finp" placeholder="N° affilié IPRES" value={form.num_ipres || ""} onChange={e => setForm(f => ({ ...f, num_ipres: e.target.value }))} />
            </div>
          )}

          {/* Situation matrimoniale */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginTop: 4 }}>Situation matrimoniale</div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="flbl">Statut</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
              {["Célibataire", "Marié(e)", "Divorcé(e)", "Veuf/Veuve"].map(s => (
                <button key={s} type="button" onClick={() => {
                  setForm(f => {
                    const isFemme = f.genre === "Femme";
                    const nEp = (s === "Marié(e)" && !isFemme) ? Math.max(1, parseInt(f.nb_epouses || 1)) : 0;
                    const nEnf = parseInt(f.nb_enfants || 0);
                    const parts = Math.min(5, 1 + (s === "Marié(e)" ? (isFemme ? 0.5 : nEp * 0.5) : 0) + Math.min(4, nEnf) * 0.5);
                    return { ...f, situation_matrimoniale: s, nb_epouses: String(nEp), parts_fiscales: String(parts) };
                  });
                }} style={{
                  padding: "7px 14px", borderRadius: 8, border: "1.5px solid",
                  borderColor: form.situation_matrimoniale === s ? "var(--blue)" : "var(--border)",
                  background: form.situation_matrimoniale === s ? "var(--blue-ll)" : "var(--white)",
                  color: form.situation_matrimoniale === s ? "var(--blue)" : "var(--text2)",
                  fontSize: 12, fontWeight: form.situation_matrimoniale === s ? 700 : 400, cursor: "pointer", transition: "all .12s"
                }}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {isSN && form.genre !== "Femme" && (
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Nombre d'épouses</label>
                <select className="fsel" value={form.nb_epouses || "0"} onChange={e => {
                  const nEp = parseInt(e.target.value);
                  const nEnf = parseInt(form.nb_enfants || 0);
                  const isMarie = form.situation_matrimoniale === "Marié(e)";
                  const parts = Math.min(5, 1 + (isMarie ? nEp * 0.5 : 0) + Math.min(4, nEnf) * 0.5);
                  setForm(f => ({ ...f, nb_epouses: String(nEp), parts_fiscales: String(parts) }));
                }}>
                  {["0", "1", "2", "3", "4"].map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
            )}
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Nombre d'enfants à charge</label>
              <select className="fsel" value={form.nb_enfants || "0"} onChange={e => {
                const nEnf = parseInt(e.target.value);
                const nEp = parseInt(form.nb_epouses || 0);
                const isMarie = form.situation_matrimoniale === "Marié(e)";
                const parts = Math.min(5, 1 + (isMarie ? nEp * 0.5 : 0) + Math.min(4, nEnf) * 0.5);
                setForm(f => ({ ...f, nb_enfants: String(nEnf), parts_fiscales: String(parts) }));
              }}>
                {["0", "1", "2", "3", "4", "5", "6", "7", "8"].map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Résumé parts calculées */}
          {isSN && form.situation_matrimoniale && (
            <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>🧮</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#065F46" }}>Parts fiscales : {form.parts_fiscales || 1}</div>
                <div style={{ fontSize: 11, color: "#047857" }}>
                  {form.situation_matrimoniale} · {form.nb_epouses || 0} épouse(s) · {form.nb_enfants || 0} enfant(s)
                  {" → "}{form.situation_matrimoniale === "Célibataire" ? "1 part de base" : `1 part + ${parseFloat(form.nb_epouses || 0) * 0.5} épouse(s) + ${Math.min(4, parseInt(form.nb_enfants || 0)) * 0.5} enfant(s)`}
                </div>
              </div>
            </div>
          )}

          {/* Conjoint */}
          {form.situation_matrimoniale === "Marié(e)" && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginTop: 4 }}>Conjoint(e)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="field" style={{ marginBottom: 0 }}><label className="flbl">Prénom et Nom du conjoint</label><input className="finp" placeholder="Prénom NOM" value={form.nom_conjoint || ""} onChange={e => setForm(f => ({ ...f, nom_conjoint: e.target.value }))} /></div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="flbl">Régime matrimonial</label>
                  <select className="fsel" value={form.regime_matrimonial || ""} onChange={e => setForm(f => ({ ...f, regime_matrimonial: e.target.value }))}>
                    <option value="">Choisir…</option>
                    <option>Communauté de biens</option>
                    <option>Séparation de biens</option>
                    <option>Polygamie</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Adresse */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginTop: 4 }}>Adresse</div>
          <div className="field" style={{ marginBottom: 0 }}><label className="flbl">Adresse complète</label><input className="finp" placeholder="Rue, quartier, ville" value={form.adresse || ""} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}><label className="flbl">Téléphone</label><input className="finp" placeholder="+221 77 000 00 00" value={form.telephone || ""} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} /></div>
            {!isSN && <div className="field" style={{ marginBottom: 0 }}><label className="flbl">N° Sécurité Sociale</label><input className="finp" placeholder="1 XX XX XX XXX XXX XX" value={form.num_secu || ""} onChange={e => setForm(f => ({ ...f, num_secu: e.target.value }))} /></div>}
          </div>
        </div>
      )}

      {/* ONGLET CONGÉS */}
      {empTab === "conges" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>Calculateur de solde légal ({form.site === "France" ? "France" : "Sénégal"})</div>
            {(() => {
              const res = calculateLeaveBalance(form, conges);
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 10 }}>
                  <div style={{ textAlign: "center", padding: 10, background: "#fff", borderRadius: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--blue)" }}>{res.acquisition}</div>
                    <div style={{ fontSize: 10, color: "var(--text3)" }}>Cumul congés</div>
                  </div>
                  <div style={{ textAlign: "center", padding: 10, background: "#fff", borderRadius: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--green)" }}>+{res.bonus}</div>
                    <div style={{ fontSize: 10, color: "var(--text3)" }}>Bonus légal</div>
                  </div>
                  <div style={{ textAlign: "center", padding: 10, background: "#fff", borderRadius: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--red)" }}>{res.acquis_annee}</div>
                    <div style={{ fontSize: 10, color: "var(--text3)" }}>Acquis année</div>
                  </div>
                  <div style={{ textAlign: "center", padding: 10, background: "var(--blue)", borderRadius: 8, color: "#fff" }}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{res.solde}</div>
                    <div style={{ fontSize: 10, opacity: 0.8 }}>Reste à prendre</div>
                  </div>
                </div>
              );
            })()}
          </div>

          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px" }}>Configuration du compteur</div>
          <div className="field">
            <label className="flbl">Date de dernière reprise (ou début calcul)</label>
            <input type="date" className="finp" value={form.date_reprise_reference || ""} onChange={e => setForm(f => ({ ...f, date_reprise_reference: e.target.value }))} />
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>L'acquisition de {form.site === "France" ? "2,5j" : "2j"}/mois démarre à cette date. Laisser vide pour utiliser la date d'embauche.</div>
          </div>

          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px" }}>Ajustement manuel</div>
          <div className="field">
            <label className="flbl">Nombre de jours à ajouter/soustraire</label>
            <input type="number" className="finp" placeholder="ex: +5 ou -2" value={form.solde_conges_ajustement || ""} onChange={e => setForm(f => ({ ...f, solde_conges_ajustement: e.target.value }))} />
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Sert à régulariser le solde (reliquats, reports exceptionnels, etc.)</div>
          </div>
          <div className="field">
            <label className="flbl">Note sur l'ajustement</label>
            <textarea className="fta" placeholder="Raison de l'ajustement..." value={form.solde_conges_note || ""} onChange={e => setForm(f => ({ ...f, solde_conges_note: e.target.value }))} />
          </div>

          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px" }}>Détails légaux appliqués</div>
          <div style={{ fontSize: 12, color: "var(--text2)", display: "flex", flexDirection: "column", gap: 8 }}>
            {(() => {
              const res = calculateLeaveBalance(form);
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Acquisition ({form.site === "France" ? "2,5j" : "2j"} / mois travaillé)</span>
                    <span style={{ fontWeight: 600 }}>{res.acquisition} j</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Bonus Ancienneté</span>
                    <span style={{ fontWeight: 600 }}>+{res.bonusSeniority} j</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Bonus Mère de famille</span>
                    <span style={{ fontWeight: 600 }}>+{res.bonusMothers} j</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminApp({ user, onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const [selectedSite, setSelectedSite] = useState(adminSite(user));
  const [employes, setEmployes] = useState([]);
  const [allEmployes, setAllEmployes] = useState([]); // tous sites — pour messagerie
  const [fiches, setFiches] = useState([]);
  const [conges, setConges] = useState([]);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgUnread, setMsgUnread] = useState(0); // badge messagerie nav
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [myThreads, setMyThreads] = useState([]); // Pour filtrer les notifs messages
  const [search, setSearch] = useState("");
  const [filtreC, setFiltreC] = useState("Toutes");
  const [filtreA, setFiltreA] = useState("Toutes");
  const [toasts, setToasts] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [filterMois, setFilterMois] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [filterAnnee, setFilterAnnee] = useState(String(new Date().getFullYear()));
  const [filterStatut, setFilterStatut] = useState("all"); // all | brouillon | publie
  const [confirmModal, setConfirmModal] = useState(null); // {title, message, onConfirm, danger}
  const [reportMois, setReportMois] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [reportAnnee, setReportAnnee] = useState(String(new Date().getFullYear()));
  const [exportingZip, setExportingZip] = useState(false);
  const notifRef = useRef(null);
  const MOIS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
  const tabRef = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  useEffect(() => {
    const site = adminSite(user);

    // Si admin global (all), on charge tous les employés et toutes les fiches (Sénégal & France)
    const empQ = site === "all"
      ? supabase.from("employes").select("*").order("nom")
      : supabase.from("employes").select("*").eq("site", site).order("nom");

    const ficheQ = site === "all"
      ? supabase.from("fiches_paie").select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").order("annee", { ascending: false }).order("mois", { ascending: false })
      : supabase.from("fiches_paie").select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").eq("site", site).order("annee", { ascending: false }).order("mois", { ascending: false });

    // Tous les employés actifs — pour la messagerie
    const allEmpQ = supabase.from("employes").select("id,prenom,nom,poste,site,avatar_initials,avatar_color,actif").eq("actif", true).order("prenom");

    Promise.all([
      empQ, ficheQ,
      supabase.from("conges").select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").order("created_at", { ascending: false }),
      supabase.from("absences").select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").order("created_at", { ascending: false }),
      supabase.from("annonces").select("*").order("created_at", { ascending: false }),
      allEmpQ,
    ]).then(([e, f, c, a, an, allEmp]) => {
      const empIds = new Set((e.data || []).map(x => x.id));
      setEmployes(e.data || []);
      setFiches(f.data || []);
      setConges((c.data || []).filter(x => empIds.has(x.employe_id)));
      setAbsences((a.data || []).filter(x => empIds.has(x.employe_id)));
      setAnnonces(an.data || []);

      // Messagerie : l'admin global (all) voit tout le monde, sinon filtré par son site
      if (site === "all") {
        setAllEmployes(allEmp.data || []);
      } else {
        setAllEmployes((allEmp.data || []).filter(x => x.site === site));
      }

      supabase.from("threads").select("id").contains("participants", [user.id]).then(res => {
        if (res.data) setMyThreads(res.data.map(t => t.id));
      });
      setLoading(false);
    });
  }, []);

  const pushToast = (ic, t, s) => { const id = Date.now(); setToasts(p => [...p, { id, ic, t, s }]); setTimeout(() => setToasts(p => p.filter(x => x.id !== id)), 5000); };
  const pushNotif = (t, s) => setNotifs(p => [{ id: Date.now(), t, s, unread: true }, ...p].slice(0, 30));

  const exportEmployesCSV = () => {
    const actives = employes.filter(e => e.actif !== false);
    const headers = ["Prénom", "Nom", "Email", "Poste", "Département", "Site", "Date d'embauche", "Catégorie", "Login"];
    const rows = actives.map(e => [
      e.prenom || "",
      e.nom || "",
      e.email || "",
      e.poste || "",
      e.departement || "",
      e.site || "",
      e.date_entree || "",
      e.categorie || "",
      e.login || ""
    ]);

    const csvContent = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute("href", url);
    link.setAttribute("download", `equipe_dakar_${date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const ch = supabase.channel("adm-abs").on("postgres_changes", { event: "INSERT", schema: "public", table: "absences" }, async (p) => {
      const { data } = await supabase.from("absences").select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").eq("id", p.new.id).single();
      if (!data) return;
      setAbsences(prev => [data, ...prev]);
      const nom = (data.employes?.prenom || "") + " " + (data.employes?.nom || "");
      pushToast("📅", `Absence — ${nom}`, `${data.type_absence} · ${data.nb_jours} jour${data.nb_jours !== 1 ? "s" : ""}`);
      pushNotif(`Absence — ${nom}`, `${data.type_absence} · ${fDs(data.date_debut)} → ${fDs(data.date_fin)}`);
    }).on("postgres_changes", { event: "UPDATE", schema: "public", table: "absences" }, (p) => setAbsences(prev => prev.map(a => a.id === p.new.id ? { ...a, ...p.new } : a))).subscribe();
    const ch2 = supabase.channel("adm-con").on("postgres_changes", { event: "INSERT", schema: "public", table: "conges" }, async (p) => {
      const { data } = await supabase.from("conges").select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").eq("id", p.new.id).single();
      if (!data) return;
      setConges(prev => [data, ...prev]);
      const nom = (data.employes?.prenom || "") + " " + (data.employes?.nom || "");
      pushToast("🌴", `Congés — ${nom}`, `${data.type_conge} · ${data.nb_jours} jour${data.nb_jours !== 1 ? "s" : ""}`);
      pushNotif(`Congés — ${nom}`, `${data.type_conge} · ${fDs(data.date_debut)} → ${fDs(data.date_fin)}`);
    }).on("postgres_changes", { event: "UPDATE", schema: "public", table: "conges" }, (p) => setConges(prev => prev.map(c => c.id === p.new.id ? { ...c, ...p.new } : c))).subscribe();
    // ── Listener messages en arrière-plan pour badge nav ──
    const ch3 = supabase.channel("msg-badge-adm-" + user.id)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (p) => {
        if (p.new.sender_id === user.id) return;
        if (tabRef.current !== "messagerie") setMsgUnread(prev => prev + 1);
        pushNotif("💬 Message reçu", p.new.content || "Fichier joint");
      }).subscribe();
    return () => { supabase.removeChannel(ch); supabase.removeChannel(ch2); supabase.removeChannel(ch3); };
  }, []);

  // Polling badge messagerie admin — persisté via localStorage
  useEffect(() => {
    // Charger le badge initial depuis la dernière visite
    const stored = localStorage.getItem("majha_msg_last_seen_" + user.id);
    if (stored) {
      supabase.from("threads").select("id").contains("participants", [user.id]).then(res => {
        if (!res.data || res.data.length === 0) return;
        const ids = res.data.map(t => t.id);
        supabase.from("messages").select("id").in("thread_id", ids).neq("sender_id", user.id).gt("created_at", stored).then(r => {
          if (r.data && r.data.length > 0) setMsgUnread(r.data.length);
        });
      });
    }
    const lastCheckRef = { current: stored || new Date(Date.now() - 86400000).toISOString() };
    const poll = setInterval(async () => {
      if (tabRef.current === "messagerie") {
        lastCheckRef.current = new Date().toISOString();
        localStorage.setItem("majha_msg_last_seen_" + user.id, lastCheckRef.current);
        return;
      }
      const { data: threads } = await supabase.from("threads").select("id").contains("participants", [user.id]);
      if (!threads || threads.length === 0) return;
      const ids = threads.map(t => t.id);
      const { data: msgs } = await supabase.from("messages").select("id").in("thread_id", ids).neq("sender_id", user.id).gt("created_at", lastCheckRef.current);
      if (msgs && msgs.length > 0) { setMsgUnread(msgs.length); pushNotif("💬 Message reçu", `${msgs.length} nouveau${msgs.length > 1 ? "x" : ""} message${msgs.length > 1 ? "s" : ""}`); }
      lastCheckRef.current = new Date().toISOString();
    }, 8000);
    return () => clearInterval(poll);
  }, [user.id]);

  useEffect(() => {
    const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  const traiterC = async (id, statut) => {
    await supabase.from("conges").update({ statut, traite_par: user.prenom, traite_at: new Date().toISOString() }).eq("id", id);
    setConges(p => p.map(c => c.id === id ? { ...c, statut } : c));
    const c = conges.find(x => x.id === id);
    if (c) {
      try {
        await supabase.from("notifications").insert({
          employe_id: c.employe_id,
          titre: "Demande de congé",
          message: `Votre demande de congé du ${fDs(c.date_debut)} a été ${statut === "approuve" ? "approuvée" : "refusée"}.`,
          lien: "demandes",
          lu: false
        });
      } catch (e) { }
    }
  };
  const traiterA = async (id, statut) => {
    await supabase.from("absences").update({ statut, traite_par: user.prenom, traite_at: new Date().toISOString() }).eq("id", id);
    setAbsences(p => p.map(a => a.id === id ? { ...a, statut } : a));
    const a = absences.find(x => x.id === id);
    if (a) {
      try {
        await supabase.from("notifications").insert({
          employe_id: a.employe_id,
          titre: "Demande d'absence",
          message: `Votre demande d'absence du ${fDs(a.date_debut)} a été ${statut === "approuve" ? "approuvée" : "refusée"}.`,
          lien: "demandes",
          lu: false
        });
      } catch (e) { }
    }
  };




  const getLastBrut = (eid) => fiches.find(f => f.employe_id === eid)?.salaire_brut || 0;
  const calcRetAbs = (eid, mois, annee, brut) => {
    if (!brut || !mois || !annee) return 0;
    const lst = absences.filter(a => a.employe_id === eid && a.statut === "approuve" && a.type_absence === "Absence injustifiée" && new Date(a.date_debut).getMonth() + 1 === parseInt(mois) && new Date(a.date_debut).getFullYear() === parseInt(annee));
    return Math.round((brut / 30) * lst.reduce((s, a) => s + (a.nb_jours || 0), 0));
  };

  const genererFiche = async () => {
    if (!form.employe_id || !form.mois || !form.salaire_base) return;
    setSaving(true);
    try {
      const emp = employes.find(e => e.id === form.employe_id);
      const isXOF = emp?.devise === "XOF" || emp?.site !== "France" || form.devise === "XOF";
      const rawGenre = String(form.genre || emp?.genre || emp?.sexe || "").trim().toLowerCase();
      const isFemale = rawGenre === "femme" || rawGenre === "f" || rawGenre === "female" || 
        (emp?.prenom && ["habibatou", "habiba", "awa", "mame khady", "ngone", "jocelyne", "deguene", "bitylokho", "bethy", "maimouna"].some(fn => String(emp.prenom).toLowerCase().includes(fn)));
      // Parts fiscales : prendre celles du formulaire, sinon celles enregistrées sur l'employé
      const partsFiscales = form.parts_fiscales || emp?.parts_fiscales || 1;
      const nbEpouses = isFemale ? 0 : (form.nb_epouses || emp?.nb_epouses || 0);
      const nbEnfants = form.nb_enfants || emp?.nb_enfants || 0;
      const isCadre = form.regime_cadre === "oui" || (form.categorie && CONVENTION_COMMERCE[form.categorie]?.cadre) || (emp?.categorie && CONVENTION_COMMERCE[emp?.categorie]?.cadre);
      const withIPM = form.with_ipm !== "non";

      const calcForm = {
        ...form,
        parts_fiscales: String(partsFiscales),
        nb_epouses: String(nbEpouses),
        nb_enfants: String(nbEnfants),
        regime_cadre: isCadre ? "oui" : "non",
        with_ipm: withIPM ? "oui" : "non"
      };
      const calc = calcPayeSN(calcForm, isXOF);
      const leaveRes = calculateLeaveBalance(emp, conges);
      const cAcquis = leaveRes.cumul_conges;
      const cPris = leaveRes.acquis_annee;
      const cRestants = leaveRes.solde;

      const anneeFiche = parseInt(form.annee || new Date().getFullYear());
      const pdfData = {
        employe: {
          ...emp,
          genre: emp?.genre || form.genre || null,
          date_naissance: emp?.date_naissance || null,
          lieu_naissance: emp?.lieu_naissance || "DAKAR",
          num_ipres: emp?.num_ipres || null,
        },
        mois: form.mois, annee: anneeFiche,
        devise: isXOF ? "XOF" : (emp?.devise || form.devise || "EUR"),
        site: emp?.site || form.site || "Dakar",
        horaire: form.horaire || (isXOF ? "173,33" : "151,67"),
        categorie: form.categorie || emp?.categorie || "—",
        matricule: form.matricule || emp?.login,
        type_contrat: form.type_contrat || "cdi", pct_smic: form.pct_smic,
        ...calcForm,
        salaire_base: n(form.salaire_base), sursalaire: n(form.sursalaire), indem_conges: n(form.indem_conges),
        heures_absence: n(form.heures_absence),
        prime_anciennete: n(form.prime_anciennete), prime_performance: n(form.prime_performance),
        autres_primes: n(form.autres_primes), indem_transport: n(form.indem_transport),
        indem_repas: n(form.indem_repas), avantages_nature: n(form.avantages_nature),
        remb_pret: n(form.remb_pret), avance_salaire: n(form.avance_salaire),
        retenue_frais_medicaux: n(form.retenue_frais_medicaux),
        nb_epouses: nbEpouses, nb_enfants: nbEnfants,
        parts_ir: partsFiscales, parts_trimf: 1,
        calc,
        conges_acquis: cAcquis, conges_pris: cPris, conges_restants: cRestants,
      };
      const { doc, nom } = await genPDF(pdfData);
      const blob = doc.output("blob");
      const path = `fiches/${form.employe_id}/${anneeFiche}_${form.mois}_${nom}`;
      const { error } = await supabase.storage.from("Documents").upload(path, new File([blob], nom, { type: "application/pdf" }), { upsert: true });
      if (error) { setSaving(false); alert("Erreur upload: " + error.message); return; }
      const { data: urlD } = supabase.storage.from("Documents").getPublicUrl(path);

      const existant = fiches.find(f => f.employe_id === form.employe_id && String(f.mois) === String(form.mois) && Number(f.annee) === anneeFiche);
      const snapSuffix = "||SNAP:" + JSON.stringify(buildBulletinSnap(emp, calcForm, calc));
      const uploaderWithSnap = "[BROUILLON] " + (user.prenom || "") + " " + (user.nom || "") + snapSuffix;

      let fiche;
      if (existant) {
        const { data: updF, error: updErr } = await supabase.from("fiches_paie").update({
          salaire_brut: calc.brut, salaire_net: Math.max(0, calc.net),
          url: urlD.publicUrl, nom_fichier: nom,
          uploaded_by: uploaderWithSnap,
          devise: isXOF ? "XOF" : (emp?.devise || "EUR"), site: emp?.site || "Dakar",
          conges_acquis: cAcquis, conges_pris: cPris, conges_restants: cRestants
        }).eq("id", existant.id).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();
        if (updErr) { setSaving(false); alert("Erreur mise à jour: " + updErr.message); return; }
        fiche = updF;
        setFiches(p => p.map(x => x.id === existant.id ? fiche : x));
      } else {
        const { data: insF, error: insErr } = await supabase.from("fiches_paie").insert({
          employe_id: form.employe_id, mois: form.mois,
          annee: anneeFiche,
          salaire_brut: calc.brut, salaire_net: Math.max(0, calc.net),
          url: urlD.publicUrl, nom_fichier: nom,
          uploaded_by: uploaderWithSnap,
          devise: isXOF ? "XOF" : (emp?.devise || "EUR"), site: emp?.site || "Dakar",
          conges_acquis: cAcquis, conges_pris: cPris, conges_restants: cRestants
        }).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();
        if (insErr) { setSaving(false); alert("Erreur insertion: " + insErr.message); return; }
        fiche = insF;
        setFiches(p => [fiche, ...p]);
      }

      pushToast("🟡", "Brouillon généré", `Le bulletin de ${emp?.prenom} ${emp?.nom} a été généré en mode brouillon (non visible par le salarié).`);
      setModal(null); setForm({});
    } catch (err) {
      console.error("Erreur génération fiche:", err);
      alert("Erreur lors de la génération du bulletin : " + (err?.message || String(err)));
    } finally {
      setSaving(false);
    }
  };

  const uploaderBulletinPDF = async () => {
    if (!form.employe_id || !form.mois || !form.annee || !form._file) {
      alert("Veuillez sélectionner un collaborateur, la période et le fichier PDF.");
      return;
    }
    setSaving(true);
    try {
      const emp = employes.find(e => e.id === form.employe_id);
      const file = form._file;
      const isPDF = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!isPDF) {
        alert("Veuillez sélectionner un fichier PDF valide.");
        return;
      }

      const moisStr = String(form.mois).padStart(2, "0");
      const anneeNum = parseInt(form.annee);
      const cleanNom = ("Bulletin_" + (emp?.prenom || "Collab") + "_" + (emp?.nom || "") + "_" + anneeNum + "_" + moisStr + ".pdf")
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9._-]/g, "_");

      const path = `fiches/${form.employe_id}/${anneeNum}_${moisStr}_${cleanNom}`;
      const { error: upErr } = await supabase.storage.from("Documents").upload(path, file, { upsert: true });
      if (upErr) {
        alert("Erreur upload du document: " + upErr.message);
        return;
      }

      const { data: urlD } = supabase.storage.from("Documents").getPublicUrl(path);
      let extractedNet = parseFloat(form.salaire_net) || null;
      let extractedBrut = parseFloat(form.salaire_brut) || null;
      if (!extractedNet) {
        const extracted = await extractSalaryFromPdfFile(file);
        if (extracted.net) {
          extractedNet = extracted.net;
          if (extracted.brut) extractedBrut = extracted.brut;
        }
      }

      const uploaderName = `${user?.prenom || ""} ${user?.nom || ""}`.trim() || "Admin";
      const uploaderTag = form.publier_direct ? uploaderName : `[BROUILLON] ${uploaderName}`;

      const existant = fiches.find(f => f.employe_id === form.employe_id && String(f.mois).padStart(2, "0") === moisStr && Number(f.annee) === anneeNum);

      let fiche;
      if (existant) {
        const { data: updF, error: updErr } = await supabase.from("fiches_paie").update({
          salaire_brut: extractedBrut,
          salaire_net: extractedNet,
          url: urlD.publicUrl,
          nom_fichier: cleanNom,
          uploaded_by: uploaderTag,
          devise: emp?.devise || (emp?.site === "France" ? "EUR" : "XOF"),
          site: emp?.site || (mySite === "France" ? "France" : "Dakar"),
        }).eq("id", existant.id).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();
        if (updErr) {
          alert("Erreur mise à jour: " + updErr.message);
          return;
        }
        fiche = updF;
        setFiches(p => p.map(x => x.id === existant.id ? fiche : x));
      } else {
        const { data: insF, error: insErr } = await supabase.from("fiches_paie").insert({
          employe_id: form.employe_id,
          mois: moisStr,
          annee: anneeNum,
          salaire_brut: extractedBrut,
          salaire_net: extractedNet,
          url: urlD.publicUrl,
          nom_fichier: cleanNom,
          uploaded_by: uploaderTag,
          devise: emp?.devise || (emp?.site === "France" ? "EUR" : "XOF"),
          site: emp?.site || (mySite === "France" ? "France" : "Dakar"),
        }).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();
        if (insErr) {
          alert("Erreur enregistrement: " + insErr.message);
          return;
        }
        fiche = insF;
        setFiches(p => [fiche, ...p]);
      }

      if (form.publier_direct) {
        try {
          await supabase.from("notifications").insert({
            employe_id: form.employe_id,
            titre: "Nouveau bulletin de paie disponible",
            message: `Votre bulletin de paie de ${MONTHS[parseInt(moisStr) - 1]} ${anneeNum} est disponible en téléchargement.`,
            lien: "fiches",
            lu: false
          });
        } catch (e) { }
        pushToast("📄", "Bulletin publié", `Le bulletin de ${emp?.prenom} ${emp?.nom} a été publié avec succès.`);
      } else {
        pushToast("🟡", "Brouillon déposé", `Le bulletin de ${emp?.prenom} ${emp?.nom} est en attente de validation (non visible).`);
      }

      setModal(null);
      setForm({});
    } catch (err) {
      console.error("Erreur upload bulletin:", err);
      alert("Erreur lors du téléversement du bulletin : " + (err?.message || String(err)));
    } finally {
      setSaving(false);
    }
  };

  const exportBulletinsZIP = async () => {
    const fichesSite = fiches.filter(f => {
      if (mySite === "all") return true;
      if (mySite === "France") return f.site === "France" || f.employes?.site === "France";
      return f.site !== "France" && f.employes?.site !== "France";
    });
    const fichesFiltrees = fichesSite.filter(f => {
      const m = !search || (f.employes?.nom + f.employes?.prenom).toLowerCase().includes(search.toLowerCase());
      const mo = filterMois === "all" || f.mois === filterMois;
      const an = filterAnnee === "all" || String(f.annee) === filterAnnee;
      return m && mo && an;
    });

    if (fichesFiltrees.length === 0) return;

    setExportingZip(true);
    try {
      const zip = new JSZip();
      
      let moisNom = "Multi-Mois";
      if (filterMois !== "all") {
        moisNom = MONTHS[parseInt(filterMois) - 1].substring(0, 4).toUpperCase();
      }
      
      const anneeNom = filterAnnee === "all" ? "Toutes-Annees" : filterAnnee;
      const folderName = `Fiches de Paie MAJHA ${moisNom} ${anneeNom}`;
      const folder = zip.folder(folderName);

      for (const f of fichesFiltrees) {
        if (!f.url) continue;
        const response = await fetch(f.url);
        if (!response.ok) throw new Error("Erreur de téléchargement du bulletin: " + f.nom_fichier);
        const blob = await response.blob();
        folder.file(f.nom_fichier, blob);
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${folderName}.zip`);
    } catch (e) {
      console.error(e);
      alert("Erreur lors du téléchargement des bulletins: " + e.message);
    } finally {
      setExportingZip(false);
    }
  };

  const genererTousBulletins = async () => {
    const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
    const currentYear = new Date().getFullYear();

    // Génération automatique réservée à Dakar / Sénégal
    const activeEmps = employes.filter(e => e.actif && e.site !== "France" && (mySite === "all" || (e.site || "Dakar") === mySite));

    if (activeEmps.length === 0) {
      alert("Aucun collaborateur actif à Dakar trouvé.");
      return;
    }

    if (!window.confirm(`Générer / Re-générer les bulletins de paie de ${MONTHS[parseInt(currentMonth) - 1]} ${currentYear} pour les ${activeEmps.length} collaborateur(s) de Dakar ?`)) return;

    setBulkProgress({ current: 0, total: activeEmps.length, done: false, errors: 0 });
    let updatedFiches = [...fiches];
    let errCount = 0;

    for (let i = 0; i < activeEmps.length; i++) {
      const emp = activeEmps[i];
      const isXOF = true;

      const leaveRes = calculateLeaveBalance(emp, conges);
      const cAcquis = leaveRes.cumul_conges;
      const cPris = leaveRes.acquis_annee;
      const cRestants = leaveRes.solde;

      const rawGenre = String(emp.genre || emp.sexe || "").trim().toLowerCase();
      const isFemale = rawGenre === "femme" || rawGenre === "f" || rawGenre === "female" || 
        (emp.prenom && ["habibatou", "habiba", "awa", "mame khady", "ngone", "jocelyne", "deguene", "bitylokho", "bethy", "maimouna"].some(fn => String(emp.prenom).toLowerCase().includes(fn)));

      const partsFiscales = emp.parts_fiscales || 1;
      const nbEpouses = isFemale ? 0 : (emp.nb_epouses || 0);
      const nbEnfants = emp.nb_enfants || 0;
      const isCadre = emp.regime_cadre === "oui" || (emp.categorie && CONVENTION_COMMERCE[emp.categorie]?.cadre);
      const withIPM = emp.with_ipm !== "non";

      const calcForm = {
        salaire_base: emp.salaire_base || 0,
        sursalaire: emp.sursalaire || 0,
        prime_anciennete: emp.prime_anciennete || 0,
        prime_performance: emp.prime_performance || 0,
        indem_transport: emp.indem_transport || 0,
        indem_repas: emp.indem_repas || 0,
        avantages_nature: emp.avantages_nature || 0,
        parts_fiscales: String(partsFiscales),
        nb_epouses: String(nbEpouses),
        nb_enfants: String(nbEnfants),
        heures_absence: 0,
        retenue_absences: 0,
        remb_pret: 0,
        avance_salaire: 0,
        regime_cadre: isCadre ? "oui" : "non",
        with_ipm: withIPM ? "oui" : "non",
        categorie: emp.categorie || "",
        horaire: emp.horaire || "173,33"
      };

      const calc = calcPayeSN(calcForm, isXOF);
      const nomFichier = ("Bulletin_" + emp.prenom + "_" + emp.nom + "_" + currentYear + "_" + currentMonth + ".pdf").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9._-]/g, "_");

      const pdfData = {
        employe: { ...emp, date_naissance: emp.date_naissance || null, lieu_naissance: emp.lieu_naissance || "DAKAR", num_ipres: emp.num_ipres || null },
        mois: currentMonth, annee: currentYear,
        devise: "XOF",
        site: emp.site || "Dakar",
        horaire: emp.horaire || "173,33",
        categorie: emp.categorie || "—",
        matricule: emp.login,
        type_contrat: emp.type_contrat || "cdi", pct_smic: 100,
        ...calcForm,
        salaire_base: n(calcForm.salaire_base), sursalaire: n(calcForm.sursalaire),
        prime_anciennete: n(calcForm.prime_anciennete), prime_performance: n(calcForm.prime_performance),
        indem_transport: n(calcForm.indem_transport), indem_repas: n(calcForm.indem_repas),
        avantages_nature: n(calcForm.avantages_nature),
        nb_epouses: nbEpouses, nb_enfants: nbEnfants,
        parts_ir: partsFiscales, parts_trimf: 1,
        calc,
        conges_acquis: cAcquis, conges_pris: cPris, conges_restants: cRestants
      };

      try {
        const { doc, nom } = await genPDF(pdfData);
        const blob = doc.output("blob");
        const path = `fiches/${emp.id}/${currentYear}_${currentMonth}_${nom}`;
        const { error: upErr } = await supabase.storage.from("Documents").upload(path, new File([blob], nom, { type: "application/pdf" }), { upsert: true });
        if (upErr) throw upErr;
        const { data: urlD } = supabase.storage.from("Documents").getPublicUrl(path);

        const existingFiche = fiches.find(f => f.employe_id === emp.id && f.mois === currentMonth && Number(f.annee) === currentYear);
        const snapSuffix = "||SNAP:" + JSON.stringify(buildBulletinSnap(emp, calcForm, calc));
        const uploaderWithSnap = "[BROUILLON] " + (user.prenom || "") + " " + (user.nom || "") + snapSuffix;

        let fiche;
        if (existingFiche) {
          const { data: updF, error: updErr } = await supabase.from("fiches_paie").update({
            salaire_brut: calc.brut, salaire_net: Math.max(0, calc.net),
            url: urlD.publicUrl, nom_fichier: nom,
            uploaded_by: uploaderWithSnap,
            devise: "XOF", site: emp.site || "Dakar",
            conges_acquis: cAcquis, conges_pris: cPris, conges_restants: cRestants
          }).eq("id", existingFiche.id).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();
          if (updErr) throw updErr;
          fiche = updF;
          updatedFiches = updatedFiches.map(f => f.id === existingFiche.id ? fiche : f);
        } else {
          const { data: insF, error: insErr } = await supabase.from("fiches_paie").insert({
            employe_id: emp.id, mois: currentMonth, annee: currentYear,
            salaire_brut: calc.brut, salaire_net: Math.max(0, calc.net),
            url: urlD.publicUrl, nom_fichier: nom,
            uploaded_by: uploaderWithSnap,
            devise: "XOF", site: emp.site || "Dakar",
            conges_acquis: cAcquis, conges_pris: cPris, conges_restants: cRestants
          }).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();
          if (insErr) throw insErr;
          fiche = insF;
          updatedFiches = [fiche, ...updatedFiches];
        }
      } catch (err) {
        console.error("Erreur bulk", emp.prenom, err);
        errCount++;
      }
      setBulkProgress(p => ({ ...p, current: i + 1 }));
    }

    setFiches(updatedFiches);
    setBulkProgress(p => ({ ...p, done: true, errors: errCount }));
    pushToast("📋", "Brouillons prêts", `Bulletins générés en mode brouillon. Vous pouvez les vérifier ci-dessous avant publication.`);
  };

  const publierFiche = async (fiche) => {
    try {
      const cleanUploader = formatUploadedBy(fiche.uploaded_by) || (user.prenom + " " + user.nom);
      const newUploader = "[PUBLIÉ] " + cleanUploader + getBulletinSnapRaw(fiche.uploaded_by);
      const { data: updated, error } = await supabase.from("fiches_paie").update({
        uploaded_by: newUploader
      }).eq("id", fiche.id).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();

      if (error) { alert("Erreur lors de la publication : " + error.message); return; }

      try {
        await supabase.from("notifications").insert({
          employe_id: fiche.employe_id,
          titre: "Nouveau bulletin de paie disponible",
          message: `Votre bulletin de paie de ${MONTHS[parseInt(fiche.mois) - 1]} ${fiche.annee} est disponible.`,
          lien: "fiches",
          lu: false
        });
      } catch (e) { }

      setFiches(prev => prev.map(f => f.id === fiche.id ? updated : f));
      pushToast("🚀", "Bulletin publié", `Le bulletin de ${fiche.employes?.prenom || "l'employé"} est désormais visible dans son espace.`);
    } catch (e) {
      alert("Erreur: " + e.message);
    }
  };

  const repasserBrouillonFiche = async (fiche) => {
    try {
      const cleanUploader = formatUploadedBy(fiche.uploaded_by) || (user.prenom + " " + user.nom);
      const newUploader = "[BROUILLON] " + cleanUploader + getBulletinSnapRaw(fiche.uploaded_by);
      const { data: updated, error } = await supabase.from("fiches_paie").update({
        uploaded_by: newUploader
      }).eq("id", fiche.id).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();

      if (error) { alert("Erreur : " + error.message); return; }

      setFiches(prev => prev.map(f => f.id === fiche.id ? updated : f));
      pushToast("🔒", "Passé en brouillon", `Le bulletin a été repassé en brouillon (masqué pour le salarié).`);
    } catch (e) {
      alert("Erreur: " + e.message);
    }
  };

  const validerEtPublierTous = async (moisFiltre, anneeFiltre) => {
    const drafts = fiches.filter(f => {
      if (!isBulletinBrouillon(f)) return false;
      const matchSite = mySite === "all" || (mySite === "France" ? (f.site === "France" || f.employes?.site === "France") : (f.site !== "France" && f.employes?.site !== "France"));
      const matchMois = !moisFiltre || moisFiltre === "all" || f.mois === moisFiltre;
      const matchAnnee = !anneeFiltre || anneeFiltre === "all" || String(f.annee) === String(anneeFiltre);
      return matchSite && matchMois && matchAnnee;
    });

    if (drafts.length === 0) {
      alert("Aucun bulletin en mode brouillon à publier.");
      return;
    }

    const labelPeriode = moisFiltre && moisFiltre !== "all" ? `${MONTHS[parseInt(moisFiltre) - 1]} ${anneeFiltre && anneeFiltre !== "all" ? anneeFiltre : ""}` : "sélectionnée";

    setConfirmModal({
      title: "Valider et publier les bulletins",
      message: `Êtes-vous sûr de vouloir valider et publier ${drafts.length} bulletin(s) de paie (${labelPeriode}) ? Ils deviendront immédiatement visibles par les salariés concernés.`,
      confirmLabel: `✓ Publier ${drafts.length} bulletin(s)`,
      onConfirm: async () => {
        setSaving(true);
        let updatedList = [...fiches];
        let pubCount = 0;

        for (const f of drafts) {
          const cleanUploader = formatUploadedBy(f.uploaded_by) || (user.prenom + " " + user.nom);
          const newUploader = "[PUBLIÉ] " + cleanUploader + getBulletinSnapRaw(f.uploaded_by);
          const { data: updated, error } = await supabase.from("fiches_paie").update({
            uploaded_by: newUploader
          }).eq("id", f.id).select("*,employes(nom,prenom,avatar_color,avatar_initials,devise)").single();

          if (!error && updated) {
            updatedList = updatedList.map(x => x.id === f.id ? updated : x);
            pubCount++;
            try {
              await supabase.from("notifications").insert({
                employe_id: f.employe_id,
                titre: "Nouveau bulletin de paie disponible",
                message: `Votre bulletin de paie de ${MONTHS[parseInt(f.mois) - 1]} ${f.annee} est disponible.`,
                lien: "fiches",
                lu: false
              });
            } catch (e) { }
          }
        }

        setFiches(updatedList);
        setSaving(false);
        pushToast("🎉", "Bulletins publiés", `${pubCount} bulletin(s) validé(s) et publié(s) aux salariés avec succès !`);
      }
    });
  };

  const creerEmploye = async () => {
    if (!form.prenom || !form.login) return;
    const cleanLogin = (form.login || "").trim().toLowerCase();
    const existingWithLogin = employes.find(e => (e.login || "").toLowerCase().trim() === cleanLogin);
    if (existingWithLogin) {
      alert(`L'identifiant "${cleanLogin}" est déjà utilisé par ${existingWithLogin.prenom} ${existingWithLogin.nom}. Veuillez choisir un identifiant différent (par exemple : ${generateUniqueLogin(form.prenom, form.nom, employes)}).`);
      return;
    }

    setSaving(true);
    const initials = ((form.prenom?.[0] || "") + (form.nom?.[0] || "")).toUpperCase();
    const colors = ["#0047FF", "#7C3AED", "#059669", "#DC2626", "#D97706", "#0E7490", "#9D174D", "#4C1D95"];
    const finalNote = setEmployeAdminRoleInNote(form.solde_conges_note, form._adminRole || "user");
    const civil = {
      date_naissance: form.date_naissance || null,
      lieu_naissance: form.lieu_naissance || null,
      nationalite: form.nationalite || null,
      num_cni: form.num_cni || null,
      num_ipres: form.num_ipres || null,
      num_secu: form.num_secu || null,
      situation_matrimoniale: form.situation_matrimoniale || null,
      nb_epouses: parseInt(form.nb_epouses || 0),
      nb_enfants: parseInt(form.nb_enfants || 0),
      nom_conjoint: form.nom_conjoint || null,
      regime_matrimonial: form.regime_matrimonial || null,
      adresse: form.adresse || null,
      telephone: form.telephone || null,
      parts_fiscales: parseFloat(form.parts_fiscales || 1),
      categorie: form.categorie || null,
      genre: form.genre || null,
      solde_conges_ajustement: parseFloat(form.solde_conges_ajustement) || 0,
      solde_conges_note: finalNote,
      date_reprise_reference: form.date_reprise_reference || null,
      // ── Rémunération ──
      salaire_base: parseFloat(form.salaire_base) || null,
      sursalaire: parseFloat(form.sursalaire) || null,
      prime_anciennete: parseFloat(form.prime_anciennete) || null,
      prime_performance: parseFloat(form.prime_performance) || null,
      autres_primes: parseFloat(form.autres_primes) || null,
      indem_transport: parseFloat(form.indem_transport) || null,
      indem_repas: parseFloat(form.indem_repas) || null,
      avantages_nature: parseFloat(form.avantages_nature) || null,
    };
    const payload = {
      prenom: form.prenom,
      nom: form.nom,
      poste: form.poste || null,
      departement: form.departement || null,
      email: form.email ? form.email.trim() : null,
      login: cleanLogin,
      date_entree: form.date_entree || null,
      site: form.site || "Dakar",
      devise: form.devise || "XOF",
      avatar_initials: initials,
      avatar_color: colors[Math.floor(Math.random() * colors.length)],
      actif: true,
      password: form.password || "majha2025",
      ...civil
    };
    const { data, error } = await supabase.from("employes").insert(payload).select().single();
    if (error) { setSaving(false); alert("Erreur: " + error.message); return; }
    if (data) setEmployes(p => [...p, data]);
    setSaving(false); setModal(null); setForm({});
    pushToast("👤", "Collaborateur créé", `${payload.prenom} ${payload.nom} a été ajouté avec succès.`);
  };

  const modifierEmploye = async () => {
    if (!form.id) return;
    const cleanLogin = (form.login || "").trim().toLowerCase();
    const existingWithLogin = employes.find(e => e.id !== form.id && (e.login || "").toLowerCase().trim() === cleanLogin);
    if (existingWithLogin) {
      alert(`L'identifiant "${cleanLogin}" est déjà utilisé par ${existingWithLogin.prenom} ${existingWithLogin.nom}. Veuillez choisir un identifiant différent (par exemple : ${generateUniqueLogin(form.prenom, form.nom, employes, form.id)}).`);
      return;
    }

    setSaving(true);
    const initials = ((form.prenom?.[0] || "") + (form.nom?.[0] || "")).toUpperCase();
    const finalNote = setEmployeAdminRoleInNote(form.solde_conges_note, form._adminRole !== undefined ? form._adminRole : getEmployeAdminRole(form));
    const upd = {
      prenom: form.prenom, nom: form.nom, poste: form.poste, departement: form.departement,
      email: form.email ? form.email.trim() : null, login: cleanLogin, date_entree: form.date_entree,
      site: form.site, devise: form.devise,
      avatar_initials: initials, avatar_color: form.avatar_color,
      // Champs état civil
      date_naissance: form.date_naissance || null,
      lieu_naissance: form.lieu_naissance || null,
      nationalite: form.nationalite || null,
      num_cni: form.num_cni || null,
      num_ipres: form.num_ipres || null,
      num_secu: form.num_secu || null,
      situation_matrimoniale: form.situation_matrimoniale || null,
      nb_epouses: parseInt(form.nb_epouses || 0),
      nb_enfants: parseInt(form.nb_enfants || 0),
      nom_conjoint: form.nom_conjoint || null,
      regime_matrimonial: form.regime_matrimonial || null,
      adresse: form.adresse || null,
      telephone: form.telephone || null,
      parts_fiscales: parseFloat(form.parts_fiscales || 1),
      categorie: form.categorie || null,
      genre: form.genre || null,
      solde_conges_ajustement: parseFloat(form.solde_conges_ajustement) || 0,
      solde_conges_note: finalNote,
      date_reprise_reference: form.date_reprise_reference || null,
      // ── Champs Rémunération (pré-remplissage bulletins) ──
      salaire_base: parseFloat(form.salaire_base) || null,
      sursalaire: parseFloat(form.sursalaire) || null,
      prime_anciennete: parseFloat(form.prime_anciennete) || null,
      prime_performance: parseFloat(form.prime_performance) || null,
      autres_primes: parseFloat(form.autres_primes) || null,
      indem_transport: parseFloat(form.indem_transport) || null,
      indem_repas: parseFloat(form.indem_repas) || null,
      avantages_nature: parseFloat(form.avantages_nature) || null,
      ...(form.password ? { password: form.password } : {})
    };
    const { data, error } = await supabase.from("employes").update(upd).eq("id", form.id).select().single();
    if (error) { setSaving(false); alert("Erreur: " + error.message); return; }
    if (data) setEmployes(p => p.map(e => e.id === data.id ? data : e));
    setSaving(false); setModal(null); setForm({});
  };

  const publierAnnonce = async () => {
    if (!form.titre || !form.contenu) return;
    setSaving(true);
    const { data, error } = await supabase.from("annonces").insert({ ...form, type_annonce: form.type_annonce || "info", publie_par: user.prenom + " " + user.nom }).select().single();
    if (error) { setSaving(false); alert("Erreur: " + error.message); return; }
    if (data) setAnnonces(p => [data, ...p]);
    setSaving(false); setModal(null); setForm({});
  };

  const pendingC = conges.filter(c => c.statut === "en_attente").length;
  const pendingA = absences.filter(a => a.statut === "en_attente").length;
  const totalPending = pendingC + pendingA;
  const mySite = selectedSite;
  const empActifs = employes.filter(e => e.actif && e.prenom !== "À compléter" && (mySite === "all" || (e.site || "Dakar") === mySite));
  const empSearch = empActifs.filter(e => (e.nom + e.prenom + (e.email || "") + (e.departement || "")).toLowerCase().includes(search.toLowerCase()));
  // ── Totaux VRS & CSS du mois courant (tous les employés actifs XOF) ──
  const fichesMoisCourant = fiches.filter(f => {
    const m = new Date().getMonth() + 1; const y = new Date().getFullYear();
    return parseInt(f.mois) === m && parseInt(f.annee) === y && (mySite === "all" || f.site === mySite);
  });
  const vrsTotal = fichesMoisCourant.reduce((sum, fiche) => {
    const emp = employes.find(e => e.id === fiche.employe_id);
    if (!emp || (emp.devise !== "XOF" && emp.site === "France")) return sum;
    const snap = parseBulletinSnap(fiche);
    if (snap) {
      return sum + (snap.ir || 0) + (snap.trimf || 0) + (snap.cfce || 0);
    }
    try {
      const c = calcPayeSN({
        salaire_base: parseFloat(emp.salaire_base) || 0,
        sursalaire: parseFloat(emp.sursalaire) || 0,
        prime_anciennete: parseFloat(emp.prime_anciennete) || 0,
        prime_performance: parseFloat(emp.prime_performance) || 0,
        autres_primes: parseFloat(emp.autres_primes) || 0,
        indem_transport: parseFloat(emp.indem_transport) || 26000,
        indem_repas: parseFloat(emp.indem_repas) || 0,
        parts_fiscales: String(emp.parts_fiscales || 1),
        regime_cadre: emp.regime_cadre === "oui" || (emp.categorie && CONVENTION_COMMERCE[emp.categorie]?.cadre) ? "oui" : "non",
        with_ipm: emp.with_ipm !== "non" ? "oui" : "non",
        type_contrat: emp.type_contrat || "cdi",
        remb_pret: 0,
        avance_salaire: 0
      }, true);
      return sum + (c.irMensuel || 0) + (c.trimf || 0) + (c.cfce || 0);
    } catch (e) { return sum; }
  }, 0);
  const cssTotal = fichesMoisCourant.reduce((sum, fiche) => {
    const emp = employes.find(e => e.id === fiche.employe_id);
    if (!emp || (emp.devise !== "XOF" && emp.site === "France")) return sum;
    const snap = parseBulletinSnap(fiche);
    if (snap) {
      return sum + (snap.ipres || 0) + (snap.prestFam || 0) + (snap.accTrav || 0);
    }
    try {
      const c = calcPayeSN({
        salaire_base: parseFloat(emp.salaire_base) || 0,
        sursalaire: parseFloat(emp.sursalaire) || 0,
        prime_anciennete: parseFloat(emp.prime_anciennete) || 0,
        prime_performance: parseFloat(emp.prime_performance) || 0,
        autres_primes: parseFloat(emp.autres_primes) || 0,
        indem_transport: parseFloat(emp.indem_transport) || 26000,
        indem_repas: parseFloat(emp.indem_repas) || 0,
        parts_fiscales: String(emp.parts_fiscales || 1),
        regime_cadre: emp.regime_cadre === "oui" || (emp.categorie && CONVENTION_COMMERCE[emp.categorie]?.cadre) ? "oui" : "non",
        with_ipm: emp.with_ipm !== "non" ? "oui" : "non",
        type_contrat: emp.type_contrat || "cdi",
        remb_pret: 0,
        avance_salaire: 0
      }, true);
      return sum + (c.ipres_sal || 0) + (c.ipres_comp_sal || 0) + (c.ipres_pat || 0) + (c.ipres_comp_pat || 0) + (c.prest_fam || 0) + (c.acc_trav || 0);
    } catch (e) { return sum; }
  }, 0);
  // FIX: parts_fiscales toujours à jour dans liveCalc
  const calcEmp = employes.find(e => e.id === form.employe_id);
  const liveCalcForm = { ...form, parts_fiscales: form.parts_fiscales || "1" };
  const liveCalc = calcEmp && form.salaire_base ? calcPayeSN(liveCalcForm, calcEmp.devise === "XOF" || calcEmp.site !== "France") : null;
  const unread = notifs.filter(x => x.unread).length;
  // FIX: détecter bulletin existant pour ce mois/employé
  const bulletinExistant = form.employe_id && form.mois ? fiches.find(f => f.employe_id === form.employe_id && String(f.mois) === String(form.mois) && String(f.annee) === String(form.annee || new Date().getFullYear())) : null;

  // ── ÉTAT DES SALAIRES MENSUELS (XOF) ──
  const fichesReport = fiches.filter(f => {
    return String(f.mois).padStart(2, '0') === reportMois && String(f.annee) === reportAnnee && (selectedSite === "all" ? f.site === "Dakar" : f.site === selectedSite);
  });

  const reportRows = fichesReport.map(fiche => {
    const emp = employes.find(e => e.id === fiche.employe_id);
    if (!emp || (emp.devise !== "XOF" && emp.site === "France")) return null;
    const snap = parseBulletinSnap(fiche);
    if (snap) {
      return {
        employe: snap.poste ? { ...emp, poste: snap.poste } : emp,
        fiche: fiche,
        brut: snap.brut ?? ((parseFloat(emp.salaire_base) || 0) + (parseFloat(emp.sursalaire) || 0)),
        transport: snap.transport ?? (parseFloat(emp.indem_transport) || 26000),
        chargesSociales: snap.chargesSociales ?? 0,
        remuDirecte: snap.remuDirecte ?? 0,
        prestFam: snap.prestFam ?? 0,
        accTrav: snap.accTrav ?? 0,
        ipres: snap.ipres ?? 0,
        ir: snap.ir ?? 0,
        cfce: snap.cfce ?? 0,
        trimf: snap.trimf ?? 0
      };
    }
    try {
      const c = calcPayeSN({
        salaire_base: parseFloat(emp.salaire_base) || 0,
        sursalaire: parseFloat(emp.sursalaire) || 0,
        prime_anciennete: parseFloat(emp.prime_anciennete) || 0,
        prime_performance: parseFloat(emp.prime_performance) || 0,
        autres_primes: parseFloat(emp.autres_primes) || 0,
        indem_transport: parseFloat(emp.indem_transport) || 26000,
        indem_repas: parseFloat(emp.indem_repas) || 0,
        parts_fiscales: String(emp.parts_fiscales || 1),
        regime_cadre: emp.regime_cadre === "oui" || (emp.categorie && CONVENTION_COMMERCE[emp.categorie]?.cadre) ? "oui" : "non",
        with_ipm: emp.with_ipm !== "non" ? "oui" : "non",
        type_contrat: emp.type_contrat || "cdi",
        remb_pret: 0,
        avance_salaire: 0
      }, true);
      
      const chargesSocialesPatronales = (c.ipres_pat || 0) + (c.ipres_comp_pat || 0) + (c.prest_fam || 0) + (c.acc_trav || 0) + (c.cfce || 0) + (c.ipm_pat || 0);
      const remunerationDirecte = (parseFloat(emp.indem_repas) || 0) + (parseFloat(emp.prime_performance) || 0);
      const totalIpres = (c.ipres_sal || 0) + (c.ipres_pat || 0) + (c.ipres_comp_sal || 0) + (c.ipres_comp_pat || 0);
      
      return {
        employe: emp,
        fiche: fiche,
        brut: (parseFloat(emp.salaire_base) || 0) + (parseFloat(emp.sursalaire) || 0),
        transport: parseFloat(emp.indem_transport) || 26000,
        chargesSociales: chargesSocialesPatronales,
        remuDirecte: remunerationDirecte,
        prestFam: c.prest_fam || 0,
        accTrav: c.acc_trav || 0,
        ipres: totalIpres,
        ir: c.irMensuel || 0,
        cfce: c.cfce || 0,
        trimf: c.trimf || 0
      };
    } catch (e) {
      return null;
    }
  }).filter(Boolean);

  const totalBrut = reportRows.reduce((s, r) => s + r.brut, 0);
  const totalTransport = reportRows.reduce((s, r) => s + r.transport, 0);
  const totalRemuDirecte = reportRows.reduce((s, r) => s + r.remuDirecte, 0);
  const totalChargesSociales = reportRows.reduce((s, r) => s + r.chargesSociales, 0);
  const totalPrestFam = reportRows.reduce((s, r) => s + r.prestFam, 0);
  const totalAccTrav = reportRows.reduce((s, r) => s + r.accTrav, 0);
  const totalIpres = reportRows.reduce((s, r) => s + r.ipres, 0);
  const totalIR = reportRows.reduce((s, r) => s + r.ir, 0);
  const totalCFCE = reportRows.reduce((s, r) => s + r.cfce, 0);
  const totalTRIMF = reportRows.reduce((s, r) => s + r.trimf, 0);

  const exportEtatSalairesCSV = () => {
    let csv = "\uFEFF"; // BOM for Excel
    csv += "Collaborateur;Poste;Salaire Brut;Transport;Rémunération Directe (Repas+Perf);Charges Patronales;Prestation Familiale;Accident du Travail;IPRES;Impôt IR;CFCE;TRIMF\n";
    reportRows.forEach(r => {
      csv += `${r.employe.prenom} ${r.employe.nom};${r.employe.poste || "—"};${r.brut};${r.transport};${r.remuDirecte};${r.chargesSociales};${r.prestFam};${r.accTrav};${r.ipres};${r.ir};${r.cfce};${r.trimf}\n`;
    });
    csv += `TOTAL;;${totalBrut};${totalTransport};${totalRemuDirecte};${totalChargesSociales};${totalPrestFam};${totalAccTrav};${totalIpres};${totalIR};${totalCFCE};${totalTRIMF}\n`;
    
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Etat_Salaires_Mensuel_${reportMois}_${reportAnnee}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTabClick = (tabId) => {
    setTab(tabId);
    if (tabId === "messagerie") {
      setMsgUnread(0);
      localStorage.setItem("majha_msg_last_seen_" + user.id, new Date().toISOString());
    }
  };

  const ANAV = [
    { id: "dashboard", label: "Tableau de bord", ic: "📊" },
    // Simulateur et Etat des salaires : uniquement pour Dakar / Admin global (pas France)
    ...(mySite !== "France" ? [
      { id: "simulateur", label: "Simulateur Paie", ic: "⚖️" },
    ] : []),
    { id: "fiches", label: "Bulletins de paie", ic: "💰" },
    ...(mySite !== "France" ? [
      { id: "etat_salaires", label: "État des salaires", ic: "📑" },
    ] : []),
    { id: "conges", label: "Congés", ic: "🌴", badge: pendingC },
    { id: "absences", label: "Absences", ic: "📅", badge: pendingA },
    { id: "annonces", label: "Annonces", ic: "📢" },
    { id: "equipe", label: "Équipe", ic: "👥" },
    { id: "messagerie", label: "Messagerie", ic: "💬", badge: msgUnread },
  ];

  // FIX CRITIQUE: empTab dans AdminApp (pas dans EmpForm) — sinon chaque frappe
  // dans un input rerend AdminApp → EmpForm recréée → unmount/remount → perte focus
  const [empTab, setEmpTab] = useState("pro");

  if (loading) return (<><style>{CSS}</style><div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", color: "var(--text3)" }}>Chargement…</div></>);

  return (<><style>{CSS}</style>
    {/* TOASTS */}
    <div className="toast-wrap">
      {toasts.map(t => (<div key={t.id} className="toast"><span className="toast-ic">{t.ic}</span><div><div className="toast-t">{t.t}</div><div className="toast-s">{t.s}</div></div></div>))}
    </div>

    <div className={`shell ${user?.site === "France" ? "theme-france" : ""}`}>
      {/* SIDEBAR */}
      <div className="side">
        <div className="slogo">
          <img src="/logo-majha.png" alt="MAJHA" style={{ width: 42, height: 42, objectFit: "contain", mixBlendMode: "lighten", flexShrink: 0, borderRadius: 8 }} />
          <div><div className="sbrand">MAJHA</div><div className="stag">{selectedSite === "all" ? "🌍 Admin global" : selectedSite === "France" ? "🇫🇷 Espace France" : "🇸🇳 Espace Dakar"}</div></div>
        </div>
        <nav className="snav">
          <div className="nsec">Gestion RH</div>
          {ANAV.map(n => (
            <div key={n.id} className={"nitem" + (tab === n.id ? " on" : "")} onClick={() => handleTabClick(n.id)}>
              <span className="nic">{n.ic}</span><span>{n.label}</span>
              {n.badge > 0 && <span className="nbdg">{n.badge}</span>}
            </div>
          ))}
        </nav>
        <div className="sfoot">
          <div className="surow">
            <Av initials={user.avatar_initials} color={user.avatar_color} size={34} />
            <div><div className="suname">{user.prenom} {user.nom}</div><div className="surole">Administrateur</div></div>
          </div>
          <span className="soutt" onClick={onLogout}>↩ Déconnexion</span>
        </div>
      </div>

      <div className="main">
        <div className="topbar">
          <div>
            <div className="tbtitle">{ANAV.find(n => n.id === tab)?.label}</div>
            <div className="tbsub">Bonjour {user.prenom} · {TODAY_STR}</div>
          </div>
          <div className="tbright">
            {totalPending > 0 && <span className="badge b-red">{totalPending} en attente</span>}
            {/* NOTIF BELL */}
            <div style={{ position: "relative" }} ref={notifRef}>
              <button className="bell-btn" onClick={() => setShowNotifs(v => !v)}>
                🔔{unread > 0 && <span className="bell-dot" />}
              </button>
              {showNotifs && (
                <div className="notif-panel">
                  <div className="np-head">
                    <span className="np-t">Notifications {unread > 0 && <span style={{ background: "var(--red)", color: "#fff", fontSize: 10, padding: "1px 6px", borderRadius: 20, marginLeft: 6 }}>{unread}</span>}</span>
                    {unread > 0 && <button style={{ fontSize: 11, color: "var(--blue)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }} onClick={() => setNotifs(p => p.map(n => ({ ...n, unread: false })))}>Tout marquer lu</button>}
                  </div>
                  {notifs.length === 0 && <div style={{ padding: "16px", fontSize: 12, color: "var(--text3)", textAlign: "center" }}>Aucune notification</div>}
                  {notifs.slice(0, 10).map((n, i) => (
                    <div key={i} className={"np-item" + (n.unread ? " np-unread" : "")} onClick={() => setNotifs(p => p.map((x, j) => j === i ? { ...x, unread: false } : x))}>
                      <div className="np-it">{n.t}</div>
                      <div className="np-is">{n.s}</div>
                      {n.unread && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--blue)", flexShrink: 0, marginLeft: "auto" }} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Av initials={user.avatar_initials} color={user.avatar_color} size={32} fs={11} />
          </div>
        </div>
        <div className="content">

          {/* DASHBOARD */}
          {tab === "dashboard" && (
            <div>
              {/* Sélecteur de Site Interactif - Réservé aux admins globaux */}
              {adminSite(user) === "all" && (
                <div style={{ display: "flex", gap: 8, marginBottom: 20, background: "#f3f4f6", padding: 6, borderRadius: 14, width: "fit-content", border: "1px solid var(--border2)" }}>
                  {[
                    { id: "all", label: "🌍 Vue Globale", color: "var(--blue)" },
                    { id: "Dakar", label: "🇸🇳 Dakar / Sénégal", color: "#059669" },
                    { id: "France", label: "🇫🇷 France (RH)", color: "#2563eb" },
                  ].map(s => {
                    const isSel = selectedSite === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setSelectedSite(s.id)}
                        style={{
                          padding: "8px 16px",
                          borderRadius: 10,
                          fontWeight: 600,
                          fontSize: 12.5,
                          background: isSel ? "#fff" : "transparent",
                          color: isSel ? s.color : "#4b5563",
                          border: "none",
                          boxShadow: isSel ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Bannière Site : Vue filtrée vs Vue globale (Focus Dakar) */}
              {mySite !== "all" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: mySite === "France" ? "#f0f4ff" : "var(--blue-ll)", border: `1px solid ${mySite === "France" ? "#c7d7f8" : "var(--blue-l)"}`, borderRadius: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 18 }}>{mySite === "France" ? "🇫🇷" : "🇸🇳"}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--blue)" }}>{mySite === "France" ? "Espace France — RH uniquement" : "Espace Dakar — Vue filtrée"}</div>
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>{mySite === "France" ? "Gestion RH (congés, absences, messagerie) — La paie est gérée séparément." : "Données limitées au site de Dakar."}</div>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", background: "#fff", border: "1.5px solid var(--border2)", borderRadius: 14, marginBottom: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.03)" }}>
                  <span style={{ fontSize: 24 }}>🌍</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>MAJHA Accounting — Vue Globale Multi-Site</div>
                    <div style={{ display: "flex", gap: 20, marginTop: 4 }}>
                      <div style={{ fontSize: 11, color: "var(--text3)", display: "flex", alignItems: "center", gap: 6 }}>
                        <strong>Date :</strong> {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text3)", display: "flex", alignItems: "center", gap: 6 }}>
                        <strong>Heure Dakar :</strong> {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Dakar' })}
                      </div>
                    </div>
                  </div>
                  <div style={{ background: "var(--green-l)", color: "var(--green)", padding: "4px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Admin Global</div>
                </div>
              )}
              <div className="kgrid" style={{ gridTemplateColumns: mySite === "France" ? "repeat(2, 1fr)" : "repeat(5, 1fr)" }}>
                {[
                  { ic: "👥", l: "Collaborateurs", v: empActifs.length, s: mySite === "all" ? `actifs (dont ${employes.filter(e => (e.site || "Dakar") === "Dakar" && e.actif && e.prenom !== "À compléter").length} à Dakar)` : "actifs" },
                  // Masse salariale et bulletins : uniquement Dakar / Admin global
                  ...(mySite !== "France" ? (() => {
                    const fichesDuMois = fiches.filter(f => (mySite === "all" ? f.site === "Dakar" : f.site === mySite) && parseInt(f.mois) === (new Date().getMonth() + 1) && parseInt(f.annee) === new Date().getFullYear());
                    const masseBrute = fichesDuMois.reduce((s, f) => {
                      const e = employes.find(emp => emp.id === f.employe_id);
                      return s + (e ? (parseFloat(e.salaire_base) || 0) + (parseFloat(e.sursalaire) || 0) : 0);
                    }, 0);
                    const masseNette = fichesDuMois.reduce((s, f) => s + (parseFloat(f.salaire_net) || 0), 0);
                    return [
                      { ic: "💸", l: "Masse Salariale", v: `${Number(masseBrute).toLocaleString('fr-FR')} FCFA`, s: "Cumul Base + Sursalaire" },
                      { ic: "🏦", l: "Net à Payer", v: `${Number(masseNette).toLocaleString('fr-FR')} FCFA`, s: "Cumul Salaires Nets" },
                      { ic: "💰", l: "Bulletins générés", v: fichesDuMois.length, s: "total" }
                    ];
                  })() : []),
                  { ic: "⏳", l: "En attente", v: totalPending, s: "congés + absences" },
                ].map((k, i) => (
                  <div key={i} className="kcard">
                    <div className="kic">{k.ic}</div>
                    <div className="klbl">{k.l}</div>
                    <div className="kval" style={{ fontSize: k.l.includes("Masse") || k.l.includes("Net") ? (mySite === "all" ? 17 : 20) : 24 }}>{k.v}</div>
                    <div className="ksub">{k.s}</div>
                  </div>
                ))}
              </div>
              {/* ── Totaux VRS & CSS du mois — uniquement Dakar / Admin global ── */}
              {mySite !== "France" && (vrsTotal > 0 || cssTotal > 0) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                  {/* VRS */}
                  <div style={{ background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 100%)", borderRadius: 14, padding: "20px 24px", color: "#fff", position: "relative", overflow: "hidden", boxShadow: "0 8px 24px rgba(37,99,235,0.25)" }}>
                    <div style={{ position: "absolute", right: -20, top: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }}></div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", opacity: .7, marginBottom: 8 }}>VRS — Mois de {MONTHS[new Date().getMonth()]}</div>
                    <div style={{ fontSize: 11, opacity: .65, marginBottom: 4 }}>IR + TRIMF + CFCE</div>
                    <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 6 }}>{Number(vrsTotal).toLocaleString('fr-FR')} <span style={{ fontSize: 13, fontWeight: 600, opacity: .8 }}>FCFA</span></div>
                    <div style={{ fontSize: 10, opacity: .6, borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 8, marginTop: 4 }}>Versements à reverser à la DGI · {fichesMoisCourant.filter(f => { const e = employes.find(x => x.id === f.employe_id); return e && e.devise === "XOF"; }).length} bulletins XOF</div>
                  </div>
                  {/* CSS */}
                  <div style={{ background: "linear-gradient(135deg,#065f46 0%,#059669 100%)", borderRadius: 14, padding: "20px 24px", color: "#fff", position: "relative", overflow: "hidden", boxShadow: "0 8px 24px rgba(5,150,105,0.25)" }}>
                    <div style={{ position: "absolute", right: -20, top: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }}></div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", opacity: .7, marginBottom: 8 }}>CSS — Mois de {MONTHS[new Date().getMonth()]}</div>
                    <div style={{ fontSize: 11, opacity: .65, marginBottom: 4 }}>IPRES (Sal. + Pat.) + Prest. fam. + Acc. travail</div>
                    <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 6 }}>{Number(cssTotal).toLocaleString('fr-FR')} <span style={{ fontSize: 13, fontWeight: 600, opacity: .8 }}>FCFA</span></div>
                    <div style={{ fontSize: 10, opacity: .6, borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 8, marginTop: 4 }}>Versements globaux à reverser à l'IPRES/CSS · {fichesMoisCourant.filter(f => { const e = employes.find(x => x.id === f.employe_id); return e && e.devise === "XOF"; }).length} bulletins XOF</div>
                  </div>
                </div>
              )}
              {totalPending > 0 && (
                <div style={{ background: "var(--yellow-l)", border: "1px solid #FDE68A", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>⏳</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: "var(--yellow)" }}>
                    {pendingC} demande{pendingC !== 1 ? "s" : ""} de congés · {pendingA} absence{pendingA !== 1 ? "s" : ""} en attente
                  </span>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    {pendingC > 0 && <button className="btn btn-sm" style={{ background: "var(--yellow-l)", border: "1px solid #FDE68A", color: "var(--yellow)" }} onClick={() => setTab("conges")}>Voir congés</button>}
                    {pendingA > 0 && <button className="btn btn-sm" style={{ background: "var(--yellow-l)", border: "1px solid #FDE68A", color: "var(--yellow)" }} onClick={() => setTab("absences")}>Voir absences</button>}
                  </div>
                </div>
              )}
              <div className="g31">
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {/* Graphique / Masse Salariale — uniquement Dakar / Admin global */}
                  {mySite !== "France" && (
                    <div className="card" style={{ padding: "22px 22px 0 22px" }}>
                    <div className="chead" style={{ margin: 0 }}><span className="ctitle">Évolution — Masse salariale brute (6 mois)</span></div>
                    {/* SVG Bar Chart Minimaliste */}
                    <div style={{ height: 200, display: "flex", alignItems: "flex-end", gap: 16, padding: "20px 0 0", borderBottom: "1px solid var(--border)", position: "relative" }}>
                      {/* Y-axis labels */}
                      <div style={{ position: "absolute", left: 0, top: 20, bottom: 30, display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: 10, color: "var(--text3)", fontWeight: 500 }}>
                        <span>Max</span><span>Moy.</span><span>0 F</span>
                      </div>
                      <div style={{ marginLeft: 30, flex: 1, display: "flex", alignItems: "flex-end", justifyContent: "space-around", height: "100%" }}>
                        {[...Array(6)].map((_, idx) => {
                          const d = new Date(); d.setMonth(d.getMonth() - 5 + idx);
                          const m = String(d.getMonth() + 1).padStart(2, '0'); const y = d.getFullYear();
                          const f = fiches.filter(x => x.mois === m && Number(x.annee) === y && (mySite === "all" || x.site === mySite));
                          const val = f.reduce((s, a) => s + (parseFloat(a.salaire_brut) || 0), 0);
                          const randomPct = f.length === 0 ? 5 : Math.max(15, Math.min(100, (val / (2000000)) * 100)); // Dummy UI scale since lack of real history depth
                          return (
                            <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, width: "12%", height: "100%", position: "relative" }}>
                              <div style={{ width: "100%", height: `${f.length > 0 ? randomPct : 4}%`, background: f.length > 0 ? "var(--blue)" : "var(--border)", borderRadius: "6px 6px 0 0", opacity: idx === 5 ? 1 : 0.4, transition: "all .4s", position: "absolute", bottom: 0 }}></div>
                              <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 600, position: "absolute", bottom: -24, textTransform: "uppercase", letterSpacing: 0.5 }}>{MONTHS[d.getMonth()].slice(0, 3)}</div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    <div style={{ height: 30 }}></div>
                    </div>
                  )}

                  {/* Liste des Employés - Design Épuré */}
                  <div className="card">
                    <div className="chead" style={{ marginBottom: 10 }}><span className="ctitle">Employés — Vue globale</span></div>
                    <div>
                      {empActifs.slice(0, 6).map((e, i) => {
                        const startOfToday = new Date().setHours(0, 0, 0, 0);
                        const estEnConge = conges.some(c => c.employe_id === e.id && c.statut === "approuve" && new Date(c.date_debut).setHours(0, 0, 0, 0) <= startOfToday && new Date(c.date_fin).setHours(23, 59, 59, 999) >= startOfToday);
                        const hasSalaire = e.site !== "France" && parseFloat(e.salaire_base) > 0;
                        return (
                          <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: i < 5 ? "1px solid var(--border)" : "none" }}>
                            <div className="av" style={{ width: 38, height: 38, background: e.avatar_color || "var(--blue)", fontSize: 13 }}>{e.avatar_initials || "?"}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>{e.prenom} {e.nom}</div>
                              <div style={{ fontSize: 11.5, color: "var(--text3)" }}>{formatPoste(e.poste, e) || e.departement}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              {hasSalaire && (
                                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>
                                  {Number(e.salaire_base).toLocaleString("fr-FR")} FCFA
                                </div>
                              )}
                              {estEnConge ? <span className="badge b-xof" style={{ display: "inline-block", minWidth: 50, textAlign: "center" }}>En congé</span> : <span className="badge b-green" style={{ display: "inline-block", minWidth: 50, textAlign: "center" }}>Actif</span>}
                            </div>
                          </div>
                        )
                      })}
                      {empActifs.length === 0 && <div className="empty" style={{ padding: 20 }}><div className="empty-t">Aucun employé actif</div></div>}
                      <div style={{ textAlign: "center", marginTop: 10 }}>
                        <button className="btn btn-ghost" style={{ fontSize: 12, width: "100%", justifyContent: "center" }} onClick={() => setTab("equipe")}>Voir tout l'effectif ➔</button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* COLONNE DROITE : Actions, Statut, Activité */}
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {/* Actions Rapides */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>⚡ Actions rapides</div>
                    <div className="qact-grid">
                      <button className="qbtn" onClick={() => setTab("equipe")}>
                        <span className="qic">👥</span><div className="qlbl">Nouvel employé</div><div className="qsub">Ajouter un profil</div>
                      </button>
                      {mySite !== "France" ? (
                        <button className="qbtn" onClick={() => { setTab("fiches"); setTimeout(() => { setModal("fiche"); setForm({ mois: String(new Date().getMonth() + 1).padStart(2, "0"), annee: new Date().getFullYear(), type_contrat: "cdi", with_ipm: "oui" }); }, 100); }}>
                          <span className="qic">💰</span><div className="qlbl">Lancer la paie</div><div className="qsub">Calcul global</div>
                        </button>
                      ) : (
                        <button className="qbtn" onClick={() => setTab("fiches")}>
                          <span className="qic">📄</span><div className="qlbl">Bulletins de paie</div><div className="qsub">Upload des fiches</div>
                        </button>
                      )}
                      <button className="qbtn" onClick={() => setTab("annonces")}>
                        <span className="qic">📢</span><div className="qlbl">Annonce RH</div><div className="qsub">Message d'équipe</div>
                      </button>
                      <button className="qbtn" onClick={() => setTab("conges")}>
                        <span className="qic">🏖️</span><div className="qlbl">Vue Congés</div><div className="qsub">Valider requêtes</div>
                      </button>
                    </div>
                  </div>

                  {/* Statut du mois */}
                  <div className="card" style={{ background: "var(--bg)", borderColor: "transparent" }}>
                    <div className="chead" style={{ margin: 0, marginBottom: 12 }}><span className="ctitle">Tableau de suivi — {MONTHS[new Date().getMonth()]} {new Date().getFullYear()}</span></div>
                    <div className="chklst">
                      <div className="chk-item" style={{ background: totalPending > 0 ? "var(--white)" : undefined }}>
                        <div className="chk-l"><div className="chk-ic" style={{ color: totalPending > 0 ? "var(--orange)" : "var(--green)", boxShadow: totalPending > 0 ? "0 4px 12px rgba(249,115,22,0.15)" : undefined }}>{totalPending > 0 ? "!" : "✓"}</div><div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>Congés & Absences</div></div>
                        {totalPending > 0 ? <span className="badge b-xof" style={{ cursor: "pointer", boxShadow: "var(--shadow)" }} onClick={() => setTab("conges")}>{totalPending} en attente</span> : <span style={{ fontSize: 10, color: "var(--green)", fontWeight: 600 }}>Validés</span>}
                      </div>
                      <div className="chk-item">
                        <div className="chk-l"><div className="chk-ic" style={{ color: "var(--blue)" }}>↓</div><div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>Bulletins {mySite === "France" ? "disponibles" : "générés"}</div></div>
                        <span style={{ fontSize: 10, color: "var(--text3)", fontWeight: 600 }}>{fiches.filter(f => f.mois === String(new Date().getMonth() + 1).padStart(2, '0') && Number(f.annee) === new Date().getFullYear() && (mySite === "all" || f.site === mySite)).length} / {empActifs.length}</span>
                      </div>
                    </div>
                  </div>

                  {/* Activité récente */}
                  <div className="card">
                    <div className="chead" style={{ marginBottom: 16 }}><span className="ctitle">Activité récente</span></div>
                    <div>
                      {/* Fusione fiches et conges approuvés, trié par date simplifiée */}
                      {[
                        ...fiches.slice(0, 3).map(f => ({ type: "fiche", item: f, date: f.created_at })),
                        ...conges.filter(c => c.statut === "approuve").slice(0, 2).map(c => ({ type: "conge", item: c, date: c.created_at }))
                      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5).map((x, i) => {
                        if (x.type === "fiche") return (
                          <div className="tl-item" key={"f" + i}>
                            <div className="tl-dot"></div>
                            <div>
                              <div className="tl-t">Paie {MONTHS[parseInt(x.item.mois) - 1]} <span>{x.item.site === "France" ? "bulletin téléversé pour" : "générée pour"}</span> {x.item.employes?.nom}</div>
                              <div className="tl-d">{x.item.salaire_net ? `Net : ${fN(x.item.salaire_net, x.item.devise)}` : (x.item.site === "France" ? "Bulletin PDF importé" : "—")}</div>
                            </div>
                          </div>
                        );
                        return (
                          <div className="tl-item" key={"c" + i}>
                            <div className="tl-dot" style={{ background: "var(--green)", boxShadow: "0 0 0 3px var(--white)" }}></div>
                            <div>
                              <div className="tl-t">Congé approuvé <span>pour</span> {x.item.employes?.nom}</div>
                              <div className="tl-d">Du {fDs(x.item.date_debut)} au {fDs(x.item.date_fin)}</div>
                            </div>
                          </div>
                        );
                      })}
                      {fiches.length === 0 && conges.length === 0 && <div style={{ fontSize: 12, color: "var(--text3)" }}>Aucune activité récente.</div>}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}

          {/* SIMULATEUR PAIE */}
          {tab === "simulateur" && (
            <div style={{ animation: "fadeIn .3s" }}>
              {mySite === "France" ? (
                <div className="empty">
                  <div className="empty-ic" style={{ fontSize: 34 }}>🚧</div>
                  <div className="empty-t">Simulateur France indisponible</div>
                  <div className="empty-s">Le module de paie française (URSSAF, Retraite Cadre) est en cours de développement.</div>
                </div>
              ) : (
                tab === "simulateur" && (() => {
                  try {
                    return <SimulateurPaie />;
                  } catch (e) {
                    return <div style={{ padding: 40, color: "var(--red)", background: "#fff", borderRadius: 12, border: "1px solid var(--border2)" }}>
                      <div style={{ fontWeight: 700, marginBottom: 8 }}>⚠️ Erreur d'affichage du simulateur</div>
                      <div style={{ fontSize: 13, opacity: 0.8 }}>{e.message}</div>
                    </div>;
                  }
                })()
              )}
            </div>
          )}

          {/* FICHES */}
          {tab === "fiches" && (() => {
            const fichesSite = fiches.filter(f => {
              if (mySite === "all") return true;
              if (mySite === "France") return f.site === "France" || f.employes?.site === "France";
              return f.site !== "France" && f.employes?.site !== "France";
            });
            const fichesFiltrees = fichesSite.filter(f => {
              const m = !search || (f.employes?.nom + f.employes?.prenom).toLowerCase().includes(search.toLowerCase());
              const mo = filterMois === "all" || f.mois === filterMois;
              const an = filterAnnee === "all" || String(f.annee) === filterAnnee;
              const st = filterStatut === "all" || (filterStatut === "brouillon" ? isBulletinBrouillon(f) : isBulletinPublie(f));
              return m && mo && an && st;
            });
            const anneesDisponibles = [...new Set(fichesSite.map(f => String(f.annee)))].sort((a, b) => b - a);
            const draftsFiltres = fichesFiltrees.filter(isBulletinBrouillon);
            const totalDraftsSite = fichesSite.filter(isBulletinBrouillon);

            return (
              <div>
                <div className="sech" style={{ alignItems: "flex-start" }}>
                  <div>
                    <span className="sect">Bulletins de paie <span style={{ color: "var(--text3)", fontWeight: 400, fontSize: 12 }}>({fichesSite.length})</span></span>
                    <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 4 }}>
                      {mySite === "France"
                        ? "🇫🇷 Espace France : Dépôt manuel des bulletins PDF issus de votre logiciel de paie."
                        : mySite === "Dakar"
                        ? "🇸🇳 Espace Dakar : Génération et calcul automatique de la paie sénégalaise."
                        : "🇸🇳 Génération automatique pour Dakar · 🇫🇷 Dépôt des bulletins PDF pour la France"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    {fichesSite.length > 0 && (
                      <button className="btn btn-ghost btn-sm" onClick={exportBulletinsZIP} disabled={exportingZip}>
                        {exportingZip ? "Création ZIP..." : "📦 Télécharger (ZIP)"}
                      </button>
                    )}
                    {mySite !== "France" && (
                      <button className="btn btn-ghost btn-sm" onClick={genererTousBulletins}>🚀 Générer tous (Dakar)</button>
                    )}
                    <button className="btn btn-primary btn-sm" onClick={() => { setModal("upload_bulletin"); setForm({ mois: String(new Date().getMonth() + 1).padStart(2, "0"), annee: String(new Date().getFullYear()), site: "France" }); }}>
                      📤 Uploader un bulletin (France)
                    </button>
                    {mySite !== "France" && (
                      <button className="btn btn-ghost btn-sm" onClick={() => { setModal("fiche"); setForm({ mois: String(new Date().getMonth() + 1).padStart(2, "0"), annee: new Date().getFullYear(), site: "Dakar", devise: "XOF", type_contrat: "cdi", with_ipm: "oui" }); }}>
                        ✦ Générer un bulletin (Dakar)
                      </button>
                    )}
                  </div>
                </div>

                {/* BANDEAU DE VÉRIFICATION & VALIDATION DES BROUILLONS */}
                {draftsFiltres.length > 0 && (
                  <div style={{
                    background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)",
                    border: "1px solid #FDE68A",
                    borderRadius: 12,
                    padding: "16px 20px",
                    marginBottom: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 14,
                    boxShadow: "0 2px 6px rgba(217,119,6,0.08)"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: "#FDE68A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                        🔒
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: "#92400E", fontSize: 14 }}>
                          {draftsFiltres.length} bulletin{draftsFiltres.length > 1 ? "s" : ""} en attente de validation {filterMois !== "all" ? `(${MONTHS[parseInt(filterMois) - 1]} ${filterAnnee !== "all" ? filterAnnee : ""})` : ""}
                        </div>
                        <div style={{ fontSize: 12, color: "#B45309", marginTop: 3 }}>
                          Ces bulletins sont des <strong>brouillons confidentiels</strong> : ils ne sont <strong>pas visibles</strong> par les salariés. Vérifiez les montants ci-dessous puis validez-les quand vous êtes prête.
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ background: "#059669", borderColor: "#059669", fontWeight: 700, padding: "8px 16px", boxShadow: "0 2px 8px rgba(5,150,105,0.25)" }}
                        onClick={() => validerEtPublierTous(filterMois !== "all" ? filterMois : null, filterAnnee !== "all" ? filterAnnee : null)}
                      >
                        🚀 Valider & Publier ({draftsFiltres.length})
                      </button>
                    </div>
                  </div>
                )}

                {bulkProgress && (
                  <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 10, padding: 20, marginBottom: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                      <span style={{ fontWeight: 600, color: "var(--blue-d)" }}>{bulkProgress.done ? "Terminé !" : "Génération en cours..."}</span>
                      <span style={{ fontSize: 13, color: "var(--text2)" }}>{bulkProgress.current} / {bulkProgress.total}</span>
                    </div>
                    <div style={{ width: "100%", height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%`, height: "100%", background: "var(--blue)", transition: "width 0.3s" }}></div>
                    </div>
                    {bulkProgress.done && (
                      <div style={{ marginTop: 10, fontSize: 13, color: bulkProgress.errors > 0 ? "var(--red)" : "var(--green)", fontWeight: 500 }}>
                        Génération terminée avec {bulkProgress.errors} erreur(s). Les bulletins sont enregistrés en brouillon pour vérification.
                        <button className="btn btn-ghost btn-xs" style={{ marginLeft: 10 }} onClick={() => setBulkProgress(null)}>✕ Fermer</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Filtres et Recherche bulletins */}
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ flex: 1, minWidth: 200 }} className="sbox">
                    <span style={{ fontSize: 14, color: "var(--text3)" }}>🔍</span>
                    <input className="sinp" placeholder="Rechercher par nom…" value={search} onChange={e => setSearch(e.target.value)} />
                  </div>
                  <select className="fsel" style={{ width: 130, height: 38 }} value={filterMois} onChange={e => setFilterMois(e.target.value)}>
                    <option value="all">Tous les mois</option>
                    {MOIS.map((m, i) => <option key={m} value={m}>{MONTHS[i]}</option>)}
                  </select>
                  <select className="fsel" style={{ width: 100, height: 38 }} value={filterAnnee} onChange={e => setFilterAnnee(e.target.value)}>
                    <option value="all">Toutes les années</option>
                    {anneesDisponibles.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select className="fsel" style={{ width: 150, height: 38 }} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
                    <option value="all">Tous statuts</option>
                    <option value="brouillon">🟡 Brouillons ({totalDraftsSite.length})</option>
                    <option value="publie">🟢 Publiés ({fichesSite.length - totalDraftsSite.length})</option>
                  </select>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setFilterMois("all"); setFilterAnnee("all"); setFilterStatut("all"); setSearch(""); }}>Réinitialiser</button>
                </div>

                {fichesSite.length === 0 ? (
                  <div className="empty">
                    <div className="empty-ic">💰</div>
                    <div className="empty-t">Aucun bulletin disponible</div>
                    <div className="empty-s">
                      {mySite === "France"
                        ? "Uploadez les bulletins de paie PDF pour vos collaborateurs en France."
                        : "Générez ou uploadez des bulletins de paie pour l'équipe."}
                    </div>
                  </div>
                ) : (() => {
                  const PAGE = 15;
                  const page = n(form._fichePage) || 0;
                  const paginated = fichesFiltrees.slice(page * PAGE, (page + 1) * PAGE);
                  const totalPages = Math.ceil(fichesFiltrees.length / PAGE);
                  return (
                  <>
                    <div className="tblwrap">
                      <table className="tbl">
                        <thead><tr><th>Employé</th><th>Période</th><th>Brut</th><th>Net à payer</th><th>Statut</th><th>Site</th><th>Déposé par</th><th></th></tr></thead>
                        <tbody>
                          {paginated.map((f, i) => {
                            const empF = employes.find(e => e.id === f.employe_id);
                            const snapF = parseBulletinSnap(f);
                            const displayedBrut = snapF?.brut ?? (empF && empF.site !== "France" ? (parseFloat(empF.salaire_base) || 0) + (parseFloat(empF.sursalaire) || 0) + (parseFloat(empF.prime_anciennete) || 0) : f.salaire_brut);
                            const isBrouillon = isBulletinBrouillon(f);
                            return (
                              <tr key={i} style={{ background: isBrouillon ? "#FFFDF5" : undefined }}>
                                <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <Av initials={f.employes?.avatar_initials} color={f.employes?.avatar_color} size={28} fs={10} />
                                  <span style={{ fontWeight: 600, fontSize: 13 }}>{f.employes?.prenom} {f.employes?.nom}</span>
                                </div></td>
                                <td style={{ fontWeight: 500 }}>{MONTHS[parseInt(f.mois) - 1]} {f.annee}</td>
                                <td style={{ color: "var(--text3)", fontSize: 12 }}>{displayedBrut ? fN(displayedBrut, f.devise) : "—"}</td>
                                <td style={{ fontWeight: 700, color: "var(--blue)" }}>{f.salaire_net ? fN(f.salaire_net, f.devise) : "—"}</td>
                                <td>
                                  {isBrouillon ? (
                                    <span className="badge" style={{ fontSize: 11, background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#D97706", display: "inline-block" }}></span>
                                      Brouillon (non visible)
                                    </span>
                                  ) : (
                                    <span className="badge" style={{ fontSize: 11, background: "#ECFDF5", color: "#065F46", border: "1px solid #A7F3D0", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#059669", display: "inline-block" }}></span>
                                      Publié (visible)
                                    </span>
                                  )}
                                </td>
                                <td><span className={"badge " + (f.site === "France" || f.devise === "EUR" ? "b-blue" : "b-xof")} style={{ fontSize: 10 }}>{f.site || "Dakar"}</span></td>
                                <td style={{ fontSize: 11, color: "var(--text3)" }}>{formatUploadedBy(f.uploaded_by)}</td>
                                <td style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  {isBrouillon ? (
                                    <button
                                      className="btn btn-xs"
                                      style={{ background: "#059669", color: "#fff", border: "none", fontWeight: 600, padding: "4px 8px" }}
                                      title="Valider et rendre visible au salarié"
                                      onClick={() => publierFiche(f)}
                                    >
                                      ✓ Publier
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-ghost btn-xs"
                                      style={{ color: "var(--text3)", padding: "4px 6px" }}
                                      title="Repasser en brouillon (masquer au salarié)"
                                      onClick={() => repasserBrouillonFiche(f)}
                                    >
                                      🔒 Masquer
                                    </button>
                                  )}
                                  {f.url && (
                                    <>
                                      <button
                                        className="btn btn-ghost btn-xs"
                                        onClick={() => setPdfPreview({
                                          url: f.url,
                                          title: `Bulletin ${MONTHS[parseInt(f.mois) - 1]} ${f.annee} — ${f.employes ? f.employes.prenom + ' ' + f.employes.nom : ''}`,
                                          fileName: f.nom_fichier || `Bulletin_${f.employes?.nom || ''}_${f.annee}_${f.mois}.pdf`
                                        })}
                                      >
                                        👁️ Voir
                                      </button>
                                      <button
                                        className="btn btn-ghost btn-xs"
                                        title="Télécharger"
                                        onClick={() => downloadCleanPDF(f.url, f.nom_fichier || `Bulletin_${f.employes?.nom || ''}_${f.annee}_${f.mois}.pdf`)}
                                      >
                                        ⬇
                                      </button>
                                    </>
                                  )}
                                  <button className="btn btn-ghost btn-xs" style={{ color: "var(--red)", borderColor: "transparent", padding: "4px 8px" }} onClick={() => {
                                    setConfirmModal({
                                      title: "Supprimer le bulletin",
                                      message: `Supprimer le bulletin de ${f.employes?.prenom} ${f.employes?.nom} pour ${MONTHS[parseInt(f.mois) - 1]} ${f.annee} ? Cette action est irréversible.`,
                                      danger: true,
                                      confirmLabel: "Supprimer",
                                      onConfirm: async () => {
                                        try {
                                          if (f.url) {
                                            const pathMatch = f.url.split('/Documents/')[1];
                                            if (pathMatch) await supabase.storage.from("Documents").remove([pathMatch]);
                                          }
                                          await supabase.from("fiches_paie").delete().eq("id", f.id);
                                          setFiches(p => p.filter(x => x.id !== f.id));
                                        } catch (e) { }
                                      }
                                    });
                                  }}>🗑️</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 14 }}>
                        <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setForm(f => ({ ...f, _fichePage: page - 1 }))}>← Préc.</button>
                        <span style={{ fontSize: 12, color: "var(--text3)" }}>Page {page + 1} / {totalPages}</span>
                        <button className="btn btn-ghost btn-sm" disabled={page >= totalPages - 1} onClick={() => setForm(f => ({ ...f, _fichePage: page + 1 }))}>Suiv. →</button>
                      </div>
                    )}
                    </>
                  );
                })()}
              </div>
            );
          })()}
          {/* FRAIS */}
          {tab === "frais" && (
            <div>
              <div className="sech">
                <span className="sect">Gestion des Notes de Frais</span>
              </div>
              <div className="empty"><div className="empty-ic">💸</div><div className="empty-t">Module de notes de frais en construction</div><div className="empty-s">Ici vous pourrez valider ou refuser les dépenses déclarées par les employés.</div></div>
            </div>
          )}

          {/* CONGÉS */}
          {tab === "conges" && (
            <div>
              <div className="filterbar">
                {["Toutes", "en_attente", "approuve", "refuse"].map(f => (
                  <button key={f} className={"fbtn" + (filtreC === f ? " on" : "")} onClick={() => setFiltreC(f)}>
                    {f === "Toutes" ? "Toutes" : f === "en_attente" ? `En attente${pendingC > 0 ? " (" + pendingC + ")" : ""}` : f === "approuve" ? "Approuvées" : "Refusées"}
                  </button>
                ))}
              </div>
              {conges.filter(c => filtreC === "Toutes" || c.statut === filtreC).length === 0 ? (<div className="empty"><div className="empty-ic">🌴</div><div className="empty-t">Aucune demande</div></div>)
                : conges.filter(c => filtreC === "Toutes" || c.statut === filtreC).map((c, i) => (
                  <div key={i} className="ritem">
                    <Av initials={c.employes?.avatar_initials} color={c.employes?.avatar_color} size={34} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{c.employes?.prenom} {c.employes?.nom}</div>
                      <div style={{ fontSize: 12, color: "var(--text3)" }}>{c.type_conge} · {fDs(c.date_debut)} → {fDs(c.date_fin)} · {c.nb_jours} jour{c.nb_jours !== 1 ? "s" : ""}</div>
                      {c.motif && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{c.motif}</div>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                      <StatBdg s={c.statut} />
                      {c.statut === "en_attente" && <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-success btn-xs" onClick={() => traiterC(c.id, "approuve")}>✓ Valider</button>
                        <button className="btn btn-danger btn-xs" onClick={() => traiterC(c.id, "refuse")}>✕ Refuser</button>
                      </div>}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* ABSENCES */}
          {tab === "absences" && (
            <div>
              <div className="kgrid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
                {[{ l: "En attente", v: pendingA, c: "var(--yellow)" }, { l: "Validées", v: absences.filter(a => a.statut === "approuve").length, c: "var(--green)" }, { l: "Total déclarées", v: absences.length, c: "var(--text)" }].map((k, i) => (
                  <div key={i} className="kcard"><div className="klbl">{k.l}</div><div className="kval" style={{ color: k.c }}>{k.v}</div></div>
                ))}
              </div>
              <div className="filterbar">
                {["Toutes", "en_attente", "approuve", "refuse"].map(f => (
                  <button key={f} className={"fbtn" + (filtreA === f ? " on" : "")} onClick={() => setFiltreA(f)}>
                    {f === "Toutes" ? "Toutes" : f === "en_attente" ? `En attente${pendingA > 0 ? " (" + pendingA + ")" : ""}` : f === "approuve" ? "Validées" : "Refusées"}
                  </button>
                ))}
              </div>
              {absences.filter(a => filtreA === "Toutes" || a.statut === filtreA).length === 0 ? (<div className="empty"><div className="empty-ic">📅</div><div className="empty-t">Aucune absence</div></div>)
                : absences.filter(a => filtreA === "Toutes" || a.statut === filtreA).map((a, i) => {
                  const lastBrut = getLastBrut(a.employe_id);
                  const isInjust = a.type_absence === "Absence injustifiée";
                  const retenue = isInjust && lastBrut ? Math.round((lastBrut / 30) * (a.nb_jours || 0)) : 0;
                  return (
                    <div key={i} className="ritem" style={{ flexWrap: "wrap" }}>
                      <Av initials={a.employes?.avatar_initials} color={a.employes?.avatar_color} size={34} />
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{a.employes?.prenom} {a.employes?.nom}</span>
                          {isInjust && <span className="badge b-red" style={{ fontSize: 10 }}>Injustifiée</span>}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text3)" }}>{a.type_absence} · {fDs(a.date_debut)} → {fDs(a.date_fin)} · {a.nb_jours} jour{a.nb_jours !== 1 ? "s" : ""}</div>
                        {a.motif && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{a.motif}</div>}
                        {a.justificatif_url && <a href={a.justificatif_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--blue)", display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4 }}>📎 Voir le justificatif</a>}
                        {isInjust && retenue > 0 && (
                          <div style={{ marginTop: 6, background: "var(--red-l)", border: "1px solid #FECACA", borderRadius: 6, padding: "4px 10px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 11, color: "var(--red)", fontWeight: 600 }}>Retenue : {fN(retenue, a.employes?.devise || "EUR")}</span>
                            <span style={{ fontSize: 10, color: "var(--text3)" }}>({fN(Math.round(lastBrut / 30), a.employes?.devise || "EUR")}/j × {a.nb_jours}j)</span>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                        <StatBdg s={a.statut} />
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          {a.statut === "en_attente" && <><button className="btn btn-success btn-xs" onClick={() => traiterA(a.id, "approuve")}>✓ Valider</button><button className="btn btn-danger btn-xs" onClick={() => traiterA(a.id, "refuse")}>✕ Refuser</button></>}
                          <button className="btn btn-ghost btn-xs" onClick={() => { setModal("editAbs"); setForm({ ...a, _absId: a.id }); }}>✏️ Modifier</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* ANNONCES */}
          {tab === "annonces" && (
            <div>
              <div className="sech">
                <span className="sect">Annonces RH</span>
                <button className="btn btn-primary btn-sm" onClick={() => { setModal("annonce"); setForm({ type_annonce: "info" }); }}>+ Nouvelle annonce</button>
              </div>
              {annonces.length === 0 ? (<div className="empty"><div className="empty-ic">📢</div><div className="empty-t">Aucune annonce</div></div>)
                : annonces.map((a, i) => (
                  <div key={i} className="ann-card">
                    <span className={"ann-type badge " + (a.type_annonce === "urgent" ? "b-red" : a.type_annonce === "important" ? "b-yellow" : "b-blue")}>{a.type_annonce}</span>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", margin: "8px 0 5px" }}>{a.titre}</div>
                    <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>{a.contenu}</div>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>Publié par {a.publie_par} · {fD(a.created_at)}</span>
                      <button className="btn btn-danger btn-xs" onClick={async () => { await supabase.from("annonces").delete().eq("id", a.id); setAnnonces(p => p.filter(x => x.id !== a.id)); }}>Supprimer</button>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* ÉQUIPE */}
          {tab === "equipe" && (
            <div>
              <div className="sech">
                <span className="sect">
                  {empActifs.length} collaborateur{empActifs.length !== 1 ? "s" : ""}
                  {mySite !== "all" && <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: 6 }}>· {mySite}</span>}
                </span>
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn btn-ghost btn-sm" onClick={exportEmployesCSV}>⬇ Exporter CSV</button>
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    setModal("employe");
                    setEmpTab("pro");
                    const targetSite = mySite === "France" ? "France" : "Dakar";
                    setForm({ site: targetSite, devise: targetSite === "France" ? "EUR" : "XOF", departement: "Comptabilité" });
                  }}>+ Ajouter</button>
                </div>
              </div>
              <div className="sbox">
                <span style={{ fontSize: 14, color: "var(--text3)" }}>🔍</span>
                <input className="sinp" placeholder="Rechercher par nom, email, département…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              {empActifs.length === 0 && (
                <div className="empty">
                  <div className="empty-ic">"🇸🇳"</div>
                  <div className="empty-t">Aucun collaborateur{mySite !== "all" ? " sur le site " + mySite : ""}</div>
                  <div className="empty-s">Ajoutez votre premier collaborateur en cliquant sur "+ Ajouter"</div>
                </div>
              )}
              {empActifs.length > 0 && empSearch.length === 0 && (
                <div className="empty">
                  <div className="empty-ic">🔍</div>
                  <div className="empty-t">Aucun résultat pour "{search}"</div>
                  <div className="empty-s">Vérifiez l'orthographe ou effacez la recherche</div>
                </div>
              )}
              {empSearch.length > 0 && (
                <div className="tblwrap">
                  <table className="tbl">
                    <thead><tr>
                      <th>Collaborateur</th><th>Poste & Rôle</th><th>Département</th><th>Site</th><th>Régime</th><th>Login</th><th></th>
                    </tr></thead>
                    <tbody>
                      {empSearch.map((e, i) => {
                        const adminRole = getEmployeAdminRole(e);
                        return (
                          <tr key={i}>
                            <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <Av initials={e.avatar_initials} color={e.avatar_color} size={30} fs={11} />
                              <div><div style={{ fontWeight: 600, fontSize: 13 }}>{e.prenom} {e.nom}</div><div style={{ fontSize: 11, color: "var(--text3)" }}>{e.email || "—"}</div></div>
                            </div></td>
                            <td>
                              <div style={{ fontSize: 12, color: "var(--text2)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <span>{formatPoste(e.poste, e) || "—"}</span>
                                {adminRole === "global" && (
                                  <span className="badge" style={{ fontSize: 9, background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A", padding: "1px 6px" }}>👑 Admin Global</span>
                                )}
                                {adminRole === "dakar" && (
                                  <span className="badge" style={{ fontSize: 9, background: "#EFF6FF", color: "#1E40AF", border: "1px solid #BFDBFE", padding: "1px 6px" }}>🛡️ Admin Dakar</span>
                                )}
                                {adminRole === "france" && (
                                  <span className="badge" style={{ fontSize: 9, background: "#EFF6FF", color: "#1E40AF", border: "1px solid #BFDBFE", padding: "1px 6px" }}>🛡️ Admin France</span>
                                )}
                              </div>
                            </td>
                            <td><span className="badge b-blue" style={{ fontSize: 10 }}>{e.departement || "—"}</span></td>
                            <td><span className={"badge " + (e.devise === "XOF" ? "b-xof" : "b-navy")} style={{ fontSize: 10 }}>{e.site || "Dakar"}</span></td>
                            <td><span className={"badge " + (e.devise === "XOF" ? "b-xof" : "b-blue")} style={{ fontSize: 10 }}>{e.devise || "EUR"}</span></td>
                            <td style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text3)" }}>{e.login}</td>
                            <td style={{ display: "flex", gap: 4 }}>
                              <button className="btn btn-ghost btn-xs" onClick={async () => {
                                const newPwd = window.prompt(`Saisissez le nouveau mot de passe pour ${e.prenom} ${e.nom} :`);
                                if (!newPwd) return;
                                if (window.confirm(`Êtes-vous sûr de vouloir modifier le mot de passe de ${e.prenom} ?`)) {
                                  const { error } = await supabase.from("employes").update({ password: newPwd }).eq("id", e.id);
                                  if (error) alert("Erreur: " + error.message);
                                  else alert("Mot de passe réinitialisé avec succès !");
                                }
                              }}>🔑 Reset MDP</button>
                              <button className="btn btn-ghost btn-xs" onClick={() => {
                                const isF = e.genre === "Femme" || e.genre === "F" || ["habibatou", "habiba", "awa", "mame khady", "ngone", "jocelyne", "deguene", "bitylokho", "bethy", "maimouna"].some(fn => String(e.prenom).toLowerCase().includes(fn));
                                setForm({ ...e, genre: isF ? "Femme" : (e.genre || "Homme"), nb_epouses: isF ? "0" : String(e.nb_epouses || 0), password: "", _adminRole: getEmployeAdminRole(e) });
                                setModal("modifier");
                              }}>✏️ Modifier</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ÉTAT DES SALAIRES */}
          {tab === "etat_salaires" && (
            <div>
              <div className="sech">
                <span className="sect">
                  État des salaires mensuels
                  <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: 6 }}>· Sénégal (FCFA)</span>
                </span>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <select className="sinp" style={{ width: 130, height: 36, padding: "0 10px" }} value={reportMois} onChange={e => setReportMois(e.target.value)}>
                    {MOIS.map((m, i) => <option key={m} value={m}>{MONTHS[i]}</option>)}
                  </select>
                  <select className="sinp" style={{ width: 100, height: 36, padding: "0 10px" }} value={reportAnnee} onChange={e => setReportAnnee(e.target.value)}>
                    {["2024", "2025", "2026", "2027"].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  {reportRows.length > 0 && (
                    <button className="btn btn-ghost btn-sm" onClick={exportEtatSalairesCSV} disabled={exportingZip}>⬇ Exporter Excel</button>
                  )}
                </div>
              </div>

              {reportRows.length === 0 ? (
                <div className="empty">
                  <div className="empty-ic">🇸🇳</div>
                  <div className="empty-t">Aucune fiche de paie générée pour cette période</div>
                  <div className="empty-s">Générez des bulletins de paie dans l'onglet "Bulletins de paie" pour voir les données s'afficher ici.</div>
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
                    <div className="kcard" style={{ padding: "16px 20px" }}>
                      <div className="klbl" style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase" }}>Masse Salariale brute</div>
                      <div className="kval" style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>{totalBrut.toLocaleString('fr-FR')} <span style={{ fontSize: 11, fontWeight: 600 }}>FCFA</span></div>
                    </div>
                    <div className="kcard" style={{ padding: "16px 20px" }}>
                      <div className="klbl" style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase" }}>Charges Patronales</div>
                      <div className="kval" style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>{totalChargesSociales.toLocaleString('fr-FR')} <span style={{ fontSize: 11, fontWeight: 600 }}>FCFA</span></div>
                    </div>
                    <div className="kcard" style={{ padding: "16px 20px" }}>
                      <div className="klbl" style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase" }}>Total Retenues IR</div>
                      <div className="kval" style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>{totalIR.toLocaleString('fr-FR')} <span style={{ fontSize: 11, fontWeight: 600 }}>FCFA</span></div>
                    </div>
                    <div className="kcard" style={{ padding: "16px 20px" }}>
                      <div className="klbl" style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase" }}>Total CFCE + TRIMF</div>
                      <div className="kval" style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>{(totalCFCE + totalTRIMF).toLocaleString('fr-FR')} <span style={{ fontSize: 11, fontWeight: 600 }}>FCFA</span></div>
                    </div>
                  </div>

                  {/* Table with horizontal scroll support */}
                  <div className="tblwrap" style={{ overflowX: "auto" }}>
                    <table className="tbl" style={{ minWidth: 1200 }}>
                      <thead>
                        <tr>
                          <th>Collaborateur</th>
                          <th>Poste</th>
                          <th style={{ textAlign: "right" }}>Salaire Brut</th>
                          <th style={{ textAlign: "right" }}>Transport</th>
                          <th style={{ textAlign: "right" }}>Rém. Directe</th>
                          <th style={{ textAlign: "right" }}>Charges Soc. Pat.</th>
                          <th style={{ textAlign: "right" }}>Prestation Fam.</th>
                          <th style={{ textAlign: "right" }}>Accident Trav.</th>
                          <th style={{ textAlign: "right" }}>IPRES (Sal+Pat)</th>
                          <th style={{ textAlign: "right" }}>Retenue IR</th>
                          <th style={{ textAlign: "right" }}>CFCE</th>
                          <th style={{ textAlign: "right" }}>TRIMF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportRows.map((r, i) => (
                          <tr key={i}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Av initials={r.employe.avatar_initials} color={r.employe.avatar_color} size={26} fs={10} />
                                <span style={{ fontWeight: 600, fontSize: 12 }}>{r.employe.prenom} {r.employe.nom}</span>
                              </div>
                            </td>
                            <td style={{ fontSize: 11, color: "var(--text3)" }}>{formatPoste(r.employe.poste, r.employe) || "—"}</td>
                            <td style={{ textAlign: "right", fontWeight: 600 }}>{r.brut.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--text2)" }}>{r.transport.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--text2)" }}>{r.remuDirecte.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--text2)", fontWeight: 500 }}>{r.chargesSociales.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--text3)" }}>{r.prestFam.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--text3)" }}>{r.accTrav.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--text2)" }}>{r.ipres.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--red)", fontWeight: 500 }}>{r.ir.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--text3)" }}>{r.cfce.toLocaleString('fr-FR')}</td>
                            <td style={{ textAlign: "right", color: "var(--text3)" }}>{r.trimf.toLocaleString('fr-FR')}</td>
                          </tr>
                        ))}
                        {/* Totals Row */}
                        <tr style={{ background: "rgba(15,43,122,0.06)", fontWeight: 700, borderTop: "2px solid var(--border)" }}>
                          <td colSpan="2" style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>Total Général</td>
                          <td style={{ textAlign: "right", color: "var(--blue)" }}>{totalBrut.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right" }}>{totalTransport.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right" }}>{totalRemuDirecte.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right", color: "var(--blue)" }}>{totalChargesSociales.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right" }}>{totalPrestFam.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right" }}>{totalAccTrav.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right" }}>{totalIpres.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right", color: "var(--red)" }}>{totalIR.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right" }}>{totalCFCE.toLocaleString('fr-FR')}</td>
                          <td style={{ textAlign: "right" }}>{totalTRIMF.toLocaleString('fr-FR')}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── MESSAGERIE ── toujours monté pour garder les listeners actifs */}
          <div style={{ display: tab === "messagerie" ? "block" : "none" }}>
            <MessagerieTab user={user} employes={allEmployes.length ? allEmployes : employes} empActifs={allEmployes.filter(e => e.actif !== false)} mySite={mySite} onUnreadChange={setMsgUnread} setConfirmModal={setConfirmModal} isVisible={tab === "messagerie"} />
          </div>

        </div>
      </div>

      {/* BOTTOM NAV MOBILE */}
      <div className="bnav"><div className="bnav-inner">
        {ANAV.slice(0, 5).map(n => (
          <button key={n.id} className={"btab" + (tab === n.id ? " on" : "")} onClick={() => setTab(n.id)} style={{ position: "relative" }}>
            {n.badge > 0 && <span className="btab-bdg">{n.badge}</span>}
            <span className="btab-ic">{n.ic}</span><span className="btab-lb">{n.label.split(" ")[0]}</span>
          </button>
        ))}
      </div></div>
    </div>

    {/* ── MODALS ── */}

    {/* MODAL UPLOADER BULLETIN (FRANCE / PARIS) */}
    {modal === "upload_bulletin" && (
      <div className="ov" onClick={() => setModal(null)}>
        <div className="modal" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
          <div className="mh" style={{ background: "linear-gradient(135deg,#0D2680,var(--blue))", borderRadius: "16px 16px 0 0" }}>
            <div>
              <div className="mt" style={{ color: "#fff", fontSize: 17 }}>📤 Uploader un bulletin de paie (France)</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.7)", marginTop: 3 }}>
                Dépôt du bulletin PDF officiel pour les collaborateurs basés en France (Paris)
              </div>
            </div>
            <button className="mx" style={{ color: "rgba(255,255,255,.7)" }} onClick={() => setModal(null)}>✕</button>
          </div>

          <div className="mb" style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#1E40AF" }}>
              ℹ️ <strong>Régime France</strong> : La paie est gérée sur votre logiciel externe. Déposez ici le fichier PDF pour que le salarié le retrouve dans son espace.
            </div>

            {/* Collaborateur France */}
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Collaborateur (France) *</label>
              <select className="fsel" value={form.employe_id || ""} onChange={e => {
                const eid = e.target.value;
                const emp = employes.find(x => x.id === eid);
                setForm(f => ({ ...f, employe_id: eid, site: emp?.site || "France", devise: emp?.devise || "EUR" }));
              }}>
                <option value="">Sélectionner un collaborateur en France…</option>
                {empActifs.filter(e => e.site === "France").map(e => (
                  <option key={e.id} value={e.id}>
                    {e.prenom} {e.nom} — {formatPoste(e.poste, e) || e.departement} (🇫🇷 France)
                  </option>
                ))}
              </select>
            </div>

            {/* Période */}
            <div className="fgrid2">
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Mois du bulletin *</label>
                <select className="fsel" value={form.mois || String(new Date().getMonth() + 1).padStart(2, "0")} onChange={e => setForm(f => ({ ...f, mois: e.target.value }))}>
                  {MOIS.map((m, i) => <option key={m} value={m}>{MONTHS[i]}</option>)}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="flbl">Année *</label>
                <select className="fsel" value={form.annee || String(new Date().getFullYear())} onChange={e => setForm(f => ({ ...f, annee: e.target.value }))}>
                  {["2024", "2025", "2026", "2027"].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            {/* Upload Zone */}
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="flbl">Fichier du bulletin (PDF officiel) *</label>
              <label className={"upload-pretty" + (form._fileName ? " has-file" : "")} style={{ cursor: "pointer", border: "2px dashed var(--border2)", borderRadius: 10, padding: 20, textAlign: "center", background: form._fileName ? "#f0fdf4" : "var(--bg)", display: "block" }}>
                <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }} onChange={async e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setForm(f => ({ ...f, _file: file, _fileName: file.name, _fileSize: (file.size / 1024).toFixed(1) + " Ko", _isReading: true }));
                    const extracted = await extractSalaryFromPdfFile(file);
                    setForm(f => ({
                      ...f,
                      _file: file,
                      _fileName: file.name,
                      _fileSize: (file.size / 1024).toFixed(1) + " Ko",
                      _isReading: false,
                      salaire_net: extracted.net || f.salaire_net || null,
                      salaire_brut: extracted.brut || f.salaire_brut || null
                    }));
                  }
                }} />
                <div style={{ fontSize: 32 }}>{form._fileName ? "📄" : "☁️"}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: form._fileName ? "var(--green)" : "var(--text)", marginTop: 4 }}>
                  {form._fileName ? form._fileName : "Cliquez pour sélectionner le bulletin PDF"}
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                  {form._fileSize ? `Taille : ${form._fileSize}` : "Format .PDF uniquement"}
                </div>
              </label>
            </div>

            {/* Détection automatique & Saisie de secours du salaire net */}
            {form._fileName && (
              <div style={{ background: form.salaire_net ? "#ECFDF5" : "#FFFBEB", border: `1px solid ${form.salaire_net ? "#A7F3D0" : "#FDE68A"}`, borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: form.salaire_net ? "#065F46" : "#92400E" }}>
                    {form._isReading ? "⏳ Détection automatique en cours…" : (form.salaire_net ? "✓ Salaire net détecté" : "⚠️ Montant net non détecté automatiquement")}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                    {form.salaire_net ? "Montant qui apparaîtra sur le profil du salarié" : "Vous pouvez saisir le montant net manuellement :"}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="number"
                    step="any"
                    className="finp"
                    style={{ width: 110, padding: "6px 8px", fontSize: 13, fontWeight: 700, textAlign: "right" }}
                    placeholder="Montant net"
                    value={form.salaire_net || ""}
                    onChange={e => setForm(f => ({ ...f, salaire_net: e.target.value }))}
                  />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>€</span>
                </div>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
              <button className="btn btn-ghost" type="button" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
              <button className="btn btn-primary" type="button" onClick={uploaderBulletinPDF} disabled={saving || !form.employe_id || !form._file}>
                {saving ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                    Téléversement en cours…
                  </span>
                ) : "✓ Publier le bulletin PDF (France)"}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* MODAL GÉNÉRER FICHE (DAKAR / SÉNÉGAL) */}
    {modal === "fiche" && (
      <div className="ov" onClick={() => setModal(null)}>
        <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="mh" style={{ background: "var(--blue)", borderRadius: "16px 16px 0 0" }}>
            <div>
              <div className="mt" style={{ color: "#fff", fontSize: 17 }}>✦ Générer un bulletin de paie (Dakar)</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.7)", marginTop: 3 }}>
                Calcul automatique · Législation & Code du Travail du Sénégal (FCFA)
              </div>
            </div>
            <button className="mx" style={{ color: "rgba(255,255,255,.7)" }} onClick={() => setModal(null)}>✕</button>
          </div>

          <div className="mb" style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>

            {/* ── SECTION 1 : Employé & Période ── */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 10 }}>① Employé & Période (Dakar)</div>
              <div className="field" style={{ marginBottom: 10 }}>
                <label className="flbl">Employé (Dakar)</label>
                <select className="fsel" value={form.employe_id || ""} onChange={e => {
                  const eid = e.target.value;
                  const lb = fiches.find(f => f.employe_id === eid)?.salaire_brut || 0;
                  const emp = employes.find(e => e.id === eid);
                  setForm(f => ({
                    ...f,
                    employe_id: eid,
                    retenue_absences: calcRetAbs(eid, f.mois, f.annee, lb) || "",
                    // Pré-remplir état civil
                    parts_fiscales: String(emp?.parts_fiscales || 1),
                    nb_epouses: String(emp?.nb_epouses || 0),
                    nb_enfants: String(emp?.nb_enfants || 0),
                    categorie: emp?.categorie || f.categorie || "",
                    regime_cadre: emp?.categorie && CONVENTION_COMMERCE[emp.categorie]?.cadre ? "oui" : "non",
                    horaire: f.horaire || (emp?.devise === "XOF" ? "173,33" : "151,67"),
                    // ── Pré-remplir Rémunération depuis le profil employé ──
                    salaire_base: emp?.salaire_base ? String(emp.salaire_base) : (f.salaire_base || ""),
                    sursalaire: emp?.sursalaire ? String(emp.sursalaire) : "",
                    prime_anciennete: emp?.prime_anciennete ? String(emp.prime_anciennete) : "",
                    prime_performance: emp?.prime_performance ? String(emp.prime_performance) : "",
                    autres_primes: emp?.autres_primes ? String(emp.autres_primes) : "",
                    indem_transport: emp?.indem_transport ? String(emp.indem_transport) : "",
                    indem_repas: emp?.indem_repas ? String(emp.indem_repas) : "",
                    avantages_nature: emp?.avantages_nature ? String(emp.avantages_nature) : "",
                  }));
                }}>
                  <option value="">Sélectionner un collaborateur à Dakar…</option>
                  {empActifs.filter(e => (e.site || "Dakar") !== "France").map(e => (
                    <option key={e.id} value={e.id}>{e.prenom} {e.nom} — {formatPoste(e.poste, e) || e.departement} (🇸🇳 Dakar)</option>
                  ))}
                </select>
              </div>
              {calcEmp && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 8, marginBottom: 10 }}>
                  <div className="av" style={{ width: 34, height: 34, background: calcEmp.avatar_color || "var(--blue)", fontSize: 12, flexShrink: 0 }}>{calcEmp.avatar_initials || "?"}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{calcEmp.prenom} {calcEmp.nom}</div>
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>{formatPoste(calcEmp.poste, calcEmp)} · {calcEmp.site} · <span style={{ color: "var(--blue)", fontWeight: 500 }}>{calcEmp.devise === "XOF" ? "Régime sénégalais (XOF)" : "Régime France (EUR)"}</span></div>
                    {/* Indicateurs état civil */}
                    <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
                      {calcEmp.parts_fiscales && <span style={{ background: "#D1FAE5", color: "#065F46", fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 600 }}>✓ {calcEmp.parts_fiscales} part{calcEmp.parts_fiscales > 1 ? "s" : ""} IR</span>}
                      {calcEmp.situation_matrimoniale && <span style={{ background: "#EDE9FE", color: "#5B21B6", fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 500 }}>{calcEmp.situation_matrimoniale}</span>}
                      {calcEmp.nb_enfants > 0 && <span style={{ background: "#FEF3C7", color: "#92400E", fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 500 }}>{calcEmp.nb_enfants} enfant{calcEmp.nb_enfants > 1 ? "s" : ""}</span>}
                      {calcEmp.categorie && calcEmp.devise === "XOF" && <span style={{ background: "var(--blue-ll)", color: "var(--blue)", fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 600 }}>Cat. {calcEmp.categorie}</span>}
                      {!calcEmp.date_naissance && calcEmp.devise === "XOF" && <span style={{ background: "#FEE2E2", color: "var(--red)", fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 500 }}>⚠ État civil incomplet</span>}
                    </div>
                  </div>
                  <span className={"badge " + (calcEmp.devise === "XOF" ? "b-xof" : "b-blue")}>{calcEmp.devise}</span>
                </div>
              )}
              <div className="fgrid2">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="flbl">Mois</label>
                  <select className="fsel" value={form.mois || ""} onChange={e => setForm(f => ({ ...f, mois: e.target.value }))}>
                    {MOIS.map((m, i) => <option key={m} value={m}>{MONTHS[i]}</option>)}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="flbl">Année</label>
                  <select className="fsel" value={form.annee || new Date().getFullYear()} onChange={e => setForm(f => ({ ...f, annee: e.target.value }))}>
                    {[2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              {/* Type de contrat — conditionnel selon régime */}
              <div className="field" style={{ marginBottom: 0, marginTop: 10 }}>
                <label className="flbl">Type de contrat</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8, marginTop: 6 }}>
                  {(calcEmp?.devise === "XOF" ? [
                    ["cdi", "💼 CDI / CDD"],
                    ["stagiaire", "🎓 Stagiaire"],
                  ] : [
                    ["cdi", "💼 CDI / CDD"],
                    ["alternance", "🎓 Alternance"],
                    ["stagiaire", "🛂 Stagiaire"],
                  ]).map(([val, label]) => (
                    <button key={val} type="button"
                      onClick={() => setForm(f => ({ ...f, type_contrat: val }))}
                      style={{
                        padding: "8px 12px", borderRadius: 8, border: "1.5px solid",
                        borderColor: (!form.type_contrat && val === "cdi") || form.type_contrat === val ? "var(--blue)" : "var(--border)",
                        background: (!form.type_contrat && val === "cdi") || form.type_contrat === val ? "var(--blue-ll)" : "var(--white)",
                        color: (!form.type_contrat && val === "cdi") || form.type_contrat === val ? "var(--blue)" : "var(--text2)",
                        fontSize: 12, fontWeight: (!form.type_contrat && val === "cdi") || form.type_contrat === val ? 700 : 400,
                        cursor: "pointer", textAlign: "center", transition: "all .12s"
                      }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Champ spécial alternance */}
              {(!calcEmp || calcEmp.devise !== "XOF") && (form.type_contrat === "alternance") && (
                <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "10px 14px", marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#92400E", marginBottom: 6 }}>🎓 Alternance — Exonération quasi-totale ≤ 79% SMIC (1 801,80 €)</div>
                  <div className="fgrid2">
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label className="flbl">% du SMIC</label>
                      <select className="fsel" value={form.pct_smic || "80"} onChange={e => {
                        const pct = parseInt(e.target.value);
                        const brutAlt = Math.round(1802 * pct / 100);
                        // Toujours mettre à jour le salaire depuis le % SMIC en mode alternance
                        setForm(f => ({ ...f, pct_smic: String(pct), salaire_base: String(brutAlt) }));
                      }}>                      <option value="27">27% — 1ère année, &lt;18 ans</option>
                        <option value="39">39% — 1ère année, 18-25 ans</option>
                        <option value="43">43% — 2ème année, &lt;18 ans</option>
                        <option value="51">51% — 2ème année, 18-25 ans</option>
                        <option value="53">53% — 3ème année, &lt;18 ans</option>
                        <option value="67">67% — 3ème année, 18-25 ans</option>
                        <option value="80">80% — +26 ans (année 1)</option>
                        <option value="85">85% — +26 ans (année 2)</option>
                        <option value="100">100% SMIC ou +</option>
                      </select>
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label className="flbl">Salaire brut calculé</label>
                      <div style={{ padding: "10px 13px", background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "var(--blue)" }}>
                        {fN(Math.round(1802 * (n(form.pct_smic) || 80) / 100), "EUR")}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {(form.type_contrat === "stagiaire") && (
                <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 8, padding: "10px 14px", marginTop: 8, fontSize: 11, color: "var(--blue)" }}>
                  {calcEmp?.devise === "XOF"
                    ? "🎓 Stagiaire Dakar : pas d'IPRES ni d'IR. TRIMF uniquement si gratification > 25 000 FCFA (CGI art.183)"
                    : "🛂 Stagiaire France : pas de cotisations si gratification ≤ 618€/mois (franchise 15% PASS 2025). CSG/CRDS sur la part dépassant ce seuil."}
                </div>
              )}
            </div>

            {/* ── SECTION 2 : Rémunération ── */}
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 10 }}>② Éléments de rémunération</div>
              <div className="fgrid2">
                {[
                  ["Salaire de base *", "salaire_base", "Saisir le montant"],
                  ["Sursalaire", "sursalaire", ""],
                  ["Indemnités de congés (imposables)", "indem_conges", "ex : 150 000"],
                  ["Heures d'absence", "heures_absence", "ex : 10"],
                  ["Prime d'ancienneté", "prime_anciennete", ""],
                  ["Prime de performance", "prime_performance", ""],
                  ["Autres primes", "autres_primes", ""],
                  ["Avantages en nature", "avantages_nature", ""],
                ].map(([l, k, ph]) => (
                  <div key={k} className="field" style={{ marginBottom: 8 }}>
                    <label className="flbl">{l}</label>
                    <input type="number" className="finp" placeholder={ph} value={form[k] || ""} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
            </div>

            {/* ── SECTION 3 : Indemnités ── */}
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px" }}>③ Indemnités exonérées d'IR</div>
                <span style={{ fontSize: 10, color: "var(--text3)" }}>Non soumises à cotisations</span>
              </div>
              <div className="fgrid2">
                {[
                  ["Indemnité de transport", "indem_transport", "ex : 26 000"],
                  ["Indemnité de repas", "indem_repas", "ex : 50 000"],
                ].map(([l, k, ph]) => (
                  <div key={k} className="field" style={{ marginBottom: 0 }}>
                    <label className="flbl">{l}</label>
                    <input type="number" className="finp" placeholder={ph} value={form[k] || ""} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
            </div>

            {/* ── SECTION 3b : Déductions & Situation familiale ── */}
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 10 }}>③b Déductions & Situation familiale</div>
              <div className="fgrid2">
                {/* Le champ 'Remboursement prêt N°1' a été retiré conformément aux conventions */}
                <div className="field" style={{ marginBottom: 8 }}>
                  <label className="flbl">Avance sur salaire <span style={{ color: "var(--text3)", fontWeight: 400, fontSize: 10 }}>(retenue)</span></label>
                  <input type="number" className="finp" placeholder="0" value={form.avance_salaire || ""} onChange={e => setForm(f => ({ ...f, avance_salaire: e.target.value }))} />
                </div>
                {calcEmp?.devise === "XOF" && (
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label className="flbl">Retenue Frais Médicaux IPM <span style={{ color: "var(--text3)", fontWeight: 400, fontSize: 10 }}>(20% soins du mois)</span></label>
                    <input type="number" className="finp" placeholder="0" value={form.retenue_frais_medicaux || ""} onChange={e => setForm(f => ({ ...f, retenue_frais_medicaux: e.target.value }))} />
                  </div>
                )}
                {calcEmp?.devise === "XOF" && <>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label className="flbl">Nombre d'épouses</label>
                    <select className="fsel" value={form.nb_epouses || "0"} onChange={e => setForm(f => ({ ...f, nb_epouses: e.target.value }))}>
                      {["0", "1", "2", "3", "4"].map(v => <option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label className="flbl">Nombre d'enfants à charge</label>
                    <select className="fsel" value={form.nb_enfants || "0"} onChange={e => {
                      const nEnf = parseInt(e.target.value || 0);
                      const nEp = parseInt(form.nb_epouses || 0);
                      setForm(f => ({ ...f, nb_enfants: String(nEnf), parts_fiscales: String(Math.min(5, 1 + Math.min(4, nEnf) * 0.5 + nEp * 0.5)) }));
                    }}>
                      {["0", "1", "2", "3", "4", "5", "6", "7"].map(v => <option key={v}>{v}</option>)}
                    </select>
                  </div>
                </>}
              </div>
            </div>

            {/* ── SECTION 4 : Situation fiscale & Options (XOF uniquement) ── */}
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 10 }}>
                ④ {calcEmp?.devise === "XOF" ? "Situation fiscale & Options (Sénégal)" : "Options & Paramètres (France)"}
              </div>
              {calcEmp?.devise === "XOF" ? (
                <>
                  <div className="fgrid2">
                    <div className="field" style={{ marginBottom: 8 }}>
                      <label className="flbl">Parts fiscales (quotient familial)</label>
                      <select className="fsel" value={form.parts_fiscales || "1"} onChange={e => setForm(f => ({ ...f, parts_fiscales: e.target.value }))}>
                        <option value="1">1 part — Célibataire</option>
                        <option value="1.5">1,5 part — Marié sans enfant</option>
                        <option value="2">2 parts — 1 enfant</option>
                        <option value="2.5">2,5 parts — 2 enfants</option>
                        <option value="3">3 parts — 3 enfants</option>
                        <option value="3.5">3,5 parts — 4 enfants</option>
                        <option value="4">4 parts — 5 enfants</option>
                        <option value="5">5 parts — Maximum légal</option>
                      </select>
                    </div>
                    <div className="field" style={{ marginBottom: 8 }}>
                      <label className="flbl">Heures travaillées</label>
                      <input className="finp" placeholder="173h33" value={form.horaire || ""} onChange={e => setForm(f => ({ ...f, horaire: e.target.value }))} />
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label className="flbl">Catégorie convention</label>
                      <input className="finp" placeholder="ex : 6A, 7B…" value={form.categorie || ""} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))} />
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label className="flbl">Matricule</label>
                      <input className="finp" placeholder="Optionnel" value={form.matricule || ""} onChange={e => setForm(f => ({ ...f, matricule: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    {[
                      ["regime_cadre", "IPRES Complémentaire cadres (3,6%)"],
                      ["with_ipm", "IPM Prévoyance Maladie (Forfait 8 500 F sal / 8 500 F pat)"],
                    ].map(([key, label]) => (
                      <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text2)", cursor: "pointer", padding: "7px 12px", border: "1.5px solid", borderColor: form[key] === "oui" ? "var(--blue)" : "var(--border)", borderRadius: 8, background: form[key] === "oui" ? "var(--blue-ll)" : "var(--white)", transition: "all .12s" }}>
                        <input type="checkbox" checked={form[key] === "oui"} onChange={e => setForm(f => ({ ...f, [key]: e.target.checked ? "oui" : "non" }))} style={{ width: 14, height: 14, accentColor: "var(--blue)" }} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                /* ── OPTIONS FRANCE ── */
                <>
                  <div style={{ background: "var(--blue-ll)", border: "1px solid var(--blue-l)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "var(--blue)" }}>
                    ℹ️ Taux appliqués automatiquement : CSG/CRDS 9,7% · Retraite CNAV 6,9% (plafond SS 3 864€/mois) · ARRCO 3,15% · Maladie patronal 7% · Chômage pat. 4,05%
                  </div>
                  <div className="fgrid2">
                    <div className="field" style={{ marginBottom: 8 }}>
                      <label className="flbl">Heures travaillées</label>
                      <input className="finp" placeholder="151h67" value={form.horaire || ""} onChange={e => setForm(f => ({ ...f, horaire: e.target.value }))} />
                    </div>
                    <div className="field" style={{ marginBottom: 8 }}>
                      <label className="flbl">Convention collective</label>
                      <select className="fsel" value={form.convention_fr || "syntec"} onChange={e => setForm(f => ({ ...f, convention_fr: e.target.value }))}>
                        <option value="syntec">Syntec (Bureaux d'études)</option>
                        <option value="commerce">Commerce de détail</option>
                        <option value="metallurgie">Métallurgie</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label className="flbl">Catégorie / Niveau</label>
                      <input className="finp" placeholder="ex : Cadre, AM, Employé…" value={form.categorie || ""} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))} />
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label className="flbl">Matricule</label>
                      <input className="finp" placeholder="Optionnel" value={form.matricule || ""} onChange={e => setForm(f => ({ ...f, matricule: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text2)", cursor: "pointer", padding: "7px 12px", border: "1.5px solid", borderColor: form.is_cadre_fr === "oui" ? "var(--blue)" : "var(--border)", borderRadius: 8, background: form.is_cadre_fr === "oui" ? "var(--blue-ll)" : "var(--white)", transition: "all .12s" }}>
                      <input type="checkbox" checked={form.is_cadre_fr === "oui"} onChange={e => setForm(f => ({ ...f, is_cadre_fr: e.target.checked ? "oui" : "non" }))} style={{ width: 14, height: 14, accentColor: "var(--blue)" }} />
                      <span>Statut cadre (AGIRC T2 + cotisations sup.)</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text2)", cursor: "pointer", padding: "7px 12px", border: "1.5px solid", borderColor: form.with_prevoyance_fr === "oui" ? "var(--blue)" : "var(--border)", borderRadius: 8, background: form.with_prevoyance_fr === "oui" ? "var(--blue-ll)" : "var(--white)", transition: "all .12s" }}>
                      <input type="checkbox" checked={form.with_prevoyance_fr === "oui"} onChange={e => setForm(f => ({ ...f, with_prevoyance_fr: e.target.checked ? "oui" : "non" }))} style={{ width: 14, height: 14, accentColor: "var(--blue)" }} />
                      <span>Prévoyance supplémentaire</span>
                    </label>
                  </div>
                </>
              )}
            </div>

            {/* ── Absences injustifiées ── */}
            {(() => {
              const eid = form.employe_id; const mois = parseInt(form.mois); const annee = parseInt(form.annee || new Date().getFullYear());
              const lst = absences.filter(a => a.employe_id === eid && a.statut === "approuve" && a.type_absence === "Absence injustifiée" && new Date(a.date_debut).getMonth() + 1 === mois && new Date(a.date_debut).getFullYear() === annee);
              if (!lst.length) return null;
              const lb = fiches.find(f => f.employe_id === eid)?.salaire_brut || liveCalc?.brut || 0;
              const tauxJ = lb ? Math.round(lb / 30) : 0;
              return (
                <div className="abs-alert" style={{ marginBottom: 0 }}>
                  <div className="abs-alert-t">⚠ Absences injustifiées validées ce mois — retenue automatique calculée</div>
                  {lst.map((a, i) => (
                    <div key={i} className="abs-alert-row">
                      <span>{fDs(a.date_debut)} → {fDs(a.date_fin)} · {a.nb_jours}j · {fN(tauxJ, calcEmp?.devise || "XOF")}/j</span>
                      <span style={{ fontWeight: 700, color: "var(--red)" }}>- {fN(tauxJ * (a.nb_jours || 0), calcEmp?.devise || "XOF")}</span>
                    </div>
                  ))}
                  <div style={{ borderTop: "1px solid #FECACA", paddingTop: 8, marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "var(--text3)" }}>Retenue totale (modifiable si besoin)</span>
                    <input type="number" className="finp" style={{ width: 140, textAlign: "right" }} value={form.retenue_absences || ""} onChange={e => setForm(f => ({ ...f, retenue_absences: e.target.value }))} />
                  </div>
                </div>
              );
            })()}

            {/* ── RÉCAPITULATIF LIVE ── */}
            {liveCalc && liveCalc.brut > 0 && (
              <div style={{ background: "#0C1B3A", borderRadius: 12, padding: "18px 20px", color: "#fff", border: "none" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,.4)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 14 }}>
                  Simulation — {(form.type_contrat === "alternance") ? "Contrat d'alternance" : (form.type_contrat === "stagiaire") ? "Convention de stage" : "Calcul automatique"}
                  {liveCalc?.note && <div style={{ marginTop: 4, fontSize: 10, color: "#FCD34D", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>{liveCalc.note}</div>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", marginBottom: 14 }}>
                  {(calcEmp.devise === "XOF" ? [
                    ["💰 SÉNÉGAL — Salaire Brut Global", fN(liveCalc.brut, calcEmp.devise), false],
                    ["PART SALARIALE (Retenues)", "...", false],
                    ["IPRES Régime Général (5,6%)", "-" + fN(liveCalc.ipres_sal, calcEmp.devise), true],
                    form.regime_cadre === "oui" ? ["IPRES Régime Cadre (2,4%)", "-" + fN(liveCalc.ipres_comp_sal || 0, calcEmp.devise), true] : null,
                    ["IPM Santé (Forfait)", "-" + fN(liveCalc.ipm_sal || 0, calcEmp.devise), true],
                    ["TRIMF", "-" + fN(liveCalc.trimf, calcEmp.devise), true],
                    ["IR (Impôt sur le Revenu)", "-" + fN(liveCalc.irMensuel, calcEmp.devise), true],
                    ["PART PATRONALE (Assurances employeur)", "...", false],
                    ["IPRES Régime Général (8,4%)", "+" + fN(liveCalc.ipres_pat || 0, calcEmp.devise), false],
                    form.regime_cadre === "oui" ? ["IPRES Régime Cadre (3,6%)", "+" + fN(liveCalc.ipres_comp_pat || 0, calcEmp.devise), false] : null,
                    form.with_ipm === "oui" ? ["IPM Santé Patronal (Forfait)", "+" + fN(liveCalc.ipm_pat || 0, calcEmp.devise), false] : null,
                    ["CSS / Allocations Familiales", "+" + fN(liveCalc.prest_fam || 0, calcEmp.devise), false],
                    ["CSS / Accidents de Travail", "+" + fN(liveCalc.acc_trav || 0, calcEmp.devise), false],
                    ["CFCE (Contribution Forfaitaire 3%)", "+" + fN(liveCalc.cfce || 0, calcEmp.devise), false],
                  ] : [
                    ["Salaire brut", fN(liveCalc.brut, calcEmp.devise), false],
                    ["CSG non-déd. 2,4%", "-" + fN(Math.round(liveCalc.brut * 0.9825 * 0.024), calcEmp.devise), true],
                    ["CSG déd. + CRDS 7,3%", "-" + fN(Math.round(liveCalc.brut * 0.9825 * 0.073), calcEmp.devise), true],
                    ["Retraite CNAV 6,9%", "-" + fN(Math.round(Math.min(liveCalc.brut, 3864) * 0.069), calcEmp.devise), true],
                    ["ARRCO 3,15%", "-" + fN(Math.round(Math.min(liveCalc.brut, 3864) * 0.0315), calcEmp.devise), true],
                    liveCalc.prevoyance_sal > 0 ? ["Prévoyance 1%", "-" + fN(liveCalc.prevoyance_sal, calcEmp.devise), true] : null,
                    ["Chômage pat. 4,05%", "(" + fN(Math.round(liveCalc.brut * 0.0405), calcEmp.devise) + ")", false],
                  ]).filter(Boolean).concat([
                    liveCalc.retAbsAbsence > 0 ? ["Retenue pour absence", "-" + fN(liveCalc.retAbsAbsence, calcEmp.devise), true] : null,
                    liveCalc.retAbs > 0 ? ["Ret. absences", "-" + fN(liveCalc.retAbs, calcEmp.devise), true] : null,
                    n(form.avance_salaire) > 0 ? ["Avance salaire", "-" + fN(n(form.avance_salaire), calcEmp.devise), true] : null,
                    n(form.retenue_frais_medicaux) > 0 ? ["Retenue Frais Médicaux (20%)", "-" + fN(n(form.retenue_frais_medicaux), calcEmp.devise), true] : null,
                    n(form.indem_conges) > 0 ? ["+ Indemnités congés (imposables)", "+" + fN(n(form.indem_conges), calcEmp.devise), false] : null,
                    n(form.indem_transport) > 0 ? ["+ Transport", "+" + fN(n(form.indem_transport), calcEmp.devise), false] : null,
                    n(form.indem_repas) > 0 ? ["+ Repas", "+" + fN(n(form.indem_repas), calcEmp.devise), false] : null,
                    n(form.prime_performance) > 0 ? ["+ Prime perf.", "+" + fN(n(form.prime_performance), calcEmp.devise), false] : null,
                  ].filter(Boolean)).map(([l, v, red], i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: red ? "#FCA5A5" : "rgba(255,255,255,.65)" }}>
                      <span>{l}</span><span style={{ fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: "1px solid rgba(255,255,255,.1)", paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)" }}>Net à payer</div>
                    {calcEmp.devise === "XOF" ? (
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,.3)", marginTop: 2 }}>
                        Charges pat. : {fN((liveCalc.ipres_pat || 0) + (liveCalc.prest_fam || 0) + (liveCalc.acc_trav || 0) + (liveCalc.cfce || 0), calcEmp.devise)}
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,.3)", marginTop: 2 }}>
                        Charges pat. : Maladie {fN(Math.round(liveCalc.brut * 0.07), calcEmp.devise)} + Chômage {fN(Math.round(liveCalc.brut * 0.0405), calcEmp.devise)} + Retraite {fN(Math.round(Math.min(liveCalc.brut, 3864) * 0.0855), calcEmp.devise)}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: "-1px" }}>
                    {fN(Math.max(0, liveCalc.net), calcEmp.devise)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* FIX: Avertissement bulletin existant */}
          {bulletinExistant && (
            <div style={{ margin: "0 24px 0", padding: "10px 14px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, color: "#92400E" }}>Un bulletin existe déjà pour {MONTHS[parseInt(form.mois) - 1]} {form.annee || new Date().getFullYear()}</div>
                <div style={{ color: "#B45309", marginTop: 2 }}>Net : {fN(bulletinExistant.salaire_net, bulletinExistant.devise)} · Générer en écrasera l'existant.</div>
              </div>
            </div>
          )}

          <div className="mf" style={{ gap: 12, padding: "14px 24px" }}>
            <button className="btn btn-ghost" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center", position: "relative", overflow: "hidden" }}
              disabled={saving || !form.employe_id || !form.mois || !form.salaire_base} onClick={genererFiche}>
              {saving ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                  Génération en cours…
                </span>
              ) : (bulletinExistant ? "🔄 Regénérer le bulletin" : "✦ Générer & télécharger le bulletin")}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* MODAL CRÉER EMPLOYÉ */}
    {modal === "employe" && (
      <div className="ov" onClick={() => setModal(null)}>
        <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
          <div className="mh" style={{ background: "linear-gradient(135deg,#0D2680,var(--blue))", borderRadius: "16px 16px 0 0" }}>
            <div>
              <div className="mt" style={{ color: "#fff" }}>👤 Ajouter un collaborateur</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)", marginTop: 3 }}>Tous les champs avec * sont obligatoires</div>
            </div>
            <button className="mx" style={{ color: "rgba(255,255,255,.7)" }} onClick={() => setModal(null)}>✕</button>
          </div>
          <div className="mb"><EmpForm form={form} setForm={setForm} modal={modal} empTab={empTab} setEmpTab={setEmpTab} conges={conges} employes={employes} /></div>
          <div className="mf">
            <button className="btn btn-ghost" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn btn-primary" disabled={saving || !form.prenom || !form.login} onClick={creerEmploye}>{saving ? "Création…" : "Créer le collaborateur"}</button>
          </div>
        </div>
      </div>
    )}

    {/* MODAL MODIFIER EMPLOYÉ */}
    {modal === "modifier" && (
      <div className="ov" onClick={() => setModal(null)}>
        <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
          <div className="mh" style={{ background: "var(--bg)", borderBottom: "2px solid var(--blue)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Av initials={form.avatar_initials} color={form.avatar_color || "var(--blue)"} size={40} />
              <div>
                <div className="mt">{form.prenom} {form.nom}</div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{form.poste} · Modifier les informations</div>
              </div>
            </div>
            <button className="mx" onClick={() => setModal(null)}>✕</button>
          </div>
          <div className="mb"><EmpForm form={form} setForm={setForm} modal={modal} empTab={empTab} setEmpTab={setEmpTab} conges={conges} employes={employes} /></div>
          <div className="mf" style={{ justifyContent: "space-between" }}>
            <button className="btn btn-ghost" style={{ color: "var(--red)", borderColor: "transparent" }} onClick={() => {
              setConfirmModal({
                title: "Désactiver le compte",
                message: `Désactiver le compte de ${form.prenom} ${form.nom} ? Il n'aura plus accès à la plateforme.`,
                danger: true,
                confirmLabel: "Confirmer",
                onConfirm: async () => {
                  const { error } = await supabase.from("employes").update({ actif: false }).eq("id", form.id);
                  if (error) alert("Erreur: " + error.message);
                  else {
                    setEmployes(p => p.map(e => e.id === form.id ? { ...e, actif: false } : e));
                    setModal(null);
                    pushToast("👤", "Employé désactivé", `${form.prenom} ${form.nom} a été retiré de l'équipe.`);
                  }
                }
              });
            }}>🗑️ Désactiver</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn btn-primary" disabled={saving} onClick={modifierEmploye}>{saving ? "Enregistrement…" : "✓ Enregistrer les modifications"}</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* MODALE DE CONFIRMATION GLOBALE */}
    {confirmModal && (
      <div className="ov" style={{ zIndex: 3000 }} onClick={() => setConfirmModal(null)}>
        <div className="modal" style={{ maxWidth: 400, textAlign: "center", padding: "30px 24px" }} onClick={e => e.stopPropagation()}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>{confirmModal.danger ? "⚠️" : "❓"}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>{confirmModal.title}</div>
          <div style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.6, marginBottom: 24 }}>{confirmModal.message}</div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button className="btn btn-ghost" onClick={() => setConfirmModal(null)} style={{ minWidth: 120 }}>Annuler</button>
            <button className={"btn " + (confirmModal.danger ? "btn-danger" : "btn-primary")} onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }} style={{ minWidth: 120 }}>
              {confirmModal.confirmLabel || "Confirmer"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* MODAL MODIFIER ABSENCE */}
    {modal === "editAbs" && (
      <div className="ov" onClick={() => setModal(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="mh"><div><div className="mt">Modifier l'absence</div><div className="msub">{form.employes?.prenom} {form.employes?.nom}</div></div><button className="mx" onClick={() => setModal(null)}>✕</button></div>
          <div className="mb">
            <div className="field"><label className="flbl">Type d'absence</label>
              <select className="fsel" value={form.type_absence || ""} onChange={e => setForm(f => ({ ...f, type_absence: e.target.value }))}>
                {ABS_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid2">
              <div className="field"><label className="flbl">Date de début</label><input type="date" className="finp" value={form.date_debut || ""} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} /></div>
              <div className="field"><label className="flbl">Date de fin</label><input type="date" className="finp" value={form.date_fin || ""} onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))} /></div>
            </div>
            <div className="field"><label className="flbl">Motif</label><textarea className="fta" value={form.motif || ""} onChange={e => setForm(f => ({ ...f, motif: e.target.value }))} /></div>
            <div className="field"><label className="flbl">Statut</label>
              <select className="fsel" value={form.statut || "en_attente"} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}>
                <option value="en_attente">⏳ En attente</option>
                <option value="approuve">✓ Validé</option>
                <option value="refuse">✗ Refusé</option>
              </select>
            </div>
          </div>
          <div className="mf">
            <button className="btn btn-ghost" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn btn-primary" disabled={saving} onClick={async () => {
              setSaving(true);
              const nb = countJoursOuvrables(form.date_debut, form.date_fin);
              const upd = { type_absence: form.type_absence, date_debut: form.date_debut, date_fin: form.date_fin, nb_jours: nb, motif: form.motif, statut: form.statut, traite_par: user.prenom + " " + user.nom, traite_at: new Date().toISOString() };
              const { error } = await supabase.from("absences").update(upd).eq("id", form._absId);
              if (error) { setSaving(false); alert("Erreur: " + error.message); return; }
              setAbsences(p => p.map(a => a.id === form._absId ? { ...a, ...upd } : a));
              setSaving(false); setModal(null); setForm({});
            }}>{saving ? "Enregistrement…" : "✓ Enregistrer"}</button>
          </div>
        </div>
      </div>
    )}

    {/* MODAL ANNONCE */}
    {modal === "annonce" && (
      <div className="ov" onClick={() => setModal(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="mh"><div className="mt">Nouvelle annonce</div><button className="mx" onClick={() => setModal(null)}>✕</button></div>
          <div className="mb">
            <div className="field"><label className="flbl">Type</label>
              <select className="fsel" value={form.type_annonce || "info"} onChange={e => setForm(f => ({ ...f, type_annonce: e.target.value }))}>
                <option value="info">ℹ️ Information</option><option value="important">⚠️ Important</option><option value="urgent">🚨 Urgent</option>
              </select>
            </div>
            <div className="field"><label className="flbl">Titre</label><input className="finp" placeholder="Titre de l'annonce…" value={form.titre || ""} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} /></div>
            <div className="field"><label className="flbl">Contenu</label><textarea className="fta" style={{ minHeight: 100 }} value={form.contenu || ""} onChange={e => setForm(f => ({ ...f, contenu: e.target.value }))} /></div>
          </div>
          <div className="mf">
            <button className="btn btn-ghost" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn btn-primary" disabled={saving || !form.titre || !form.contenu} onClick={publierAnnonce}>{saving ? "Publication…" : "Publier"}</button>
          </div>
        </div>
      </div>
    )}

      {/* MODAL PRÉVISUALISATION PDF ADMIN */}
      <PdfPreviewModal preview={pdfPreview} onClose={() => setPdfPreview(null)} />

    </>);
}

/* ═══════════════════════════════════════════════════════
   LOGIN
═══════════════════════════════════════════════════════ */
function Login({ onLogin }) {
  const [login, setLogin] = useState("");
  const [pass, setPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async () => {
    if (!login || !pass) { setErr("Veuillez remplir tous les champs."); return; }
    setLoading(true); setErr("");
    const { data, error } = await supabase.from("employes").select("*").eq("login", login.trim().toLowerCase()).eq("password", pass).limit(1);
    if (error || !data?.length) { setErr("Identifiants incorrects."); setLoading(false); return; }
    
    // Purge toute ancienne session persistante locale
    try { localStorage.removeItem("majha_rh_sess"); } catch { }
    // Enregistrement uniquement dans la session active de l'onglet/navigateur
    try { sessionStorage.setItem("majha_rh_sess_secure", JSON.stringify(data[0])); } catch { }
    
    onLogin(data[0]);
    setLoading(false);
  };

  return (
    <><style>{CSS}</style>
      <div className="lp">
        <div className="lcard">
          <div className="llogo">
            <img src="/logo-majha.png" alt="MAJHA" style={{ width: 52, height: 52, objectFit: "contain", flexShrink: 0 }} />
            <div>
              <div className="lbrand">MAJHA Accounting</div>
              <div className="lsub">Espace RH interne</div>
            </div>
          </div>
          <div className="lh">Connexion</div>
          <div className="lp2">Accédez à votre espace personnel confidentiel</div>
          {err && <div className="lerr">{err}</div>}

          {/* Formulaire sécurisé sans balise form ni type password pour neutraliser totalement Edge */}
          <div onKeyDown={e => { if (e.key === "Enter") doLogin(); }}>
            <div className="lfield">
              <label className="llbl" htmlFor="mj_user_token">Identifiant</label>
              <input
                id="mj_user_token"
                name="mj_user_token"
                className="linp"
                placeholder="votre.login"
                value={login}
                onChange={e => setLogin(e.target.value)}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                spellCheck="false"
                autoCorrect="off"
                autoCapitalize="off"
              />
            </div>

            <div className="lfield">
              <label className="llbl" htmlFor="mj_sec_cred">Mot de passe</label>
              <div style={{ position: "relative" }}>
                <input
                  id="mj_sec_cred"
                  name="mj_sec_cred"
                  type="text"
                  className={`linp ${showPass ? "" : "sec-mask"}`}
                  style={{ paddingRight: 40 }}
                  placeholder="••••••••"
                  value={pass}
                  onChange={e => setPass(e.target.value)}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  spellCheck="false"
                  autoCorrect="off"
                  autoCapitalize="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", fontSize: 16,
                    color: "var(--text3)", padding: 4
                  }}
                  title={showPass ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPass ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            <button type="button" onClick={doLogin} className="lbtn" disabled={loading} style={{ marginTop: 14 }}>
              {loading ? "Connexion en cours…" : "Se connecter →"}
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 18, fontSize: 11, color: "var(--text3)" }}>
            <span>🔒</span> Session confidentielle · Déconnexion auto en cas d'inactivité
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   ROOT & GESTION DES SESSIONS
═══════════════════════════════════════════════════════ */
export default function App() {
  // Purge immédiate de toute ancienne session permanente
  useEffect(() => {
    try { localStorage.removeItem("majha_rh_sess"); } catch { }
  }, []);

  // Session uniquement active par onglet / fermeture de navigateur
  const [user, setUser] = useState(() => {
    try {
      const s = sessionStorage.getItem("majha_rh_sess_secure");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    document.title = "MAJHA — Espace RH";
  }, []);

  const logout = () => {
    try {
      sessionStorage.removeItem("majha_rh_sess_secure");
      localStorage.removeItem("majha_rh_sess");
    } catch { }
    setUser(null);
  };

  // ── DÉCONNEXION AUTOMATIQUE PAR INACTIVITÉ (15 MINUTES) ──
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes
    let timeoutId;

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        alert("Votre session a expiré après 15 minutes d'inactivité pour des raisons de confidentialité.");
        logout();
      }, INACTIVITY_LIMIT_MS);
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(ev => window.addEventListener(ev, resetTimer));

    resetTimer();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
    };
  }, [user]);

  if (!user) return <Login onLogin={setUser} />;
  return isEmployeAdmin(user) ? <AdminApp user={user} onLogout={logout} /> : <EmployeApp user={user} onLogout={logout} />;
}
